import * as THREE from 'three';
import { applyInteriorDark } from '../engine/interior';
import { BUILD } from '../config';
import { getTierMaterial, getFoundationMaterial, WORLD_TILE, type BuildTier, type SkinId } from '../engine/materials';
import type { Inventory, ItemId } from './inventory';
import { buildCraftableModel, buildDoorLeafModel, type CraftedDoorId } from './craftables';
import { LOCAL_PLAYER } from './identity';
import { obbOverlap, type Box, type BoxPool } from '../engine/collision';
import { preloadGLB, cloneGLBIfReady, onGLBReady } from '../engine/gltf';

// A small lock model attached by a door/chest handle (both faces). Shared builder so a door + chest look identical.
export function makeLockPair(parent: THREE.Object3D, kind: 'wooden_lock' | 'keypad_lock', x: number, y: number, faceZ: number): void {
  for (const sz of [1, -1]) {
    const lk = buildCraftableModel(kind);
    lk.scale.setScalar(0.8);
    lk.position.set(x, y - 0.5 * 0.8, sz * faceZ); // the model's clasp sits ~y0.5 -> drop so it lands at `y`
    if (sz < 0) lk.rotation.y = Math.PI;             // back face looks outward too
    lk.traverse((o) => { o.castShadow = true; });
    lk.userData.lockPart = true; // tagged so removeAimedLock can strip the graphic off the door
    if (kind === 'keypad_lock') {
      // Status LIGHT: GREEN = no code (unlocked, anyone may open), RED = code set (locked). Recoloured by
      // setKeypadLight() when the code changes. Unlit so it reads as a glowing LED at any time of day.
      const light = new THREE.Mesh(new THREE.SphereGeometry(0.035, 8, 6), new THREE.MeshBasicMaterial({ color: 0x33dd44 }));
      light.position.set(0.16, 0.62, 0.13); // top corner of the keypad face
      light.userData.keypadLight = true;
      lk.add(light);
    }
    parent.add(lk);
  }
}
import {
  CATALOG, PILLAR_HALF, PILLAR_FLARE,
  isGroundFoundation, isCeiling, isFootprint, isWallSlot, footprintType, wallSlotHeight,
  type PieceId,
} from './build/catalog';
import {
  S, TRI_R, TRI_CORNERS, SQUARE_CORNERS,
  edgesFor, rotXZ, angle, mod, snapDeg, wallKey, keyOf, mate, footprintTerrain,
  pointInFootprint, type FootprintShape,
} from './build/grid';
import { GridSpatialIndex } from './build/spatialIndex';
import { OccupancyLedger, foundationTerrainFit, footprintNoOverlap, ceilingNoOverlap } from './build/validator';
import { Stability } from './build/stability';
import { CrumbleField } from './build/crumble';
import { BuildGLBInstances } from './build/glbInstances';
import { BuildDebug } from './build/debug';
import { RoomGraph, type Room } from './build/roomGraph';
import type { PlacedPiece, ResolvedSlot, PlacementReason } from './build/types';

// Phase 3 building. This file is a THIN FACADE over the build/ modules: the catalog owns the per-
// piece defs (anchors / occupancy / support / rules), grid owns the square+triangle cell math, the
// spatial index + placed map hold logical occupancy, the validator carries the rule bodies, and the
// stability graph computes support (feeding the crumble visual). The facade keeps the exact public
// API the rest of the app reaches (group, ghost, colliders, doors, isUnderRoof/openings/cutaway,
// getFoundations) and the mesh factories below are reused unchanged — only the snap/validate/occupy
// logic now flows through the modules. Foundations are fixed half-wall-tall blocks; raised tiers add
// whole half-walls and a spot is invalid unless the ground under it is flat enough to fit. They snap
// edge-to-edge via sockets and can also be placed freely anywhere to start new platforms. Height is
// smart by default (match the neighbor you snap to, low default on open ground) and can be raised
// with a right-click drag. Walls stand on foundation edges, strong side out, weak-side beam in.

export type { PieceId };

interface PlacedFoundation {
  type: 'square' | 'triangle';
  x: number;
  z: number;
  yaw: number;
  top: number; // absolute world Y of the walkable surface
  base: number; // absolute world Y of the underside (plants into local terrain)
  tier: BuildTier;
  elevated: boolean; // true for ceilings/floors placed a storey up (not a ground foundation)
}

interface PlacedWall { id: PieceId; cx: number; cy: number; cz: number; yaw: number; h: number; base: number; }
interface PlacedStair { cx: number; cy: number; cz: number; yaw: number; }
interface PlacedPillar { cx: number; cy: number; cz: number; } // slim column: yaw is fixed, cy = mid-height
interface PlacedDoor { mesh: THREE.Group; hx: number; hy: number; hz: number; closedYaw: number; sign: number; open: boolean; t: number; lock?: 'wooden_lock' | 'keypad_lock'; code?: string; granted?: string[]; owner: string; mirror?: boolean; pair?: PlacedDoor; autoclose?: boolean; openedAt?: number; } // pair = the other leaf of a double door (act as ONE unit); code/granted = keypad combo + who has entered it; autoclose = swing shut ~5s after opening (openedAt = nowSec stamp)

// LOCAL_PLAYER (the door owner) now comes from ./identity (shared, MP-swappable). The base-claim totem is a
// DEPLOYABLE (game/deployables.ts) now; build/remove authorization comes through the claimAuthAt hook below.

const DOOR_ARC_STEPS = 4; // yaw samples per swept door leaf (closed -> open) in the swing-arc gates

// --- Local world save shapes (game/persist.ts). One ordered list keeps built pieces and crafted door
// units in true placement order; door swing/lock state rides separately (matched back by hinge). ---
const CRAFTED_DOOR_IDS = new Set<string>(['wooden_door', 'metal_door', 'metal_double_door', 'reinforced_door']);
export interface SavedBuildPiece {
  k: 'piece' | 'craftdoor';
  def: string; // PieceId, or the crafted door item id for k='craftdoor'
  tier: BuildTier;
  x: number; y: number; z: number; yaw: number;
  sign: number; raised: boolean;
  hp: number;
  oldId: string; // instance id at save time (mounted deployables reference their host by it)
  skinId?: SkinId | null; // cosmetics-only material variant (optional: old saves simply omit it)
}
export interface SavedDoorState {
  x: number; y: number; z: number; mirror: boolean; open: boolean;
  lock?: 'wooden_lock' | 'keypad_lock'; code?: string; granted?: string[]; owner: string;
  autoclose?: boolean; // optional: old saves simply omit it
}
export interface SavedBuild { pieces: SavedBuildPiece[]; doors: SavedDoorState[]; }

const WEAK_MAT = new THREE.MeshLambertMaterial({ color: BUILD.weakColor, flatShading: true });
applyInteriorDark(WEAK_MAT); // weak-side beams dim with their walls inside the OLOS dark
const GHOST_MAT = new THREE.MeshBasicMaterial({ color: BUILD.validColor, transparent: true, opacity: BUILD.ghostOpacity, depthWrite: false, side: THREE.DoubleSide });
// The crisp part of the bounds ghost: a near-opaque edge outline, color-synced with the fill each frame.
const GHOST_EDGE_MAT = new THREE.LineBasicMaterial({ color: BUILD.validColor, transparent: true, opacity: BUILD.ghostEdgeOpacity, depthWrite: false });
// Amber SOFT-side face + arrow on wall-family ghosts (owner: valid/soft was unreadable at 0.45 alpha).
const GHOST_SOFT_MAT = new THREE.MeshBasicMaterial({ color: BUILD.ghostSoftColor, transparent: true, opacity: 0.55, depthWrite: false, side: THREE.DoubleSide });
const GHOST_WARN = new THREE.Color(0xffb020); // valid-but-sagging tint: projected support is nearly spent
const WALL_AIM_PT = new THREE.Vector3(); // scratch for the wall solver's ray-distance scoring
const nowSec = (): number => performance.now() / 1000; // the builder's damage/repair/undo clock

// The piece's local bounds (procedural build probed once per piece+tier) -- the ghost and the hammer
// highlight both render THIS, not the geometry (owner 2026-07-12: "highlight the BOUNDING BOX/TRIANGLE").
const LOCAL_BOUNDS = new Map<string, { bb: THREE.Box3; tri: boolean }>();
function localBoundsFor(id: PieceId, tier: BuildTier): { bb: THREE.Box3; tri: boolean } {
  const key = `${id}/${tier}`;
  let e = LOCAL_BOUNDS.get(key);
  if (!e) {
    const probe = makePiece(id, true, tier);
    e = { bb: new THREE.Box3().setFromObject(probe), tri: id.includes('triangle') };
    probe.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh && m.geometry) m.geometry.dispose(); });
    LOCAL_BOUNDS.set(key, e);
  }
  return e;
}

// Build the bounds shape itself: a box (or 3-sided prism for triangle pieces) + edge outline.
function boundsShape(bb: THREE.Box3, tri: boolean, fill: THREE.Material, edge: THREE.LineBasicMaterial): THREE.Group {
  const g = new THREE.Group();
  const size = bb.getSize(new THREE.Vector3()), c = bb.getCenter(new THREE.Vector3());
  const geo = tri
    ? new THREE.CylinderGeometry(TRI_R, TRI_R, Math.max(size.y, 0.02), 3)
    : new THREE.BoxGeometry(Math.max(size.x, 0.02), Math.max(size.y, 0.02), Math.max(size.z, 0.02));
  const mesh = new THREE.Mesh(geo, fill);
  mesh.position.copy(c);
  g.add(mesh);
  const lines = new THREE.LineSegments(new THREE.EdgesGeometry(geo, 20), edge);
  lines.position.copy(c);
  g.add(lines);
  return g;
}

export class Builder {
  readonly group = new THREE.Group();
  active = false;
  // The HAMMER is out (held as the active item) while NOT in build mode. The orchestrator sets this
  // so the demolish/upgrade highlight and actions work off the hammer instead of build mode. Every
  // demolish/upgrade gate reads `editing` (= active || hammerActive), so one flag drives both tools.
  hammerActive = false;
  piece: PieceId = 'foundation_square';
  tier: BuildTier = 'twig';
  devFree = false; // dev free-build (` / ~): ignore resource cost + always read as affordable
  // The DEPLOYABLE base claim governs who may build/remove in its area (the area totem). main wires this to consult
  // deployables: null = no claim covers here (FREE-BUILD, open to anyone); else build = you own/are a member, author =
  // you placed it (drives the 50% demolish refund). When unset (tests), everything reads as free-build.
  claimAuthAt: ((x: number, z: number) => { build: boolean; author: boolean } | null) | null = null;
  private inventory: Inventory | null = null;
  private readonly pieceTier = new Map<string, BuildTier>(); // placed id -> tier it was built at (for refunds)
  // Ground foundations placed with R (a half-wall up): the save replay must pass `raised` back through
  // applyNetPlace or the solver re-resolves them LOW on open ground and the replay verify skips them.
  private readonly pieceRaised = new Set<string>();

  private ghostTop = 0; // absolute world Y of the walkable surface (the floor)
  private ghostBase = -BUILD.foundDepth; // absolute world Y of the ghost underside
  private yawDeg = 0;
  private socketIndex = 0;
  private snapped = false;

  // Logical occupancy + spatial index of placed pieces (the new build core). The mirror arrays
  // below are written alongside on every place() so the read methods (colliders/isUnderRoof/etc)
  // stay literally unchanged.
  private readonly placed = new Map<string, PlacedPiece>();
  private readonly index = new GridSpatialIndex();
  private readonly ledger = new OccupancyLedger();
  private readonly stability = new Stability(this.placed);
  private readonly crumble = new CrumbleField();
  // Instanced GLB visuals for placed pieces (build/glbInstances.ts): one InstancedMesh per file submesh
  // for every piece of a kind -- the per-piece group keeps only its invisible pick proxy.
  private readonly glbInstances = new BuildGLBInstances();
  // Opt-in build gizmos (anchors by family, normal arrows, occupied/free slots, support bars, the
  // last rejection reason). Off by default; setDebug() flips it from the existing debug toggle.
  private readonly debug = new BuildDebug();
  // EXACT room detection (build/roomGraph): commit/removePiece flip the dirty flag, the first
  // roomAt/clearanceAt/isSealedAt query pays the rebuild -- at most once per frame, never hot.
  private readonly roomGraph = new RoomGraph();
  private roomsDirty = true;
  // Deployable colliders (furniture + chests + campfires) for the deployable-clearance and door
  // swing gates. main wires this to the deploy managers over a pooled array; unwired (tests) skips
  // those gates entirely.
  collectDeployBoxes: (() => readonly Box[]) | null = null;
  private nextId = 0;

  private readonly foundations: PlacedFoundation[] = [];
  // Per edge: how tall the wall stack already is above the foundation top (0 = empty), plus the
  // list of each wall piece's cumulative top (so a ceiling can snap to a half-wall's halfway level).
  private readonly wallStacks = new Map<string, number>();
  private readonly wallTops = new Map<string, number[]>();
  private readonly walls: PlacedWall[] = [];
  private readonly stairs: PlacedStair[] = [];
  private readonly pillars: PlacedPillar[] = [];
  private readonly doors: PlacedDoor[] = [];
  lastDoorDenied = false; // set when the last toggle attempt hit a locked door you don't own (main shows a denied feed)
  private lastDeniedDoor: PlacedDoor | null = null; // the door that denied -- main offers the keypad code pad for it
  lastToggledOpen = false;             // the .open state AFTER the last successful toggleDoorLook/Near (open vs closing SFX)
  readonly lastToggledPos = new THREE.Vector3(); // world position of that door's leaf (SFX pan/distance)
  // MP: fired when the LOCAL player toggles / locks / unlocks / (re)codes a door, so NetBackend can broadcast +
  // persist it (remote clients + late joiners apply via the applyDoor*At methods). Default no-op = single-player.
  onDoorToggle: (x: number, y: number, z: number, open: boolean) => void = () => {};
  onDoorLock: (x: number, y: number, z: number, kind: 'wooden_lock' | 'keypad_lock', code: string | undefined) => void = () => {};
  onDoorUnlock: (x: number, y: number, z: number) => void = () => {};
  onDoorCode: (x: number, y: number, z: number, code: string | null) => void = () => {};
  onDoorGrant: (x: number, y: number, z: number, user: string) => void = () => {}; // correct keypad code entered
  // MP: main injects the base-claim access test (does a claim covering (x,z) include the local player?) so a
  // claim member passes a wooden/keypad door lock exactly like the server's gate. null = single-player.
  claimAccessCheck: ((x: number, z: number) => boolean) | null = null;
  // Window sockets a window deployable already fills, keyed by opening centre + height band. A one-way
  // set is enough: window deployables have no removal path today, so occupancy never frees.
  private readonly windowOccupied = new Set<string>();
  private readonly ceilingMeshes: Array<{ mesh: THREE.Object3D; top: number }> = [];
  // Per placed-instance closure that reverses its legacy mirror-array writes, so a collapse removes a
  // piece from every read path (colliders/openings/foundations/wall-stacks) without re-deriving keys.
  private readonly mirrorUndo = new Map<string, () => void>();
  private pendingWallKey = '';
  // The slots + parents the pending ghost would reserve, carried from update() into place().
  private pendingSlots: ResolvedSlot[] = [];
  private pendingParents: string[] = [];
  private ghost: THREE.Group;
  private ghostSoft: THREE.Group | null = null; // amber SOFT-face overlay (wall-family ghosts only)
  private ghostYawEase = 0;      // eased render yaw (the solver's gYaw stays exact for commit)
  private ghostEaseMs = 0;       // last ease tick (performance.now)
  private ghostWasHidden = true; // snap (no glide) on the first frame the ghost reappears
  private ghostValid = false;
  private lastReason: PlacementReason = 'NO_HIT'; // why the last ghost was (in)valid, for debug/HUD
  private lastPiece: PieceId | '' = '';
  private lastTier: BuildTier | '' = '';
  private readonly gPos = new THREE.Vector3();
  private gYaw = 0;
  private doorSign = 1; // which way the ghosted door will hinge/swing
  private wallFlip = false; // F on a wall: swap which face is the SOFT (weak) side, so the player aims the hard side out
  private doorGhost: THREE.Group | null = null; // preview group for a held crafted door (real leaf geometry, fitted to the socket)
  private doorGhostKey = ''; // cache key so the ghost only rebuilds when the socket/kind/swing changes
  // The live build-aim ray (camera through the mouse), fed by update(); wall-socket scoring prefers it.
  private readonly aimRay = new THREE.Ray();
  private aimRayValid = false;
  // Reused for demolish ray-picking against placed solids (avoids per-call allocations).
  private readonly raycaster = new THREE.Raycaster();
  private readonly demolishRay = new THREE.Vector3();
  private readonly demolishDir = new THREE.Vector3();
  // A translucent red box that wraps the placed piece the cursor is aimed at, so you SEE what X will
  // demolish before you press it. Driven each frame by aimDemolish(); hidden when not over a piece.
  private readonly demolishHighlight = new THREE.Mesh(
    new THREE.BoxGeometry(1, 1, 1),
    new THREE.MeshBasicMaterial({ color: 0xff3b2a, transparent: true, opacity: 0.34, depthWrite: false }),
  );
  playerY = 0; // the player's feet height, so deployables land on the floor they stand on
  readonly playerPos = new THREE.Vector3(); // player world pos, for the build-reach gate
  private raisedFoundation = false; // R (foundations only): place a half-wall up instead of at floor
  // True while applyNet* replays ANOTHER player's build op (or the join-time world rehydrate). The
  // authority already validated the actor's reach/claim/cost on THEIR client, so the local-player
  // gates (reach, claim access, inventory, editing-tool-out) must not re-judge it here.
  private remoteApplying = false;
  // The resolved transform of the last successful place(), so NetBackend can broadcast WHAT was placed
  // (the placeBuild intent itself has no payload -- the ghost solver resolved it). Fresh object per place.
  private lastPlacedInfo: { piece: PieceId; tier: BuildTier; x: number; y: number; z: number; yaw: number; sign: number; raised: boolean } | null = null;
  get lastPlaced(): { piece: PieceId; tier: BuildTier; x: number; y: number; z: number; yaw: number; sign: number; raised: boolean } | null { return this.lastPlacedInfo; }

  constructor() {
    this.ghost = this.buildGhostShape();
    this.ghost.visible = false;
    this.group.add(this.ghost);
    this.group.add(this.crumble.group);
    this.group.add(this.glbInstances.group);
    this.group.add(this.debug.group);
    this.demolishHighlight.visible = false;
    this.demolishHighlight.renderOrder = 3; // drawn over the solids it wraps
    this.group.add(this.demolishHighlight);
    this.lastPiece = this.piece;
    this.lastTier = this.tier;
  }

  setActive(v: boolean): void {
    this.active = v;
    this.ghost.visible = v;
    this.ghostWasHidden = true; // whichever way it flips, the next shown frame snaps into place
    if (!v) {
      this.demolishHighlight.visible = false;
      this.clearAimTint();
      if (this.socketPreview) this.socketPreview.visible = false; // no blue socket markers outside build mode
    }
  }

  // The player's resource inventory: building pulls its cost from here, and removing refunds 50%.
  setInventory(inv: Inventory): void {
    this.inventory = inv;
  }

  // Cost to place a piece at the given tier.
  private costFor(_piece: PieceId, tier: BuildTier): { item: ItemId; amount: number } {
    return BUILD.cost[tier];
  }

  // Can the player afford the CURRENT ghost piece? Dev free-build (or no inventory wired) -> always yes.
  private canAfford(): boolean {
    if (this.devFree || this.remoteApplying || !this.inventory) return true;
    const c = this.costFor(this.piece, this.tier);
    return this.inventory.count(c.item) >= c.amount;
  }

  // The hammer item is out (held while NOT building). Flips on the demolish/upgrade targeting path
  // without turning the ghost on. Hide the red highlight the moment the hammer is put away.
  setHammerActive(v: boolean): void {
    this.hammerActive = v;
    if (!v) { this.demolishHighlight.visible = false; this.clearAimTint(); } // the target highlight is hammer-only now
  }

  // Tint the aim highlight to match the hammer's mode: red for Remove, green for an Upgrade.
  setHighlightColor(hex: number): void {
    this.tintMat.color.setHex(hex);
    this.tintMat.emissive.setHex(hex === 0xff3b2a ? 0x551008 : 0x0a3a10);
  }

  // True when EITHER tool that can edit placed pieces is out: build mode (X to demolish) or the
  // hammer (left-click to upgrade, its own key to remove). The aimDemolish highlight and the
  // demolish/upgradeAt picks all gate on this, so the hammer reuses the exact build-mode pick.
  private get editing(): boolean {
    return this.active || this.hammerActive || this.remoteApplying;
  }

  // Highlight (in red) the placed piece the cursor is aimed at -- what demolish/upgrade would hit --
  // or hide it if the cursor isn't over a piece / no editing tool is out. Same pick as demolish() and
  // upgradeAt(), so it doubles as the hammer's targeting reticle (driven while the hammer is active).
  aimDemolish(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number): void {
    if (!this.editing) { this.clearAimTint(); return; }
    const hit = this.pickPlaced(ox, oy, oz, dx, dy, dz);
    this.tintAim(hit ?? null);
  }

  // Highlight the aimed PIECE (the owner hated the wrapping box: it never fit the shape) -- but tint a
  // cheap PROCEDURAL stand-in at the same transform/tier, never the real placed mesh: for a GLB tier that
  // mesh can run hundreds of thousands of verts, and this highlight rebuilds every time the aim target
  // changes (every frame while it's moving across a base). The stand-in is disposed the moment aim moves
  // off / the tool stows.
  private tintTarget: PlacedPiece | null = null;
  private tintStand: THREE.Group | null = null;
  private readonly tintMat = new THREE.MeshLambertMaterial({ color: 0xff3b2a, transparent: true, opacity: 0.4, emissive: new THREE.Color(0x551008), side: THREE.DoubleSide, depthWrite: false });
  private readonly tintEdgeMat = new THREE.LineBasicMaterial({ color: 0xff3b2a, transparent: true, opacity: 0.95, depthWrite: false });
  setAimTintColor(hex: number): void {
    this.tintMat.color.setHex(hex);
    this.tintEdgeMat.color.setHex(hex);
  }
  private tintAim(target: PlacedPiece | null): void {
    if (target === this.tintTarget) return;
    this.clearAimTint();
    if (!target) return;
    this.tintTarget = target;
    // BOUNDS highlight (owner 2026-07-12): the aimed piece reads as its box/triangle + edges, exactly
    // like the placement ghost -- not a re-tinted copy of its geometry.
    const tier = this.pieceTier.get(target.id) ?? 'wood';
    const { bb, tri } = localBoundsFor(target.defId, tier);
    const stand = boundsShape(bb, tri, this.tintMat, this.tintEdgeMat);
    stand.position.copy(target.position);
    stand.rotation.y = target.yaw;
    this.group.add(stand);
    this.tintStand = stand;
  }
  private clearAimTint(): void {
    if (this.tintStand) { this.group.remove(this.tintStand); disposeSolid(this.tintStand); this.tintStand = null; }
    this.tintTarget = null;
    this.demolishHighlight.visible = false; // legacy box stays retired
  }

  toggle(): void {
    this.setActive(!this.active);
  }

  // Turn the build debug gizmos on/off (wired to the existing debug-overlay toggle in main.ts). When
  // turned on we rebuild the placed-piece gizmos once so they appear immediately, not on the next
  // placement. Cheap and fully opt-in: nothing here runs while debug is off.
  setDebug(v: boolean): void {
    this.debug.setEnabled(v);
    if (v) this.refreshDebug();
  }

  // Debug gizmos + the room-tint overlay, refreshed together on every base change. The room graph
  // only rebuilds here when it is dirty AND debug is on, so the production path pays nothing.
  private refreshDebug(): void {
    this.debug.rebuild(this.placed.values());
    if (this.debug.enabled) {
      this.ensureRooms();
      this.debug.showRooms(this.roomGraph.debugCells());
    }
  }

  // Tier ordering for the hammer's upgrade-only gate (downgrading a stranger's stone wall to twig
  // from across the map was a raid exploit). Full 5-tier ladder so metal/reinforced rank correctly.
  private readonly tierRank: Record<string, number> = { twig: 0, wood: 1, stone: 2, metal: 3, reinforced: 4 };
  private lastProjected = 1;                  // stability.project() of the current ghost (drives the amber warn tint)
  private lastRule: string | null = null;     // which rule body failed when lastReason === 'RULE_FAIL'
  private stickyWallKey: string | null = null; // the wall edge held by hysteresis (see updateWall)
  private wallCycleSeen = 0;                  // socketIndex value already consumed as a cycle request
  private pendingSoftDir: { x: number; z: number } | null = null; // wall ghost's SOFT-face normal, consumed by commit()
  private readonly lastSockets: Array<{ x: number; y: number; z: number; active: boolean }> = []; // this frame's candidate sockets (preview markers)
  private socketPreview: THREE.InstancedMesh | null = null;
  private readonly previewM = new THREE.Matrix4();

  selectPiece(id: PieceId): void {
    this.piece = id;
    this.socketIndex = 0;
    this.wallCycleSeen = 0;
    this.stickyWallKey = null;
    this.raisedFoundation = false; // the raise state never silently carries across piece switches
    this.wallFlip = false;         // fresh hold: soft side back to the default (interior)
  }

  selectTier(t: BuildTier): void {
    // The ghost stays a flat valid/invalid color; the tier only skins the solid on placement.
    this.tier = t;
  }

  scroll(dir: number): void {
    if (this.snapped) this.socketIndex += dir > 0 ? 1 : -1;
    else this.yawDeg = (((this.yawDeg + dir * BUILD.rotateStepDeg) % 360) + 360) % 360;
  }

  // R while building: ONE verb -- cycle a snapped piece's socket, or rotate a free piece a notch.
  // The old R also flipped doors and raised foundations with zero affordance; those moved to F
  // (fKey), and the HUD chip (keyHint) names what each key does for the current ghost state.
  rKey(): void {
    if (this.snapped) this.socketIndex += 1;
    else this.yawDeg = (this.yawDeg + BUILD.rotateStepDeg) % 360;
  }

  // F while building: the piece's ALT action -- flip a door's swing direction, or raise/lower a
  // ground foundation by a half-wall (height otherwise auto-snaps to the cursor).
  fKey(): void {
    if (this.piece === 'door') { this.doorSign *= -1; return; }
    if (isGroundFoundation(this.piece)) { this.raisedFoundation = !this.raisedFoundation; return; }
    if (this.isWallLike()) this.wallFlip = !this.wallFlip; // walls: F flips which face is the soft (weak) side
  }

  // True for the wall-family pieces (plain wall, doorway, window, half-wall) -- everything that routes through
  // updateWall and therefore carries a soft/hard side the player can flip with F.
  private isWallLike(): boolean {
    return !isGroundFoundation(this.piece) && !isCeiling(this.piece) && this.piece !== 'stairs' && this.piece !== 'door' && this.piece !== 'pillar';
  }

  // The contextual key legend for the HUD chip next to the placement hint.
  get keyHint(): string {
    if (this.piece === 'door') return 'R rotate · F flip swing';
    if (isGroundFoundation(this.piece)) return `${this.snapped ? 'R cycle edge' : 'R rotate'} · F ${this.raisedFoundation ? 'lower' : 'raise'}`;
    if (this.isWallLike()) return `${this.snapped ? 'R cycle socket' : 'R rotate'} · F flip strong side`;
    return this.snapped ? 'R cycle socket' : 'R rotate';
  }

  // Drop the old ghost and free its geometry (materials are shared and kept). Without the
  // geometry.dispose() the GPU buffers piled up every rebuild.
  private disposeGhost(): void {
    this.group.remove(this.ghost);
    this.ghost.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (mesh.geometry) mesh.geometry.dispose();
    });
    this.ghostSoft = null;
  }

  // The placement ghost: the piece's BOUNDING BOX / TRIANGLE (fill + bright edges), never its
  // geometry. Wall-family ghosts also carry the amber SOFT-face overlay + inward arrow so which
  // side is weak reads instantly (positioned per frame from the solver's pendingSoftDir).
  private buildGhostShape(): THREE.Group {
    const { bb, tri } = localBoundsFor(this.piece, this.tier);
    const g = boundsShape(bb, tri, GHOST_MAT, GHOST_EDGE_MAT);
    if (this.isWallLike()) {
      const size = bb.getSize(new THREE.Vector3()), c = bb.getCenter(new THREE.Vector3());
      const soft = new THREE.Group();
      const quad = new THREE.Mesh(new THREE.PlaneGeometry(size.x * 0.94, size.y * 0.88), GHOST_SOFT_MAT);
      soft.add(quad);
      const arrow = new THREE.Mesh(new THREE.ConeGeometry(0.14, 0.34, 12), GHOST_SOFT_MAT);
      arrow.rotation.x = Math.PI / 2; // apex points +z (out of the quad, INTO the soft-side room)
      arrow.position.z = 0.22;
      soft.add(arrow);
      soft.position.set(c.x, c.y, 0); // z set per frame to the soft face
      soft.visible = false;
      g.add(soft);
      this.ghostSoft = soft;
    } else this.ghostSoft = null;
    return g;
  }

  // `ray` (the live camera-through-mouse aim, main's build branch): wall-socket picking scores by
  // distance to THIS ray instead of the resolved ground point -- in 3rd person a steep camera often
  // resolves the ground point near your feet while the mouse visually sits on a far edge, which made
  // the ghost "want a wall right next to me, not where my mouse is" (owner 2026-07-12). Rig/test
  // callers pass no ray and keep the plain point behavior.
  update(groundPoint: THREE.Vector3 | null, ray?: THREE.Ray | null): void {
    this.aimRayValid = !!ray;
    if (ray) this.aimRay.copy(ray);
    if (!this.active || !groundPoint) {
      this.ghost.visible = false;
      this.ghostWasHidden = true; // reappear = snap, not a cross-map glide
      this.lastReason = 'NO_HIT';
      if (this.socketPreview) this.socketPreview.visible = false;
      return;
    }
    this.lastSockets.length = 0; // repopulated by whichever solver runs below
    this.ghost.visible = true;
    // Each placement family runs its own rule-first solver path (foundations + ceilings keep the
    // §B / updateCeiling math verbatim; walls/door/stairs their own snap), each one storing
    // the resolved transform, the slots/parents it would reserve, and the last failure reason.
    if (isGroundFoundation(this.piece)) this.updateFoundation(groundPoint);
    else if (isCeiling(this.piece)) this.updateCeiling(groundPoint);
    else if (this.piece === 'stairs') this.updateStairs(groundPoint);
    else if (this.piece === 'door') this.updateDoor(groundPoint);
    else if (this.piece === 'pillar') this.updatePillar(groundPoint);
    else this.updateWall(groundPoint);

    // BUILD REACH: you must be near what you place (no building across the map). Gate after the piece's
    // own validity so a far ghost reads TOO_FAR rather than a false OK.
    if (this.ghostValid) {
      const dx = this.gPos.x - this.playerPos.x, dy = this.gPos.y - this.playerPos.y, dz = this.gPos.z - this.playerPos.z;
      if (dx * dx + dy * dy + dz * dz > BUILD.reach * BUILD.reach) { this.ghostValid = false; this.lastReason = 'TOO_FAR'; }
    }
    // BASE-CLAIM GATE: you can't build inside someone else's claim (free-build areas are open to anyone).
    if (this.ghostValid && !this.buildAuthed(this.gPos.x, this.gPos.z)) { this.ghostValid = false; this.lastReason = 'CLAIM'; }
    // DEPLOYABLE CLEARANCE: a solid piece may not be driven through placed furniture (a wall
    // through a furnace, a foundation through a chest). Doorway/window/door pieces are exempt
    // inside deployBlocked -- mounted leaves and frames legitimately share their opening's space.
    if (this.ghostValid && this.deployBlocked()) { this.ghostValid = false; this.lastReason = 'BOX_OVERLAP'; }

    // Rebuild the ghost shape only when the piece or tier changes (bounds are fixed per piece+tier).
    // Every other frame we just reposition it -- no per-frame geometry churn.
    const rebuilt = this.piece !== this.lastPiece || this.tier !== this.lastTier;
    if (rebuilt) {
      this.disposeGhost();
      this.ghost = this.buildGhostShape();
      this.group.add(this.ghost);
      this.lastPiece = this.piece;
      this.lastTier = this.tier;
    }

    // EASED ghost transform: the solver's gPos/gYaw stay exact (commit uses them verbatim); the RENDERED
    // ghost glides toward them so candidate-socket flips read as a magnetic snap instead of teleport
    // jitter. A rebuild, a reappear, or a big jump (> ~2.5m: crossed to another socket cluster) snaps.
    const nowMs = performance.now();
    const dt = Math.min(0.1, (nowMs - this.ghostEaseMs) / 1000);
    this.ghostEaseMs = nowMs;
    const targetYaw = this.gYaw + (this.piece === 'door' ? this.doorSign * 0.5 : 0); // door hangs ajar to show its swing
    if (rebuilt || this.ghostWasHidden || this.ghost.position.distanceToSquared(this.gPos) > 6.25) {
      this.ghost.position.copy(this.gPos);
      this.ghostYawEase = targetYaw;
    } else {
      const k = 1 - Math.exp(-BUILD.ghostEaseRate * dt);
      this.ghost.position.lerp(this.gPos, k);
      let dYaw = targetYaw - this.ghostYawEase;
      dYaw = ((dYaw + Math.PI) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2) - Math.PI; // shortest arc
      this.ghostYawEase += dYaw * k;
    }
    this.ghost.rotation.y = this.ghostYawEase;
    this.ghostWasHidden = false;
    // Red = can't place here; blue = placeable but you can't afford it; green = good to build --
    // blending toward AMBER as the projected support runs low, so a long cantilever visibly sags
    // toward its limit before it flips to NO_SUPPORT red. Edges mirror the fill color, near-opaque.
    GHOST_MAT.color.set(!this.ghostValid ? BUILD.invalidColor : this.canAfford() ? BUILD.validColor : BUILD.unaffordColor);
    if (this.ghostValid && this.lastProjected < 0.25) GHOST_MAT.color.lerp(GHOST_WARN, 1 - this.lastProjected / 0.25);
    GHOST_EDGE_MAT.color.copy(GHOST_MAT.color);
    // SOFT-side overlay (walls/doorways/windows): sit the amber face+arrow on the weak face. The
    // solver's pendingSoftDir is world-space; rotate it into the ghost's local frame to pick +z/-z.
    if (this.ghostSoft) {
      const sd = this.pendingSoftDir;
      this.ghostSoft.visible = !!sd;
      if (sd) {
        const c = Math.cos(this.gYaw), s = Math.sin(this.gYaw);
        const lz = s * sd.x + c * sd.z; // world -> local z of the soft normal
        const sign = lz >= 0 ? 1 : -1;
        this.ghostSoft.position.z = sign * (BUILD.wallThick / 2 + 0.03);
        this.ghostSoft.rotation.y = sign > 0 ? 0 : Math.PI; // arrow apex keeps pointing INTO the soft room
      }
    }
    // Debug: preview the slots this next placement would reserve (green clear / red rejected) and
    // surface the reason code. No-op while debug is off; lastReason is always stored regardless.
    this.debug.showGhost(this.pendingSlots, this.gPos, this.ghostValid, this.lastReason);
    this.syncSocketPreview();
  }

  // SOCKET PREVIEW: while a piece is held, every candidate socket in reach shows as a small marker
  // (the SELECTED one enlarged), so R-cycling and cursor sweeps are predictable instead of guessed.
  // One instanced draw call; hidden the moment build mode ends.
  private syncSocketPreview(): void {
    if (!this.socketPreview) {
      const geo = new THREE.OctahedronGeometry(0.085);
      const mat = new THREE.MeshBasicMaterial({ color: 0x53d8e8, transparent: true, opacity: 0.85, depthWrite: false });
      this.socketPreview = new THREE.InstancedMesh(geo, mat, 48);
      this.socketPreview.frustumCulled = false;
      this.socketPreview.renderOrder = 3;
      this.group.add(this.socketPreview);
    }
    const pm = this.socketPreview;
    // DEDUPE by world position: two conjoined foundations both contribute a socket at their shared edge;
    // showing (and cycling) both read as "two slightly different snap points" for what is ONE spot. Collapse
    // markers within 0.25m into one (active status wins) so the shared edge presents a single socket.
    const shown: Array<{ x: number; y: number; z: number; active: boolean }> = [];
    for (const s of this.lastSockets) {
      const dup = shown.find((o) => Math.abs(o.x - s.x) < 0.25 && Math.abs(o.y - s.y) < 0.25 && Math.abs(o.z - s.z) < 0.25);
      if (dup) { dup.active = dup.active || s.active; continue; }
      shown.push({ x: s.x, y: s.y, z: s.z, active: s.active });
      if (shown.length >= 48) break;
    }
    const n = shown.length;
    for (let i = 0; i < n; i++) {
      const s = shown[i];
      const sc = s.active ? 2.0 : 1.0;
      this.previewM.makeScale(sc, sc, sc).setPosition(s.x, s.y, s.z);
      pm.setMatrixAt(i, this.previewM);
    }
    pm.count = n;
    pm.instanceMatrix.needsUpdate = true;
    pm.visible = n > 0;
  }

  // --- BUILD-IN ANIMATION (owner spec): a placed piece rises from its base over ~0.45s instead of popping
  // into existence, for LOCAL placements and remote players' builds alike. Disabled during the join replay
  // (main enables it once live) and by the Settings toggle. Base-anchored y-scale: the bottom edge stays put.
  buildAnimEnabled = false;
  private readonly animating: Array<{ solid: THREE.Object3D; t: number; y: number; minY: number }> = [];
  private animateIn(solid: THREE.Object3D): void {
    if (!this.buildAnimEnabled) return;
    const bb = new THREE.Box3().setFromObject(solid);
    if (!isFinite(bb.min.y) || !isFinite(bb.max.y) || bb.max.y - bb.min.y < 0.05) return;
    const minY = bb.min.y - solid.position.y; // local-space bottom offset
    this.animating.push({ solid, t: 0, y: solid.position.y, minY });
    solid.scale.y = 0.02;
    solid.position.y = solid.position.y + minY * (1 - 0.02);
  }
  tickAnimations(dt: number): void {
    for (let i = this.animating.length - 1; i >= 0; i--) {
      const a = this.animating[i];
      a.t = Math.min(1, a.t + dt / BUILD.placeAnimSecs);
      const s = 1 - (1 - a.t) * (1 - a.t); // ease-out: fast rise, soft settle
      if (!a.solid.parent) { this.animating.splice(i, 1); continue; } // demolished mid-animation
      a.solid.scale.y = Math.max(0.02, s);
      a.solid.position.y = a.y + a.minY * (1 - s);
      if (a.t >= 1) { a.solid.scale.y = 1; a.solid.position.y = a.y; this.animating.splice(i, 1); }
      // Instanced GLB pieces render from their pool matrices, not the group -- push the anim transform.
      const pid = a.solid.userData.placedId as string | undefined;
      if (pid) this.glbInstances.updateMatrix(pid, a.solid);
    }
  }

  // Hand a freshly committed solid to the instancer (no-op unless makePiece marked it glbWant).
  private adoptGLB(id: string, solid: THREE.Group): void {
    const want = solid.userData.glbWant as { file: string; target: THREE.Box3; verticalFix: boolean } | undefined;
    if (!want) return;
    this.glbInstances.attach(id, solid, want.file, want.target, want.verticalFix);
  }

  place(): boolean {
    if (!this.active || !this.ghostValid) return false;
    if (!this.canAfford()) return false; // not enough resources -> ghost is blue, placement blocked
    if (!this.buildAuthed(this.gPos.x, this.gPos.z)) return false; // authority re-check: never build inside a claim you don't own
    if (!this.devFree && !this.remoteApplying && this.inventory) { const c = this.costFor(this.piece, this.tier); this.inventory.take(c.item, c.amount); }
    const solid = makePiece(this.piece, false, this.tier, null, true); // instanced: the Builder adopts the GLB below
    solid.position.copy(this.gPos);
    solid.rotation.y = this.gYaw;
    this.group.add(solid);
    this.animateIn(solid); // ~0.45s base-anchored rise (no-op during replay / when the setting is off)

    // COMMIT: register the instance in the placed map + spatial index, reserve its occupancy slots,
    // and fold it into the support graph. The mirror-array writes below keep every downstream read
    // (colliders, isUnderRoof, openings, getFoundations, ceiling levels) identical to before. Each
    // branch ALSO records a per-instance undo closure so a later collapse can reverse exactly these
    // writes (with the live keys/levels in scope) — far safer than re-deriving them from the transform.
    const id = this.commit(solid);
    this.adoptGLB(id, solid); // instanced GLB visuals join their pools once the piece has its id
    this.pieceTier.set(id, this.tier); // remember the build tier for refunds
    if (this.raisedFoundation && isGroundFoundation(this.piece)) this.pieceRaised.add(id); // save replay re-passes it
    // Record the resolved transform for the network broadcast (a fresh object, so NetBackend can tell
    // a new placement from the previous one by identity).
    this.lastPlacedInfo = { piece: this.piece, tier: this.tier, x: this.gPos.x, y: this.gPos.y, z: this.gPos.z, yaw: this.gYaw, sign: this.doorSign, raised: this.raisedFoundation };

    if (isFootprint(this.piece)) {
      const entry: PlacedFoundation = {
        type: footprintType(this.piece), x: this.gPos.x, z: this.gPos.z, yaw: this.gYaw,
        top: this.ghostTop, base: this.ghostBase, tier: this.tier, elevated: isCeiling(this.piece),
      };
      this.foundations.push(entry);
      const ceilEntry = isCeiling(this.piece) ? { mesh: solid, top: this.ghostTop } : null;
      if (ceilEntry) this.ceilingMeshes.push(ceilEntry);
      this.mirrorUndo.set(id, () => {
        spliceItem(this.foundations, entry);
        if (ceilEntry) spliceItem(this.ceilingMeshes, ceilEntry);
      });
    } else if (isWallSlot(this.piece)) {
      const h = wallSlotHeight(this.piece);
      const key = this.pendingWallKey;
      const prevStack = this.wallStacks.get(key) ?? 0;
      const top = prevStack + h;
      this.wallStacks.set(key, top);
      const tops = this.wallTops.get(key) ?? [];
      tops.push(top); // record this piece's cumulative top as a ceiling snap level
      this.wallTops.set(key, tops);
      const wallEntry: PlacedWall = { id: this.piece, cx: this.gPos.x, cy: this.gPos.y, cz: this.gPos.z, yaw: this.gYaw, h, base: this.gPos.y - h / 2 };
      this.walls.push(wallEntry);
      this.mirrorUndo.set(id, () => {
        spliceItem(this.walls, wallEntry);
        // Drop THIS wall's recorded top, then derive the edge's stack height from whatever REMAINS.
        // This is order-independent: collapsing a stack bottom-up (or removing a mid piece) leaves
        // wallStacks/wallTops matching exactly the surviving walls. The old code restored a captured
        // prevStack, so a multi-piece collapse left a stale height behind -> you could rebuild a piece
        // floating in the air above where the stack used to be.
        const cur = this.wallTops.get(key);
        if (cur) {
          const t = cur.lastIndexOf(top);
          if (t >= 0) cur.splice(t, 1);
        }
        if (cur && cur.length > 0) this.wallStacks.set(key, Math.max(...cur));
        else { this.wallTops.delete(key); this.wallStacks.delete(key); }
      });
    } else if (this.piece === 'stairs') {
      const entry: PlacedStair = { cx: this.gPos.x, cy: this.gPos.y, cz: this.gPos.z, yaw: this.gYaw };
      this.stairs.push(entry);
      this.mirrorUndo.set(id, () => spliceItem(this.stairs, entry));
    } else if (this.piece === 'pillar') {
      const entry: PlacedPillar = { cx: this.gPos.x, cy: this.gPos.y, cz: this.gPos.z };
      this.pillars.push(entry);
      this.mirrorUndo.set(id, () => spliceItem(this.pillars, entry));
    } else if (this.piece === 'door') {
      const entry: PlacedDoor = { mesh: solid, hx: this.gPos.x, hy: this.gPos.y, hz: this.gPos.z, closedYaw: this.gYaw, sign: this.doorSign, open: false, t: 0, owner: LOCAL_PLAYER };
      this.doors.push(entry);
      this.mirrorUndo.set(id, () => spliceItem(this.doors, entry));
    }
    this.socketIndex = 0;
    // RETROACTIVE RELINK: wire pieces that were placed BEFORE this one but would have chosen it as a
    // support parent (supports[] edges are otherwise frozen at placement) -- this is what lets a
    // rebuilt wall re-carry the sagging ceiling above it.
    const np = this.placed.get(id);
    if (np) this.relinkSupports(np);
    // A fresh piece can never starve an EXISTING run (added support edges only raise values), but the
    // cascade runs here too so place() and any future demolish share one collapse path.
    this.runCollapseCascade();
    // Refresh the debug gizmos for the new base state (no-op while debug is off).
    this.refreshDebug();
    return true;
  }

  // REMOVAL WINDOW (Rust-style, owner spec): can `target` be demolished AT ALL right now, independent of
  // who is asking? Free-build grace (removeGraceSec) always applies from placement, no claim required --
  // this is what lets a stranger undo a piece someone just misplaced on open ground. A claim that covers
  // the piece AND that the remover can access extends the window to removeGraceClaimSec. Past whichever
  // window applies, the piece is permanent: no claim + past removeGraceSec never opens again. Dev
  // free-build and remote replays (already judged on the acting client) bypass this entirely.
  private removeWindowOk(target: PlacedPiece): boolean {
    if (this.devFree || this.remoteApplying) return true;
    // CRAFTED DOORS are PICK-UP-ABLE, not window-gated (owner 2026-07-12): an UNLOCKED door comes off
    // any time (demolishTarget refunds the door item -- that's the pickup); a LOCKED door NEVER comes
    // off with the hammer (key reminder) -- raid it down or take its frame instead.
    if (target.defId === 'door' && target.doorKind) return !this.doorUnitLocked(target);
    const age = nowSec() - target.placedAt;
    if (age < BUILD.removeGraceSec) return true;
    const a = this.claimAuthAt?.(target.mesh.position.x, target.mesh.position.z) ?? null;
    return !!a?.build && age < BUILD.removeGraceClaimSec;
  }

  // Any leaf of this door unit carrying a lock? (Both leaves of a double door live under the piece's mesh.)
  private doorUnitLocked(piece: PlacedPiece): boolean {
    for (const d of this.doors) if (d.mesh.parent === piece.mesh && d.lock) return true;
    return false;
  }

  // Read-only preview of removeWindowOk for the HUD (hold-to-remove shows a refusal hint instead of a
  // progress ring when this is false). Same pick as demolish(); does not mutate anything.
  canRemoveNow(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number): boolean {
    const target = this.pickPlaced(ox, oy, oz, dx, dy, dz);
    return !!target && this.removeWindowOk(target);
  }

  // Is the hammer currently aimed at SOMETHING (the red/green wrap box is showing)? Lets the HUD tell
  // "no piece under the reticle" apart from "a piece is here but its removal window expired".
  get demolishHighlightVisible(): boolean {
    return this.demolishHighlight.visible;
  }

  // Demolish the placed piece the ray (o,d) hits first, then let the support graph crumble whatever
  // it was holding up. The ray is tested against every placed solid's meshes (they carry
  // userData.placedId); the nearest hit is removed via removePiece(...,true) so it crumbles, then the
  // graph is recomputed and the cascade collapses any now-unsupported dependents (e.g. a ceiling
  // whose last wall just went). `dir` is assumed normalized. Returns true if something was demolished.
  demolish(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number): boolean {
    if (!this.editing) return false;
    return this.demolishTarget(this.pickPlaced(ox, oy, oz, dx, dy, dz));
  }

  // Remove ONE already-resolved piece. Split out of demolish() so the LOCAL ray pick and the position-keyed
  // net apply feed the exact same removal + cascade path (the key is what keeps every client's world identical).
  private demolishTarget(target: PlacedPiece | undefined): boolean {
    if (!target) return false;
    // REACH GATE: the pick ray runs from the camera (far=500 so zoomed-out picking works), so cap by
    // the PLAYER's distance to the struck piece -- no dismantling across the map. Remote ops were
    // reach-gated on the ACTOR's client; ours is somewhere else entirely.
    if (!this.remoteApplying && target.mesh.position.distanceToSquared(this.playerPos) > (BUILD.reach + S) ** 2) return false;
    // REMOVAL WINDOW GATE: refuse outright once the piece has aged past its free (or claimed) grace
    // window -- see removeWindowOk. Checked before the claim-access gate below so an EXPIRED piece
    // inside your own claim still reads 'permanent', not 'no access'.
    if (!this.removeWindowOk(target)) return false;
    // GATE: free-build areas (no claim) are open to anyone; inside a claim, only the owner/members may remove. Dev
    // free-build bypasses it. Refund 50% of the build cost, but only to the claim's AUTHOR (a raider who merely OWNS
    // a captured base dismantles, no refund; free-build has no author, so no refund either).
    if (!this.devFree && !this.remoteApplying) {
      const a = this.claimAuthAt?.(target.mesh.position.x, target.mesh.position.z) ?? null;
      if (a && !a.build) return false; // a claim you don't have access to
      const tier = this.pieceTier.get(target.id);
      // Doors are excluded from the material table: a hung door leaf is a crafted ITEM. Removing an
      // UNLOCKED crafted door is a PICKUP (owner 2026-07-12) -- the exact item returns to the hand.
      // (A locked one never reaches here: removeWindowOk refuses it.)
      if (this.inventory && target.defId === 'door' && target.doorKind) this.inventory.add(target.doorKind as ItemId, 1);
      if (tier && this.inventory && target.defId !== 'door') {
        const c = this.costFor(target.defId, tier);
        // UNDO GRACE: dismantling YOUR OWN just-placed piece refunds 100% inside the window (a
        // misplaced wall is free to fix). Past the window, the raid-proof rule stands: 50%, and
        // only to the claim's AUTHOR.
        if (target.placedBy === LOCAL_PLAYER && nowSec() - target.placedAt < BUILD.undoGraceSec) this.inventory.add(c.item, c.amount);
        else if (a?.author) this.inventory.add(c.item, Math.floor(c.amount * 0.5));
      }
    }
    // Remove the struck piece (crumbles), then relax the graph and cascade-collapse its dependents.
    this.removePiece(target, true);
    this.stability.recompute();
    this.runCollapseCascade();
    this.refreshDebug();
    return true;
  }

  // True if the player may remove pieces at (x,z): free-build (no claim) OR a claim they can access (or dev free-build).
  // Used by the aim highlight so "can't remove here" reads before you press the key.
  canRemoveAt(x: number, z: number): boolean {
    return this.buildAuthed(x, z);
  }

  // Is (x,z) covered by a placed build piece? A foundation/floor covers its tile, so any piece whose centre sits
  // within ~half a tile of (x,z) counts. Used to STOP a felled tree from respawning up through someone's base -- the
  // node holds its respawn until the build there is gone. Cheap: one spatial-index circle query.
  buildCoversAt(x: number, z: number): boolean {
    return this.index.queryCircle(x, z, S * 0.6).length > 0;
  }

  // --- NETWORK APPLY (spec section 19, M1). Another player's build ops (and the join-time rehydrate
  // replay) commit through the SAME solver + place() path as a local placement, so slots, wall stacks,
  // support edges, and the room graph stay byte-identical to what the acting client computed. The op
  // carries the RESOLVED transform; we point the solver's cursor at it and let it snap to the same
  // socket, then verify it landed where the actor said before committing (fail-soft on divergence). ---
  applyNetPlace(pieceId: string, tier: BuildTier, x: number, y: number, z: number, yaw: number, sign = 1, raised = false, skinId: SkinId | null = null): boolean {
    if (!(pieceId in CATALOG)) return false; // wire data is untrusted
    const piece = pieceId as PieceId;
    const saved = {
      active: this.active, piece: this.piece, tier: this.tier, yawDeg: this.yawDeg,
      doorSign: this.doorSign, raised: this.raisedFoundation, px: this.playerPos.clone(),
    };
    this.remoteApplying = true;
    this.active = true;
    this.selectPiece(piece); // resets socket cycling + sticky state like a fresh local hold
    this.tier = tier;
    this.yawDeg = THREE.MathUtils.radToDeg(yaw);
    this.doorSign = sign;
    this.raisedFoundation = raised;
    this.playerPos.set(x, y, z); // reach is measured from the ACTOR, who stood at the piece
    const target = new THREE.Vector3(x, y, z);
    this.update(target);
    let ok = false;
    const dy = ((this.gYaw - yaw) % (Math.PI * 2) + Math.PI * 3) % (Math.PI * 2) - Math.PI;
    if (this.ghostValid && this.gPos.distanceToSquared(target) < 0.09 && Math.abs(dy) < 0.03) {
      const preId = this.nextId; // place() assigns exactly `p${preId}` on success (see commit())
      ok = this.place();
      if (ok && skinId) { const p = this.placed.get(`p${preId}`); if (p) this.applySkinTo(p, skinId); } // rebuild replay: skin persists across a save reload
    }
    else console.warn('[oxide net] remote build op did not resolve here -- skipped', pieceId, x.toFixed(1), z.toFixed(1), this.lastReason);
    // Restore the local player's build state (their held ghost re-solves on the next frame's update()).
    this.remoteApplying = false;
    this.selectPiece(saved.piece);
    this.tier = saved.tier;
    this.yawDeg = saved.yawDeg;
    this.doorSign = saved.doorSign;
    this.raisedFoundation = saved.raised;
    this.playerPos.copy(saved.px);
    this.setActive(saved.active);
    return ok;
  }

  applyNetDemolish(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number): boolean {
    this.remoteApplying = true;
    try { return this.demolish(ox, oy, oz, dx, dy, dz); } finally { this.remoteApplying = false; }
  }

  applyNetUpgrade(tier: BuildTier, ox: number, oy: number, oz: number, dx: number, dy: number, dz: number): boolean {
    this.remoteApplying = true;
    try { return this.upgradeAt(ox, oy, oz, dx, dy, dz, tier); } finally { this.remoteApplying = false; }
  }

  applyNetRepair(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number): boolean {
    this.remoteApplying = true;
    try { return this.repairAt(ox, oy, oz, dx, dy, dz); } finally { this.remoteApplying = false; }
  }

  // --- POSITION-KEYED net apply. The acting client resolves its aim RAY to one placed piece (resolvePieceAt)
  // and broadcasts THAT piece's world position -- not the ray. Every other client edits the piece AT that spot,
  // so a wall demolished here is the same wall gone everywhere, instead of each client re-raycasting and possibly
  // striking the foundation behind it (the wrong-piece + not-synced bugs). The legacy ray applyNet* above stay
  // for replaying old persisted ops that predate this. ---
  applyNetDemolishAt(x: number, y: number, z: number): boolean {
    this.remoteApplying = true;
    try { return this.demolishTarget(this.pieceAtKey(x, y, z)); } finally { this.remoteApplying = false; }
  }

  applyNetUpgradeAt(tier: BuildTier, x: number, y: number, z: number): boolean {
    this.remoteApplying = true;
    try { return this.upgradeTarget(this.pieceAtKey(x, y, z), tier); } finally { this.remoteApplying = false; }
  }

  applyNetRepairAt(x: number, y: number, z: number): boolean {
    this.remoteApplying = true;
    try { return this.repairTarget(this.pieceAtKey(x, y, z)); } finally { this.remoteApplying = false; }
  }

  // Resolve an aim ray to the world position of the placed piece it strikes (or null). The acting client
  // calls this to stamp a demolish/upgrade/repair op with a STABLE piece key instead of the ray itself.
  resolvePieceAt(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number): { x: number; y: number; z: number } | null {
    const p = this.pickPlaced(ox, oy, oz, dx, dy, dz);
    return p ? { x: p.mesh.position.x, y: p.mesh.position.y, z: p.mesh.position.z } : null;
  }

  // Find the placed piece nearest a broadcast key (a piece's own mesh centre). Distinct piece centres sit far
  // apart (edge midpoints / corners / tile centres), so a tight radius resolves the exact one struck elsewhere.
  private pieceAtKey(x: number, y: number, z: number): PlacedPiece | undefined {
    let best: PlacedPiece | undefined;
    let bestD = 0.25; // (0.5u)^2 -- generous against float/JSON drift, far tighter than the gap between two real pieces
    for (const p of this.placed.values()) {
      const mx = p.mesh.position.x - x, my = p.mesh.position.y - y, mz = p.mesh.position.z - z;
      const d = mx * mx + my * my + mz * mz;
      if (d < bestD) { bestD = d; best = p; }
    }
    return best;
  }

  // --- LOCAL WORLD SAVE (game/persist.ts). serializeWorld snapshots every placed piece in PLACEMENT
  // ORDER (instance ids are monotonic); deserializeWorld REPLAYS them through the same
  // applyNetPlace/placeCraftedDoor paths the network rehydrate uses, so slots, wall stacks, support
  // edges, colliders and the room graph all regenerate instead of being field-poked. Returns the
  // old->new id map so mounted deployables can re-find their host piece. A piece that no longer
  // resolves (terrain/rule changes since the save) fails soft: skipped with the applyNetPlace warn,
  // and anything that rested on it simply skips too. ---
  serializeWorld(): SavedBuild {
    const pieces: SavedBuildPiece[] = [];
    const ids = [...this.placed.keys()].sort((a, b) => Number(a.slice(1)) - Number(b.slice(1)));
    for (const id of ids) {
      const p = this.placed.get(id)!;
      const tier = this.pieceTier.get(id) ?? 'wood';
      const base = { tier, x: p.position.x, y: p.position.y, z: p.position.z, yaw: p.yaw, hp: p.hp, oldId: id, skinId: p.skinId ?? null };
      if (p.defId === 'door' && !this.doors.some((d) => d.mesh === p.mesh)) {
        // A crafted door unit (its leaves parent to the unit group; the legacy build-menu door IS the
        // doors[] mesh). The held-item kind recovers from tier + leaf count -- no extra bookkeeping.
        const leaves = this.doors.filter((d) => d.mesh.parent === p.mesh);
        const def: CraftedDoorId = tier === 'wood' ? 'wooden_door' : tier === 'reinforced' ? 'reinforced_door'
          : leaves.length === 2 ? 'metal_double_door' : 'metal_door';
        pieces.push({ k: 'craftdoor', def, sign: leaves.find((l) => !l.mirror)?.sign ?? 1, raised: false, ...base });
      } else {
        const sign = p.defId === 'door' ? this.doors.find((d) => d.mesh === p.mesh)?.sign ?? 1 : 1;
        pieces.push({ k: 'piece', def: p.defId, sign, raised: this.pieceRaised.has(id), ...base });
      }
    }
    const doors: SavedDoorState[] = this.doors.map((d) => ({
      x: d.hx, y: d.hy, z: d.hz, mirror: !!d.mirror, open: d.open,
      lock: d.lock, code: d.code, granted: d.granted?.slice(), owner: d.owner,
      autoclose: d.autoclose || undefined,
    }));
    return { pieces, doors };
  }

  deserializeWorld(data: SavedBuild): Map<string, string> {
    const remap = new Map<string, string>();
    for (const r of data.pieces) {
      const newId = `p${this.nextId}`; // commit()/placeCraftedDoor assign exactly this id on success
      const ok = r.k === 'craftdoor'
        ? CRAFTED_DOOR_IDS.has(r.def) && this.placeCraftedDoor(r.x, r.y, r.z, r.yaw, r.def as CraftedDoorId, r.sign)
        : this.applyNetPlace(r.def, r.tier, r.x, r.y, r.z, r.yaw, r.sign, r.raised, r.skinId ?? null);
      if (!ok) continue;
      remap.set(r.oldId, newId);
      const p = this.placed.get(newId);
      if (p) p.hp = Math.min(p.maxHp, Math.max(1, r.hp)); // raid damage survives the reload
    }
    // Door state after every leaf exists again: match by hinge position + which leaf of a pair.
    for (const s of data.doors) {
      const d = this.doors.find((x) => Math.abs(x.hx - s.x) < 0.05 && Math.abs(x.hy - s.y) < 0.05 && Math.abs(x.hz - s.z) < 0.05 && !!x.mirror === s.mirror);
      if (!d) continue;
      d.open = s.open; // tickDoors animates t toward it
      d.owner = s.owner;
      if (s.autoclose) d.autoclose = true;
      if (s.granted?.length) d.granted = s.granted.slice();
      if (s.code) d.code = s.code;
      if (s.lock && !d.lock) {
        // Same attach as lockAimedDoor; a pair shares the lock, so its own saved entry then no-ops.
        const w = BUILD.doorwayWidth * 0.99, h = BUILD.doorwayHeight * 0.99;
        makeLockPair(d.mesh, s.lock, w * 0.8, h * 0.5, 0.09);
        d.lock = s.lock;
        if (d.pair) { d.pair.lock = s.lock; if (s.code) d.pair.code = s.code; }
        if (s.lock === 'keypad_lock') this.setKeypadLight(d); // restore the green/red LED
      }
    }
    return remap;
  }

  // May the local player build/remove at (x,z)? Free-build (no claim over the spot) is open to anyone; a claim blocks
  // anyone who isn't its owner or an added member. Dev free-build always allows; an unwired hook reads as free-build.
  private buildAuthed(x: number, z: number): boolean {
    if (this.devFree || this.remoteApplying) return true; // remote ops were claim-checked on the ACTOR's client

    const a = this.claimAuthAt?.(x, z);
    return !a || a.build;
  }

  // --- BASE-CLAIM UPKEEP support (the upkeep system reads the footprint cost + drives decay) ---
  // Total build materials of every piece whose centre falls within `radius` of (x,z), aggregated by item id.
  baseMaterials(x: number, z: number, radius: number): Map<ItemId, number> {
    const r2 = radius * radius; const out = new Map<ItemId, number>();
    for (const p of this.placed.values()) {
      const dx = p.position.x - x, dz = p.position.z - z;
      if (dx * dx + dz * dz > r2) continue;
      const c = this.costFor(p.defId, this.pieceTier.get(p.id) ?? 'wood');
      out.set(c.item, (out.get(c.item) ?? 0) + c.amount);
    }
    return out;
  }

  // DECAY: crumble ONE piece within `radius` of (x,z), then cascade-collapse anything that loses support. Returns true
  // if a piece was removed (false = nothing left). Target order, so the base PEELS gradually instead of a load-bearing
  // piece yanking the whole structure down at once: (1) non-load-bearing LEAVES first (nothing rests on them -> no
  // cascade), (2) higher pieces before lower (top-down), (3) weaker tier before stronger. Only when nothing but the
  // load-bearing skeleton remains does removing it cascade -- the natural final collapse.
  decayOneIn(x: number, z: number, radius: number): boolean {
    const r2 = radius * radius;
    const dependedOn = new Set<string>(); // every id some other piece rests ON (i.e. load-bearing)
    for (const p of this.placed.values()) for (const s of p.supports) dependedOn.add(s);
    const tierRank: Record<string, number> = { twig: 0, wood: 1, stone: 2, metal: 3, reinforced: 4 };
    let victim: PlacedPiece | null = null, best = Infinity;
    for (const p of this.placed.values()) {
      const dx = p.position.x - x, dz = p.position.z - z;
      if (dx * dx + dz * dz > r2) continue;
      // load-bearing pieces are pushed far back; among the rest, prefer higher (top-down) then weaker tier.
      const score = (dependedOn.has(p.id) ? 1e6 : 0) - p.position.y * 100 + (tierRank[this.pieceTier.get(p.id) ?? 'wood'] ?? 1);
      if (score < best) { best = score; victim = p; }
    }
    if (!victim) return false;
    this.removePiece(victim, true);
    this.stability.recompute();
    this.runCollapseCascade();
    this.refreshDebug();
    return true;
  }

  // WITHER (upkeep decay, owner spec): drain HP from up to `maxPieces` of the outermost non-load-bearing
  // pieces so a starved base visibly rots (hammer shows the dropping bars) instead of pieces popping whole.
  // A piece reaching 0 crumbles through the normal removal path (cascade + refresh). Returns true when
  // something actually crumbled this tick (main plays the crumble SFX off that).
  witherIn(x: number, z: number, radius: number, hpPerSec: number, dt: number, maxPieces = 3): boolean {
    const r2 = radius * radius;
    const dependedOn = new Set<string>();
    for (const p of this.placed.values()) for (const s of p.supports) dependedOn.add(s);
    const tierRank: Record<string, number> = { twig: 0, wood: 1, stone: 2, metal: 3, reinforced: 4 };
    const scored: Array<{ p: PlacedPiece; score: number }> = [];
    for (const p of this.placed.values()) {
      const dx = p.position.x - x, dz = p.position.z - z;
      if (dx * dx + dz * dz > r2) continue;
      scored.push({ p, score: (dependedOn.has(p.id) ? 1e6 : 0) - p.position.y * 100 + (tierRank[this.pieceTier.get(p.id) ?? 'wood'] ?? 1) });
    }
    if (!scored.length) return false;
    scored.sort((a, b) => a.score - b.score);
    let crumbled = false;
    for (const { p } of scored.slice(0, maxPieces)) {
      p.hp -= hpPerSec * dt;
      p.lastDamagedAt = nowSec();
      if (p.hp <= 0) { this.removePiece(p, true); crumbled = true; }
    }
    if (crumbled) { this.stability.recompute(); this.runCollapseCascade(); this.refreshDebug(); }
    return crumbled;
  }

  // SELF-HEAL: a stocked claim slowly repairs its own pieces (raid/wear damage) toward full HP.
  // Pure HP top-up -- never crumbles anything, so no cascade/refresh work is needed here.
  healIn(x: number, z: number, radius: number, hpPerSec: number, dt: number): void {
    const r2 = radius * radius, step = hpPerSec * dt;
    for (const p of this.placed.values()) {
      if (p.hp >= p.maxHp) continue;
      const dx = p.position.x - x, dz = p.position.z - z;
      if (dx * dx + dz * dz > r2) continue;
      p.hp = Math.min(p.maxHp, p.hp + step);
    }
  }

  // UNCLAIMED DECAY: every placed piece NOT covered by any claim (per `isClaimed`) loses HP over
  // time at its tier's rate (twig fastest -- see UPKEEP.unclaimedDecay), regardless of any chest.
  // Pieces reaching 0 crumble via the normal removePiece+cascade path. Returns the count destroyed.
  decayUnclaimed(rates: Record<string, number>, dt: number, isClaimed: (x: number, z: number) => boolean): number {
    const dead: PlacedPiece[] = [];
    for (const p of this.placed.values()) {
      if (isClaimed(p.position.x, p.position.z)) continue;
      const rate = rates[this.pieceTier.get(p.id) ?? 'wood'] ?? 0;
      if (rate <= 0) continue;
      p.hp -= rate * dt;
      if (p.hp <= 0) dead.push(p);
    }
    for (const v of dead) this.removePiece(v, true);
    if (dead.length) { this.stability.recompute(); this.runCollapseCascade(); this.refreshDebug(); }
    return dead.length;
  }

  // EXPLOSION raid (owner spec): the DIRECT piece (`directId` -- the piece a rocket struck or a
  // charge was planted on) takes 100% of buildingDmg regardless of tier; every OTHER piece within
  // `radius` (3D) takes buildingDmg * splashFrac, falling linearly to 0 at the edge (diminishing
  // splash). Planted charges (C4/satchel) pass splashFrac 0: they damage ONLY their host piece.
  // Pieces at 0 HP crumble + cascade-collapse. Returns the count destroyed.
  // `scope` (owner spec 2026-07-12): 'all' = every piece class (C4/satchel/rocket/grenade); 'breach' =
  // DOORS ONLY among placed pieces (the explosive-ammo path -- windows/deployables get their own breach
  // damage in main.ts's explode()). Breach rounds can never take a wall or foundation down.
  explodeAt(x: number, y: number, z: number, radius: number, buildingDmg: number, directId: string | null = null, splashFrac = BUILD.explosion.splashFrac, scope: 'all' | 'breach' = 'all'): number {
    const r2 = radius * radius;
    const now = nowSec();
    const dead: PlacedPiece[] = [];
    for (const p of this.placed.values()) {
      if (scope === 'breach' && p.defId !== 'door') continue;
      let applied: number;
      if (p.id === directId) {
        applied = buildingDmg; // direct hit / planted: full damage (the piece's CENTRE may sit outside the radius)
      } else {
        const dx = p.position.x - x, dy = p.position.y - y, dz = p.position.z - z;
        const dd = dx * dx + dy * dy + dz * dz;
        if (dd > r2) continue;
        // Breach rounds hit the door they struck at full value inside the (small) blast radius -- the
        // splash falloff is for wall-raid area damage, not a door you are shooting point-blank.
        applied = scope === 'breach' ? buildingDmg * (1 - Math.sqrt(dd) / radius) : buildingDmg * splashFrac * (1 - Math.sqrt(dd) / radius);
      }
      if (applied <= 0.5) continue;
      p.hp -= applied;
      p.lastDamagedAt = now; // repair is locked out for BUILD.repairCooldownSec
      if (p.hp <= 0) dead.push(p);
    }
    for (const v of dead) this.removePiece(v, true);
    if (dead.length) { this.stability.recompute(); this.runCollapseCascade(); this.refreshDebug(); }
    return dead.length;
  }

  // The id of the placed piece the ray strikes within `far` units, or null. Public so the raid code
  // can resolve the DIRECT-HIT piece for explosions: the wall a rocket struck (re-cast from just
  // before the impact point) or the piece a C4/satchel is planted on (the aim ray at placement).
  pieceIdAt(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, far: number): string | null {
    return this.pickPlaced(ox, oy, oz, dx, dy, dz, far)?.id ?? null;
  }

  // INCENDIARY vs WOODEN doors (owner spec 2026-07-12): the fire-shell path. Burns the aimed piece for
  // `dmg` ONLY if it is a door AND its material is wood -- every other class/tier shrugs it off. One
  // call per trigger pull (not per pellet). Returns true when a wooden door actually burned.
  burnWoodDoorAt(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, dmg: number, far = 60): boolean {
    const target = this.pickPlaced(ox, oy, oz, dx, dy, dz, far);
    if (!target || target.defId !== 'door') return false;
    if ((this.pieceTier.get(target.id) ?? 'wood') !== 'wood') return false;
    target.hp -= dmg;
    target.lastDamagedAt = nowSec();
    if (target.hp <= 0) {
      this.removePiece(target, true);
      this.stability.recompute();
      this.runCollapseCascade();
      this.refreshDebug();
    }
    return true;
  }

  // DIRECT FIRE (bullets / melee tools) chips the aimed piece by the tier's per-SOURCE eco multiplier
  // (metal/reinforced sit at 0 = immune, so only explosives open them), plus the SOFT-SIDE rule: a
  // wall hit on its interior face (where the weak-side beams render) takes BUILD.softSideMult --
  // wall orientation is a real base-design decision. `rayFar` caps the pick so a bullet impact
  // re-cast cannot grab a wall far beyond the surface it actually struck. Returns true on a hit.
  //
  // `flat` (owner spec 2026-07-12, melee WEAPONS only): a per-tier EXACT damage table that bypasses
  // the multiplier + soft-side math entirely -- {twig: full weapon dmg, wood: 2, stone: 2, ...}; a
  // tier missing from the table takes 0 (metal/reinforced immunity). The caller builds it from
  // BUILD.weaponStructureDmg so the owner's flat numbers land exactly as written.
  damageAt(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, dmg: number, maxDist = Infinity, source: 'melee' | 'bullet' = 'melee', rayFar = 500, flat?: Record<string, number>): boolean {
    const target = this.pickPlaced(ox, oy, oz, dx, dy, dz, rayFar);
    if (!target) return false;
    if (maxDist !== Infinity && target.mesh.position.distanceToSquared(this.playerPos) > maxDist * maxDist) return false; // melee reach gate
    const tier = this.pieceTier.get(target.id) ?? 'wood';
    let applied: number;
    if (flat) {
      applied = flat[tier] ?? 0; // exact owner numbers: no soft-side bonus, missing tier = immune
    } else {
      const table = source === 'melee' ? BUILD.ecoMeleeMult : BUILD.ecoBulletMult;
      applied = dmg * (table[tier] ?? 1);
      // Shooter on the soft side: the shot travels AGAINST the soft face's outward normal.
      if (target.softDir && dx * target.softDir.x + dz * target.softDir.z < 0) applied *= BUILD.softSideMult;
    }
    if (applied <= 0.05) return false;
    target.hp -= applied;
    target.lastDamagedAt = nowSec();
    if (target.hp <= 0) {
      this.removePiece(target, true);
      this.stability.recompute();
      this.runCollapseCascade();
      this.refreshDebug();
    }
    return true;
  }

  // HAMMER REPAIR: restore the aimed piece to full HP for a share of its build cost (missing-HP
  // fraction * half the piece price). Gated like demolish (reach + claim) and LOCKED OUT for
  // BUILD.repairCooldownSec after the piece takes damage, so defenders can't out-repair a raid.
  repairAt(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number): boolean {
    if (!this.editing) return false;
    return this.repairTarget(this.pickPlaced(ox, oy, oz, dx, dy, dz));
  }

  private repairTarget(target: PlacedPiece | undefined): boolean {
    if (!target) return false;
    if (!this.remoteApplying && target.mesh.position.distanceToSquared(this.playerPos) > (BUILD.reach + S) ** 2) return false;
    if (!this.buildAuthed(target.mesh.position.x, target.mesh.position.z)) return false;
    if (target.hp >= target.maxHp) return false;
    if (nowSec() - target.lastDamagedAt < BUILD.repairCooldownSec) return false; // recently hit: let the raid resolve
    // INCREMENTAL repair (owner spec #7): each hammer hit spends up to +25 of the piece's own material and restores
    // HP in proportion, so you SMACK a wall a few times to bring it back rather than one free full heal. The last
    // hit only charges what's needed to top it off (never overpays past max HP).
    const tier = this.pieceTier.get(target.id) ?? 'wood';
    const c = this.costFor(target.defId, tier);           // full build material for this piece
    const full = Math.max(1, c.amount);
    const matToFull = full * ((target.maxHp - target.hp) / target.maxHp); // material equivalent of the remaining damage
    const cost = Math.max(1, Math.min(BUILD.repairPerHit, Math.ceil(matToFull)));
    if (!this.devFree && !this.remoteApplying && this.inventory) {
      if (this.inventory.count(c.item) < cost) return false; // can't afford this hit
      this.inventory.take(c.item, cost);
    }
    target.hp = Math.min(target.maxHp, target.hp + target.maxHp * (cost / full)); // HP restored ∝ material spent
    return true;
  }

  // HP of the build piece the ray is aimed at (for the hammer's hover HEALTH BAR, #6), or null if none is
  // aimed. When damaged, also carries the REPAIR COST PREVIEW (owner QoL): per-hit price + the total
  // material to fully restore it, mirroring exactly what repairTarget would charge.
  // What the hammer is on: piece kind + tier + HP, for the reticle chip and the wheel's context
  // (which upgrade slices apply). Same pick as demolish/repair, read-only.
  aimedPieceMeta(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number): { defId: PieceId; tier: BuildTier; hp: number; max: number } | null {
    if (!this.editing) return null;
    const t = this.pickPlaced(ox, oy, oz, dx, dy, dz);
    if (!t) return null;
    return { defId: t.defId, tier: this.pieceTier.get(t.id) ?? 'wood', hp: Math.max(0, t.hp), max: t.maxHp };
  }

  aimedPieceHp(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number): { hp: number; max: number; repair?: { item: ItemId; perHit: number; total: number } } | null {
    if (!this.editing) return null;
    const t = this.pickPlaced(ox, oy, oz, dx, dy, dz);
    if (!t) return null;
    const out: { hp: number; max: number; repair?: { item: ItemId; perHit: number; total: number } } = { hp: Math.max(0, t.hp), max: t.maxHp };
    if (t.hp < t.maxHp) {
      const tier = this.pieceTier.get(t.id) ?? 'wood';
      const c = this.costFor(t.defId, tier);
      const full = Math.max(1, c.amount);
      const matToFull = full * ((t.maxHp - Math.max(0, t.hp)) / t.maxHp);
      out.repair = { item: c.item, perHit: Math.max(1, Math.min(BUILD.repairPerHit, Math.ceil(matToFull))), total: Math.max(1, Math.ceil(matToFull)) };
    }
    return out;
  }

  // Upgrade the placed piece the ray hits to `tier`, rebuilding its solid mesh in place at the new
  // material and keeping the logical record (id, slots, support, colliders) untouched — only the
  // rendered geometry/material swaps. Same pick as demolish(). `dir` is assumed normalized. Returns
  // true if a piece upgraded. (A cupboard is hard-wired wood by its factory, so retiering it is a
  // no-op skin; the swap is still performed so colliders/userData stay consistent.)
  upgradeAt(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, tier: BuildTier): boolean {
    if (!this.editing) return false;
    return this.upgradeTarget(this.pickPlaced(ox, oy, oz, dx, dy, dz), tier);
  }

  private upgradeTarget(target: PlacedPiece | undefined, tier: BuildTier): boolean {
    if (!target) return false;
    // GATES, same trio placement runs: player reach (the pick ray itself is camera-based and long),
    // claim access, and UPGRADES ONLY -- letting anyone re-tier a stranger's stone wall down to twig
    // was a raid exploit.
    if (!this.remoteApplying && target.mesh.position.distanceToSquared(this.playerPos) > (BUILD.reach + S) ** 2) return false;
    if (!this.buildAuthed(target.mesh.position.x, target.mesh.position.z)) return false;
    // Door leaves are crafted items, not tiered build pieces: makePiece('door') would replace a
    // crafted pair with the legacy single-leaf mesh, so hammer-retier does not apply to them.
    if (target.defId === 'door') return false;
    const curTier = this.pieceTier.get(target.id) ?? tier;
    if ((this.tierRank[tier] ?? 0) < (this.tierRank[curTier] ?? 0)) return false; // no downgrades
    // Cost: charge the DELTA when the new tier uses the same resource (upgrading feels like
    // progression, not a full re-buy); a cross-resource jump (wood -> stone) charges the new tier in
    // full. Same tier = a no-op re-skin, free. Dev free-build bypasses the charge.
    if (curTier !== tier && !this.devFree && !this.remoteApplying && this.inventory) {
      const c = this.costFor(target.defId, tier);
      const prev = this.costFor(target.defId, curTier);
      const amount = prev.item === c.item ? Math.max(0, c.amount - prev.amount) : c.amount;
      if (this.inventory.count(c.item) < amount) return false; // can't afford the upgrade
      if (amount > 0) this.inventory.take(c.item, amount);
    }
    this.pieceTier.set(target.id, tier);
    // Re-seed HP for the new tier, preserving the DAMAGE FRACTION (an 80%-health wood wall upgrades
    // to an 80%-health stone wall -- upgrading is not a free mid-raid heal).
    const frac = target.maxHp > 0 ? Math.max(0, target.hp) / target.maxHp : 1;
    target.maxHp = BUILD.hp[tier] ?? target.maxHp;
    target.hp = target.maxHp * frac;
    // Rebuild the solid at the new tier and drop it in at the SAME transform. The factory keys off the
    // piece's defId, so it rebuilds the exact same shape — only the tier material changes.
    const fresh = makePiece(target.defId, false, tier, null, true);
    fresh.position.copy(target.mesh.position);
    fresh.rotation.y = target.yaw;
    fresh.userData.placedId = target.id;
    // Swap into the scene graph, free the old solid's geometry (tier materials are shared and kept).
    this.glbInstances.remove(target.id); // leave the old tier's pools before the new adopt
    this.group.remove(target.mesh);
    disposeSolid(target.mesh);
    this.group.add(fresh);
    this.adoptGLB(target.id, fresh);
    stitchSolid(fresh); // the re-skinned solid gets the same welded corners + world-flow UVs
    // Repoint every reference the read paths hold to the old mesh: the placed record, the foundation
    // tier mirror, the ceiling cutaway list, and the door swing list. Colliders are derived from the
    // logical mirror arrays (positions/heights), so they need no change beyond the door's mesh handle.
    target.mesh = fresh;
    this.retierMirror(target, fresh, tier);
    return true;
  }

  // Rebuild ONE piece's solid at its existing tier + transform with a new skin variant material --
  // same in-place mesh-swap path as upgradeAt (tier/HP/cost/support graph are all untouched, this is
  // cosmetics only). Shared by reskinClaim (dev dropdown) and applyNetPlace/deserializeWorld (a piece
  // placed with a saved skinId gets its real solid rebuilt once, right after the plain placement).
  private applySkinTo(p: PlacedPiece, skinId: SkinId | null): void {
    const tier = this.pieceTier.get(p.id) ?? 'wood';
    const fresh = makePiece(p.defId, false, tier, skinId, true);
    fresh.position.copy(p.mesh.position);
    fresh.rotation.y = p.yaw;
    fresh.userData.placedId = p.id;
    this.glbInstances.remove(p.id);
    this.group.remove(p.mesh);
    disposeSolid(p.mesh);
    this.group.add(fresh);
    this.adoptGLB(p.id, fresh);
    stitchSolid(fresh);
    p.mesh = fresh;
    p.skinId = skinId;
    this.retierMirror(p, fresh, tier);
  }

  // DEV-ONLY cosmetics reskin (F2 dropdown, spec: BUILDING SKINS HOOK). Reskins every piece placed BY
  // THE LOCAL PLAYER (their claim). `skinId` null clears back to the plain tier look. Doors are
  // skipped: their leaves are crafted-item models, not tier materials (see upgradeAt's door guard).
  reskinClaim(skinId: SkinId | null): number {
    let n = 0;
    for (const p of this.placed.values()) {
      if (p.placedBy !== LOCAL_PLAYER || p.defId === 'door') continue;
      if (p.skinId === skinId) continue;
      this.applySkinTo(p, skinId);
      n++;
    }
    return n;
  }

  // Ray-pick the nearest placed solid (the shared pick for demolish/upgrade/aim). Tests the ray
  // against every placed piece's mesh (they carry userData.placedId) and returns the closest hit, or
  // undefined. `dir` is normalized in place; reuses the pooled raycaster/vectors (no allocations).
  private pickPlaced(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, far = 500): PlacedPiece | undefined {
    this.demolishRay.set(ox, oy, oz);
    this.demolishDir.set(dx, dy, dz).normalize();
    this.raycaster.set(this.demolishRay, this.demolishDir);
    // The cursor ray originates at the THIRD-PERSON camera, which is ~34u from the focus at default
    // zoom and much farther zoomed out -- a tight cap silently broke picking (no red, no demolish) at
    // anything but close zoom. Default generous; the nearest hit wins, so this just targets whatever
    // placed piece is under the cursor at any zoom. (Impact re-casts pass a short `far` instead.)
    this.raycaster.far = far;
    // Placed pieces are static; the renderer already keeps their world matrices current each frame, so
    // there is no need to force a full-group updateMatrixWorld(true) on every hammer-held frame. New
    // pieces get their matrices updated once at placement (addPieceMesh) so same-frame picking works.
    let hit: PlacedPiece | undefined;
    let hitDist = Infinity;
    for (const p of this.placed.values()) {
      const hits = this.raycaster.intersectObject(p.mesh, true);
      if (hits.length > 0 && hits[0].distance < hitDist) { hitDist = hits[0].distance; hit = p; }
    }
    return hit;
  }

  // After an in-place mesh swap (upgradeAt), repoint the mirror-array references that still hold the
  // old solid, and record the new tier on the foundation mirror so a future read sees the right tier.
  // Walls/cupboard keep no mesh reference, so only foundations (tier + ceiling cutaway list) and
  // doors (swing handle) need patching.
  private retierMirror(p: PlacedPiece, fresh: THREE.Group, tier: BuildTier): void {
    if (isFootprint(p.defId)) {
      for (const f of this.foundations) {
        if (f.x === p.position.x && f.z === p.position.z && Math.abs(f.top - (p.top ?? f.top)) < 1e-6) { f.tier = tier; break; }
      }
      for (const c of this.ceilingMeshes) {
        if (c.top === p.top && Math.abs(c.mesh.position.x - p.position.x) < 1e-6 && Math.abs(c.mesh.position.z - p.position.z) < 1e-6) { c.mesh = fresh; break; }
      }
    } else if (p.defId === 'door') {
      for (const d of this.doors) {
        if (d.mesh.userData.placedId === p.id) { d.mesh = fresh; break; }
      }
    }
  }

  // Build a PlacedPiece record for the freshly placed solid, insert it into the spatial index +
  // placed map, reserve its occupancy slots, and register it with the support graph (which recomputes
  // support values from the grounded seeds). Footprints/walls carry their height band so support
  // resolution and the overlap rules can use it. The crumble field is wired and ready for a future
  // demolish/removal cascade; nothing removes a placed piece through the current public API yet.
  private commit(solid: THREE.Group): string {
    const id = `p${this.nextId++}`;
    const def = CATALOG[this.piece];
    let top: number | undefined;
    let base: number | undefined;
    if (isFootprint(this.piece)) {
      top = this.ghostTop; base = this.ghostBase;
    } else if (isWallSlot(this.piece) || this.piece === 'pillar') {
      // Pillars carry a wall-height band like walls, so the ceiling cap scan can read their top.
      const h = this.piece === 'pillar' ? BUILD.wallHeight : wallSlotHeight(this.piece);
      top = this.gPos.y + h / 2; base = this.gPos.y - h / 2;
    }
    // Doors get their own Rust-matched pool (the legacy buildwheel door counts as a wooden door);
    // every other piece draws from the tier ladder.
    const tierHp = this.piece === 'door' ? (BUILD.doorHp.door ?? 200) : (BUILD.hp[this.tier] ?? 250);
    const piece: PlacedPiece = {
      id,
      defId: this.piece,
      position: this.gPos.clone(),
      quaternion: new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), this.gYaw),
      yaw: this.gYaw,
      family: def.footprintFamily,
      top, base,
      occupiedSlots: this.pendingSlots,
      supportValue: 0,
      supports: this.pendingParents,
      mesh: solid,
      hp: tierHp, maxHp: tierHp, lastDamagedAt: -1e9,
      placedAt: nowSec(), placedBy: LOCAL_PLAYER,
      softDir: this.pendingSoftDir ?? undefined,
      skinId: null, // cosmetics default: plain tier look until reskinClaim() is used
    };
    this.pendingSoftDir = null;
    solid.userData.placedId = id; // so a crumble can find this instance's mirror entries / claim ring
    solid.updateMatrixWorld(true); // one-time so a same-frame hammer pick sees correct world matrices (pickPlaced no longer force-updates)
    stitchSolid(solid); // world-hash corner welding + world-anchored UV flow (see stitchSolid)
    this.placed.set(id, piece);
    this.index.insert(piece);
    this.ledger.reserve(this.pendingSlots);
    this.stability.register(piece); // recompute() runs inside register
    this.roomsDirty = true; // the room graph re-derives lazily from the changed placed set
    this.pendingSlots = [];
    this.pendingParents = [];
    return id;
  }

  // RETROACTIVE SUPPORT RELINKING. supports[] parent edges are frozen when a piece is placed, so
  // without this a rebuilt wall under a sagging ceiling never re-supports it and a half-collapsed
  // base is unrepairable. After `np` commits, find the ALREADY-PLACED pieces that would have chosen
  // it as a parent had it existed when they were placed (the same adjacency scans updateWall /
  // updateCeiling run), append np.id to their supports, and recompute. Placement-event cost only --
  // a few local index probes, never per-frame.
  // The scenario this must hold: wall demolished -> the ceiling capping it loses that parent on the
  // recompute and sags (or crumbles); wall rebuilt into the same socket -> the ceiling's supports[]
  // regains the NEW wall's id here and recompute() credits it again -- the base is repairable.
  private relinkSupports(np: PlacedPiece): void {
    let linked = false;
    const adopt = (q: PlacedPiece): void => {
      if (q.id === np.id || q.supports.includes(np.id)) return;
      q.supports.push(np.id);
      linked = true;
    };
    const inset = BUILD.wallThick / 2;
    if (isWallSlot(np.defId) || np.defId === 'pillar') {
      const top = np.top ?? 0;
      for (const q of this.index.queryCircle(np.position.x, np.position.z, S * 1.5)) {
        if (q.family === 'none') {
          // A wall directly ABOVE the new one in the same stack (the wallBelow adjacency, inverted):
          // its base meets our top on the same edge column.
          if (!isWallSlot(q.defId) || q.base === undefined) continue;
          if (Math.abs(q.base - top) > 0.2) continue;
          const dd = (q.position.x - np.position.x) ** 2 + (q.position.z - np.position.z) ** 2;
          if (dd < 0.25) adopt(q);
        } else if (isCeiling(q.defId) && q.top !== undefined && Math.abs(q.top - top) <= 0.35) {
          // A ceiling whose cap band meets our top: wallParentsAt would have listed us -- walls sit
          // on the ceiling's inset edge midpoints, pillars on its corners.
          if (np.defId === 'pillar') {
            for (const c of (q.family === 'square' ? SQUARE_CORNERS : TRI_CORNERS)) {
              const r = rotXZ(c.x, c.z, q.yaw);
              const dd = (q.position.x + r.x - np.position.x) ** 2 + (q.position.z + r.z - np.position.z) ** 2;
              if (dd < 0.25) { adopt(q); break; }
            }
          } else {
            for (const e of edgesFor(q.family as FootprintShape)) {
              const m = rotXZ(e.mx, e.mz, q.yaw);
              const n = rotXZ(e.nx, e.nz, q.yaw);
              const dd = (q.position.x + m.x - n.x * inset - np.position.x) ** 2 + (q.position.z + m.z - n.z * inset - np.position.z) ** 2;
              if (dd < 0.25) { adopt(q); break; }
            }
          }
        }
      }
    } else if (np.family !== 'none' && np.top !== undefined) {
      const top = np.top;
      // A rebuilt footprint re-adopts the walls whose base stands on its edges (the first wall of a
      // stack parents to the footprint under it) ...
      for (const e of edgesFor(np.family as FootprintShape)) {
        const m = rotXZ(e.mx, e.mz, np.yaw);
        const n = rotXZ(e.nx, e.nz, np.yaw);
        const wx = np.position.x + m.x - n.x * inset;
        const wz = np.position.z + m.z - n.z * inset;
        for (const q of this.index.queryCircle(wx, wz, 0.6)) {
          if (!isWallSlot(q.defId) || q.base === undefined) continue;
          if (Math.abs(q.base - top) > 0.2) continue;
          const dd = (q.position.x - wx) ** 2 + (q.position.z - wz) ** 2;
          if (dd < 0.25) adopt(q);
        }
      }
      // ... the pillars standing on its corners ...
      for (const c of (np.family === 'square' ? SQUARE_CORNERS : TRI_CORNERS)) {
        const r = rotXZ(c.x, c.z, np.yaw);
        const px = np.position.x + r.x, pz = np.position.z + r.z;
        for (const q of this.index.queryCircle(px, pz, 0.6)) {
          if (q.defId !== 'pillar' || q.base === undefined) continue;
          if (Math.abs(q.base - top) > 0.3) continue;
          const dd = (q.position.x - px) ** 2 + (q.position.z - pz) ** 2;
          if (dd < 0.25) adopt(q);
        }
      }
      // ... and, for a ceiling, the NEIGHBOUR ceilings flush against its edges (the edge-extend
      // hosts): a rebuilt floor tile re-carries the run that used to hang off it.
      if (isCeiling(np.defId)) {
        for (const q of this.index.queryCircle(np.position.x, np.position.z, S * 1.5)) {
          if (!isCeiling(q.defId) || q.top === undefined || q.id === np.id) continue;
          if (Math.abs(q.top - top) > 0.35) continue;
          if (footprintsShareEdge(np, q)) adopt(q);
        }
      }
    }
    if (linked) this.stability.recompute();
  }

  // Recompute already ran (register/remove call it). Any placed piece now at support <= 0 has lost its
  // footing: free its logical state IMMEDIATELY (slots, index, placed map, mirror arrays) so the spot
  // is instantly legal to rebuild on, hand its solid to the crumble field for the ~1s sink+fade, then
  // recompute and repeat so the collapse cascades up the run. Parasites/grounded pieces are excluded
  // by stability.collapsed(); the loop is bounded by the piece count so it always terminates.
  private runCollapseCascade(): void {
    for (let guard = this.placed.size; guard >= 0; guard--) {
      // Structural pieces that lost their footing PLUS parasites (door/cupboard/stairs) whose host is
      // gone — a parasite rides its host and falls WITH it (a door on a demolished doorway, a cupboard
      // on a crumbled floor), but never collapses on its own. Both are collected each pass.
      const doomed = [...this.stability.collapsed(), ...this.orphanedParasites()];
      if (doomed.length === 0) return;
      for (const p of doomed) this.removePiece(p, true);
      this.stability.recompute();
    }
  }

  // Placed parasites (door/cupboard/stairs) whose every host id is no longer in the placed map — their
  // support was pulled out from under them, so they go too. A parasite with no recorded host (placed
  // free on terrain) is left alone.
  private orphanedParasites(): PlacedPiece[] {
    const out: PlacedPiece[] = [];
    for (const p of this.placed.values()) {
      if (CATALOG[p.defId].support.kind !== 'parasite') continue;
      if (p.supports.length === 0) continue; // free-standing parasite, nothing to orphan from
      if (p.supports.every((id) => !this.placed.has(id))) out.push(p);
    }
    return out;
  }

  // Drop a placed piece from every logical store and (optionally) start its crumble. Used by the
  // collapse cascade; written so a future demolish can reuse it. The mirror-array writes are reversed
  // by the per-instance undo closure captured in place(), keeping colliders/openings/getFoundations
  // and the wall stack/ceiling-snap levels exactly in sync (no fragile transform re-matching).
  // Fired for every piece that leaves the world (demolish, decay, explosion, collapse cascade) --
  // main.ts uses it to orphan wall/ceiling-MOUNTED deployables so they drop instead of floating.
  onPieceRemoved: ((placedId: string) => void) | null = null;

  private removePiece(p: PlacedPiece, crumble: boolean): void {
    this.placed.delete(p.id);
    this.index.remove(p.id);
    this.ledger.free(p.occupiedSlots);
    this.roomsDirty = true; // rooms re-derive lazily; a gone wall/lid can unseal or merge rooms
    this.pieceTier.delete(p.id); // drop the refund record for the gone piece
    const undo = this.mirrorUndo.get(p.id);
    if (undo) { undo(); this.mirrorUndo.delete(p.id); }
    if (p === this.tintTarget) this.clearAimTint(); // never leave the tint material on a disposed piece
    this.glbInstances.remove(p.id); // instanced visuals leave their pools with the piece
    if (crumble) {
      // The fall-out animation must stay cheap regardless of the tier's GLB polycount: swap in a
      // procedural stand-in at the same transform/tier look and free the real (possibly GLB-heavy) mesh
      // immediately, instead of animating/re-materialing hundreds of thousands of vertices for ~1s.
      const tier = this.pieceTier.get(p.id) ?? 'wood';
      const stand = makeProceduralSolid(p.defId, tier, p.skinId);
      stand.position.copy(p.position);
      stand.rotation.y = p.yaw;
      this.group.remove(p.mesh);
      disposeSolid(p.mesh);
      this.crumble.start(stand, this.group);
    } else { this.group.remove(p.mesh); disposeSolid(p.mesh); }
    // A gone FOUNDATION takes everything deployed on it with it (chests/benches/fires can't float; owner
    // spec + Rust behaviour). main sweeps deployables in the footprint; the server sweeps its rows too.
    if (p.defId.includes('foundation') || p.defId.includes('ceiling')) this.onFoundationRemoved?.(p.position.x, p.position.y, p.position.z); // ceilings host deployables too (owner spec)
    this.onPieceRemoved?.(p.id);
  }
  onFoundationRemoved: ((x: number, y: number, z: number) => void) | null = null;

  // First person: toggle the door the camera is aimed at (not the nearest to the body). The ray
  // must point near a door's leaf and be within reach. dir is assumed normalized.
  toggleDoorLook(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number): boolean {
    const openRad = THREE.MathUtils.degToRad(BUILD.doorOpenDeg);
    let best: PlacedDoor | null = null;
    let bestPerp = 0.9; // pick the door the dot is most precisely on (smallest miss), through gaps too
    for (const d of this.doors) {
      const yaw = d.closedYaw + d.sign * openRad * d.t;
      const dir = rotXZ(1, 0, yaw), ext = d.mirror ? -1 : 1; // mirrored leaf (double-door right side) extends the other way
      const cx = d.hx + dir.x * ext * (BUILD.doorwayWidth / 2);
      const cy = d.hy + BUILD.doorwayHeight / 2;
      const cz = d.hz + dir.z * ext * (BUILD.doorwayWidth / 2);
      const t = (cx - ox) * dx + (cy - oy) * dy + (cz - oz) * dz; // distance along the ray to the leaf center
      if (t < 0 || t > 3.0) continue; // in front of us + aimed within reach (~interactRange), not across the room
      const perp = Math.hypot(ox + dx * t - cx, oy + dy * t - cy, oz + dz * t - cz);
      if (perp < bestPerp) { bestPerp = perp; best = d; }
    }
    this.lastDoorDenied = false;
    if (!best) return false;
    // Locked-door access (Rust semantics, matches the server's doorAccessOk gate):
    //   WOODEN lock -> the owner OR a member of a base claim covering the door.
    //   KEYPAD lock -> those, or anyone who has entered the code (a persistent grant).
    const lockKind = best.lock ?? best.pair?.lock;
    if (lockKind) {
      const code = best.code ?? best.pair?.code;
      const granted = (best.granted?.includes(LOCAL_PLAYER) ?? false) || (best.pair?.granted?.includes(LOCAL_PLAYER) ?? false);
      const claimOk = this.claimAccessCheck?.(best.hx, best.hz) ?? false;
      // Keypad with NO code set = unlocked (green light): anyone opens.
      const canOpen = best.owner === LOCAL_PLAYER || claimOk || (lockKind === 'keypad_lock' && (!code || granted));
      if (!canOpen) { this.lastDoorDenied = true; this.lastDeniedDoor = best; return false; }
    }
    best.open = !best.open;
    if (best.open) best.openedAt = nowSec(); // arm the auto-close timer (if flagged)
    if (best.pair) best.pair.open = best.open; // double door: both leaves swing together
    this.lastToggledOpen = best.open; this.lastToggledPos.set(best.hx, best.hy, best.hz);
    this.onDoorToggle(best.hx, best.hy, best.hz, best.open); // MP: converge the open/close on every client
    return true;
  }

  // --- door AUTO-CLOSE (owner QoL) ------------------------------------------------------------------
  // Flip auto-close on the door the ray is aimed at (build-access gated). Returns the new state, or null
  // if no door is aimed / not allowed. The flag is client-local + saved: any client holding it sends the
  // normal close op when the timer lapses, so the shut still converges for everyone.
  toggleAutocloseAimed(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number): boolean | null {
    const d = this.pickDoor(ox, oy, oz, dx, dy, dz);
    if (!d) return null;
    if (!(d.owner === LOCAL_PLAYER || (this.claimAccessCheck?.(d.hx, d.hz) ?? false))) return null;
    d.autoclose = !d.autoclose;
    if (d.pair) d.pair.autoclose = d.autoclose;
    return d.autoclose ?? false;
  }

  // Base-wide sweep: set auto-close on every door within `radius` of (cx,cz). Returns doors affected.
  setAutocloseAround(cx: number, cz: number, radius: number, on: boolean): number {
    let n = 0;
    for (const d of this.doors) {
      if (Math.hypot(d.hx - cx, d.hz - cz) > radius) continue;
      if ((d.autoclose ?? false) !== on) { d.autoclose = on; n++; }
    }
    return n;
  }

  // Is any door with auto-close ON inside the radius? (Drives the claim wheel's toggle direction.)
  anyAutocloseAround(cx: number, cz: number, radius: number): boolean {
    for (const d of this.doors) if (d.autoclose && Math.hypot(d.hx - cx, d.hz - cz) <= radius) return true;
    return false;
  }

  // Per-frame: swing auto-close doors shut ~5s after they opened. Locally-flagged doors only; the close
  // routes through the normal toggle op so it converges in MP.
  tickAutoclose(): void {
    const now = nowSec();
    for (const d of this.doors) {
      if (!d.open || !d.autoclose || d.mirror) continue; // pairs close via their primary leaf
      if (now - (d.openedAt ?? now) < 5) continue;
      d.open = false;
      if (d.pair) d.pair.open = false;
      this.onDoorToggle(d.hx, d.hy, d.hz, false);
    }
  }

  // The door leaf the ray is aimed at (same pick as toggleDoorLook, no toggle). Shared by auto-close.
  private pickDoor(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number): PlacedDoor | null {
    const openRad = THREE.MathUtils.degToRad(BUILD.doorOpenDeg);
    let best: PlacedDoor | null = null;
    let bestPerp = 0.9;
    for (const d of this.doors) {
      const yaw = d.closedYaw + d.sign * openRad * d.t;
      const dir = rotXZ(1, 0, yaw), ext = d.mirror ? -1 : 1;
      const cx = d.hx + dir.x * ext * (BUILD.doorwayWidth / 2);
      const cy = d.hy + BUILD.doorwayHeight / 2;
      const cz = d.hz + dir.z * ext * (BUILD.doorwayWidth / 2);
      const t = (cx - ox) * dx + (cy - oy) * dy + (cz - oz) * dz;
      if (t < 0 || t > 3.0) continue;
      const perp = Math.hypot(ox + dx * t - cx, oy + dy * t - cy, oz + dz * t - cz);
      if (perp < bestPerp) { bestPerp = perp; best = d; }
    }
    return best;
  }

  // Attach a lock to the door leaf the ray is aimed at (within ~3m): it parents to the swinging leaf so it swings
  // with the door, on BOTH faces, by the handle. Returns true if a door took the lock (consume the item). One per door.
  // Does the door that just denied us take a keypad code? (Drives main's code-pad prompt.)
  deniedWantsCode(): boolean {
    const d = this.lastDeniedDoor;
    return !!d && (d.lock === 'keypad_lock' || d.pair?.lock === 'keypad_lock') && !!(d.code ?? d.pair?.code);
  }

  // Try a keypad code on the door that denied us: a match grants PERSISTENT access (Rust-style,
  // survives until the lock is replaced) and swings the door open. Both leaves of a pair share it.
  tryDoorCode(code: string): boolean {
    const d = this.lastDeniedDoor;
    if (!d) return false;
    const want = d.code ?? d.pair?.code;
    if (!want || code !== want) return false;
    (d.granted ??= []).push(LOCAL_PLAYER);
    if (d.pair) (d.pair.granted ??= []).push(LOCAL_PLAYER);
    this.onDoorGrant(d.hx, d.hy, d.hz, LOCAL_PLAYER); // MP: persist the grant so the DO's gate + other clients agree
    d.open = true;
    if (d.pair) d.pair.open = d.open;
    this.onDoorToggle(d.hx, d.hy, d.hz, d.open); // the code-entry open relays like any other toggle
    this.lastDeniedDoor = null;
    return true;
  }

  // A remote player entered this keypad door's correct code (doorGrantAt): mirror the grant locally so this
  // client's gate agrees with the server's durable grant set.
  applyDoorGrantAt(x: number, y: number, z: number, user: string): void {
    const d = this.doorAtHinge(x, y, z);
    if (!d) return;
    if (!(d.granted ??= []).includes(user)) d.granted.push(user);
    if (d.pair) { if (!(d.pair.granted ??= []).includes(user)) d.pair.granted.push(user); }
  }

  lockAimedDoor(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, kind: 'wooden_lock' | 'keypad_lock', code?: string): boolean {
    const openRad = THREE.MathUtils.degToRad(BUILD.doorOpenDeg);
    let best = 1.5, target: PlacedDoor | null = null; // looser tolerance (was 0.9) so a door you're clearly facing catches
    for (const d of this.doors) {
      if (d.lock) continue; // already locked
      const yaw = d.closedYaw + d.sign * openRad * d.t;
      const dir = rotXZ(1, 0, yaw), ext = d.mirror ? -1 : 1;
      const cx = d.hx + dir.x * ext * (BUILD.doorwayWidth / 2), cy = d.hy + BUILD.doorwayHeight / 2, cz = d.hz + dir.z * ext * (BUILD.doorwayWidth / 2);
      const t = (cx - ox) * dx + (cy - oy) * dy + (cz - oz) * dz;
      if (t < 0 || t > 4.5) continue; // was 3.0
      const perp = Math.hypot(ox + dx * t - cx, oy + dy * t - cy, oz + dz * t - cz);
      if (perp < best) { best = perp; target = d; }
    }
    if (!target) return false;
    this.applyLock(target, kind, code);
    this.onDoorLock(target.hx, target.hy, target.hz, kind, code); // MP: the lock + owner + code project to everyone
    return true;
  }

  // PROXIMITY FALLBACK for lock placement: lock the nearest UNLOCKED door within range of the player. Fixes the
  // reported "can't put a wooden lock on any door" -- if the aim ray misses the leaf centre, standing next to the
  // door and clicking still works. main.ts tries the aimed pick first, then this.
  lockNearestDoor(px: number, pz: number, range: number, kind: 'wooden_lock' | 'keypad_lock', code?: string): boolean {
    let best = range * range, target: PlacedDoor | null = null;
    for (const d of this.doors) {
      if (d.lock) continue;
      const dx = d.hx - px, dz = d.hz - pz, dd = dx * dx + dz * dz;
      if (dd < best) { best = dd; target = d; }
    }
    if (!target) return false;
    this.applyLock(target, kind, code);
    this.onDoorLock(target.hx, target.hy, target.hz, kind, code); // MP: the lock + owner + code project to everyone
    return true;
  }

  private applyLock(target: PlacedDoor, kind: 'wooden_lock' | 'keypad_lock', code?: string): void {
    const w = BUILD.doorwayWidth * 0.99, h = BUILD.doorwayHeight * 0.99; // leaf dims (mirror buildDoorLeaf)
    makeLockPair(target.mesh, kind, w * 0.8, h * 0.5, 0.09); // by the handle, both faces
    target.lock = kind;
    if (code) { target.code = code; target.granted = [LOCAL_PLAYER]; } // keypad: the owner-set combo others can enter; owner auto-granted
    if (target.pair) { target.pair.lock = kind; if (code) { target.pair.code = code; target.pair.granted = [LOCAL_PLAYER]; } }
    if (kind === 'keypad_lock') this.setKeypadLight(target); // green (no code) / red (code) on placement
  }

  // Recolour the keypad status LED: RED when a code is set (locked), GREEN when it isn't (unlocked). Walks both leaves.
  private setKeypadLight(d: PlacedDoor): void {
    const hex = (d.code ?? d.pair?.code) ? 0xdd3322 : 0x33dd44;
    const paint = (leaf: PlacedDoor): void => leaf.mesh.traverse((o) => { const m = o as THREE.Mesh; if (m.userData?.keypadLight) (m.material as THREE.MeshBasicMaterial).color.setHex(hex); });
    paint(d);
    if (d.pair) paint(d.pair);
  }

  // The lock on the door the ray is aimed at (for the hold-E manage prompt/wheel): kind, do I own it, is a code set.
  aimedLockInfo(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number): { kind: 'wooden_lock' | 'keypad_lock'; mine: boolean; hasCode: boolean } | null {
    const d = this.pickAimedDoor(ox, oy, oz, dx, dy, dz);
    const kind = d?.lock ?? d?.pair?.lock;
    if (!d || !kind) return null;
    return { kind, mine: d.owner === LOCAL_PLAYER, hasCode: !!(d.code ?? d.pair?.code) };
  }

  // OWNER: strip the lock off the aimed door (both leaves). Returns the lock item id to refund, or null.
  removeAimedLock(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number): 'wooden_lock' | 'keypad_lock' | null {
    const d = this.pickAimedDoor(ox, oy, oz, dx, dy, dz);
    const kind = d?.lock ?? d?.pair?.lock;
    if (!d || !kind || d.owner !== LOCAL_PLAYER) return null;
    const strip = (leaf: PlacedDoor): void => {
      for (const c of [...leaf.mesh.children]) if (c.userData?.lockPart) { leaf.mesh.remove(c); (c as THREE.Object3D).traverse((o) => { const m = o as THREE.Mesh; m.geometry?.dispose?.(); }); }
      leaf.lock = undefined; leaf.code = undefined; leaf.granted = undefined;
    };
    strip(d);
    if (d.pair) strip(d.pair);
    this.onDoorUnlock(d.hx, d.hy, d.hz); // MP: remove the lock everywhere
    return kind;
  }

  // OWNER: set/change the keypad code on the aimed door (resets who's been granted, so a new code re-locks it).
  setAimedDoorCode(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, code: string): boolean {
    const d = this.pickAimedDoor(ox, oy, oz, dx, dy, dz);
    if (!d || d.owner !== LOCAL_PLAYER || (d.lock !== 'keypad_lock' && d.pair?.lock !== 'keypad_lock')) return false;
    d.code = code; d.granted = [LOCAL_PLAYER];
    if (d.pair) { d.pair.code = code; d.pair.granted = [LOCAL_PLAYER]; }
    this.setKeypadLight(d);
    this.onDoorCode(d.hx, d.hy, d.hz, code); // MP: the code (and re-lock) project to everyone
    return true;
  }

  // OWNER: clear the keypad code -> unlocked (green light), anyone may open.
  clearAimedDoorCode(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number): boolean {
    const d = this.pickAimedDoor(ox, oy, oz, dx, dy, dz);
    if (!d || d.owner !== LOCAL_PLAYER) return false;
    d.code = undefined; d.granted = undefined;
    if (d.pair) { d.pair.code = undefined; d.pair.granted = undefined; }
    this.setKeypadLight(d);
    this.onDoorCode(d.hx, d.hy, d.hz, null); // MP: the clear projects to everyone
    return true;
  }

  // --- REMOTE APPLY (MP): address a door leaf by its HINGE (deterministic across clients from the same deployAt)
  // and apply another player's door op. No onDoor* callbacks fire here -- this is the receiving side. ---
  private doorAtHinge(x: number, y: number, z: number): PlacedDoor | null {
    let best: PlacedDoor | null = null, bestD = 0.2 * 0.2;
    for (const d of this.doors) {
      const dx = d.hx - x, dy = d.hy - y, dz = d.hz - z, dd = dx * dx + dy * dy + dz * dz;
      if (dd < bestD) { bestD = dd; best = d; }
    }
    return best;
  }

  applyDoorToggleAt(x: number, y: number, z: number, open: boolean): void {
    const d = this.doorAtHinge(x, y, z);
    if (!d) return;
    d.open = open;
    if (open) d.openedAt = nowSec(); // a remote open still arms this client's auto-close timer
    if (d.pair) d.pair.open = open; // both leaves of a double door swing together
  }

  applyDoorLockAt(x: number, y: number, z: number, kind: 'wooden_lock' | 'keypad_lock', owner: string, code?: string): void {
    const d = this.doorAtHinge(x, y, z);
    if (!d || d.lock) return; // idempotent: already locked here (replay / dupe)
    const w = BUILD.doorwayWidth * 0.99, h = BUILD.doorwayHeight * 0.99;
    makeLockPair(d.mesh, kind, w * 0.8, h * 0.5, 0.09);
    d.lock = kind; d.owner = owner; // lock owner = the actor who placed it (gates access on every client)
    if (code) { d.code = code; d.granted = [owner]; }
    if (d.pair) { d.pair.lock = kind; d.pair.owner = owner; if (code) { d.pair.code = code; d.pair.granted = [owner]; } }
    if (kind === 'keypad_lock') this.setKeypadLight(d);
  }

  applyDoorUnlockAt(x: number, y: number, z: number): void {
    const d = this.doorAtHinge(x, y, z);
    if (!d) return;
    const strip = (leaf: PlacedDoor): void => {
      for (const c of [...leaf.mesh.children]) if (c.userData?.lockPart) { leaf.mesh.remove(c); (c as THREE.Object3D).traverse((o) => { const m = o as THREE.Mesh; m.geometry?.dispose?.(); }); }
      leaf.lock = undefined; leaf.code = undefined; leaf.granted = undefined;
    };
    strip(d);
    if (d.pair) strip(d.pair);
  }

  applyDoorCodeAt(x: number, y: number, z: number, code: string | null): void {
    const d = this.doorAtHinge(x, y, z);
    if (!d) return;
    if (code) { d.code = code; d.granted = [d.owner]; if (d.pair) { d.pair.code = code; d.pair.granted = [d.owner]; } }
    else { d.code = undefined; d.granted = undefined; if (d.pair) { d.pair.code = undefined; d.pair.granted = undefined; } }
    this.setKeypadLight(d);
  }

  // The resolved key of a window opening: centre XZ + the wall's base band (the wallKey pattern),
  // so stacked window frames on different storeys stay independent.
  private windowSocketKey(cx: number, cz: number, base: number): string {
    return `${keyOf(cx, cz)}@${Math.round(base * 2)}`;
  }

  // A window deployable just filled the socket whose opening centre is (x,y,z): record it so
  // nearestWindowSocket skips it from now on. y is the opening-centre height nearestWindowSocket
  // returns, so the wall base recovers exactly.
  markWindowOccupied(x: number, y: number, z: number): void {
    this.windowOccupied.add(this.windowSocketKey(x, z, y - BUILD.windowSill - BUILD.windowHeight / 2));
  }

  // A mounted window was pulled back off its socket (hammer removal): free it so a new window fits.
  unmarkWindowOccupied(x: number, y: number, z: number): void {
    this.windowOccupied.delete(this.windowSocketKey(x, z, y - BUILD.windowSill - BUILD.windowHeight / 2));
  }

  // PLAYER-only colliders across FILLED window openings: a thin slab in the opening so you can't walk or jump
  // through a mounted window, flagged noBullet so bullets + line-of-sight still pass through the gaps. Empty
  // (un-filled) sockets stay open. Streamed off the wall list each frame from collectWorldBoxes.
  collectWindowColliders(out: Box[], pool: BoxPool): void {
    for (const w of this.walls) {
      if (w.id !== 'window') continue;
      if (!this.windowOccupied.has(this.windowSocketKey(w.cx, w.cz, w.base))) continue; // only a FILLED window blocks
      const cy = w.base + BUILD.windowSill + BUILD.windowHeight / 2;
      const b = pool.take(w.cx, cy, w.cz, S / 2 - 0.04, BUILD.windowHeight / 2, 0.12, w.yaw); // spans the opening band, thin
      b.noBullet = true;
      out.push(b);
    }
  }

  // The wall WINDOW opening to snap a window deployable into -> its opening centre + the wall yaw.
  // Skips sockets already holding a window. With an aim `ray` the pick is by perpendicular distance
  // to the ray (the toggleDoorLook analytic pick), so you fill the frame you are LOOKING at, not
  // whichever is nearest your feet; without one it falls back to nearest-to-player. Null if none.
  nearestWindowSocket(px: number, pz: number, range: number, ray?: { origin: THREE.Vector3; direction: THREE.Vector3 }): { x: number; y: number; z: number; yaw: number } | null {
    let best: PlacedWall | null = null, bestScore = ray ? 1.6 : range * range; // ray pick: within ~1.6m of the aim line
    const rangeSq = range * range;
    for (const w of this.walls) {
      if (w.id !== 'window') continue;
      if (this.windowOccupied.has(this.windowSocketKey(w.cx, w.cz, w.base))) continue;
      const dx = w.cx - px, dz = w.cz - pz, sq = dx * dx + dz * dz;
      if (sq > rangeSq) continue; // always range-gated by the player, ray or not
      let score = sq;
      if (ray) {
        const cy = w.base + BUILD.windowSill + BUILD.windowHeight / 2;
        const t = (w.cx - ray.origin.x) * ray.direction.x + (cy - ray.origin.y) * ray.direction.y + (w.cz - ray.origin.z) * ray.direction.z;
        if (t < 0) continue; // behind the camera
        score = Math.hypot(ray.origin.x + ray.direction.x * t - w.cx, ray.origin.y + ray.direction.y * t - cy, ray.origin.z + ray.direction.z * t - w.cz);
      }
      if (score < bestScore) { bestScore = score; best = w; }
    }
    if (!best) return null;
    return { x: best.cx, y: best.base + BUILD.windowSill + BUILD.windowHeight / 2, z: best.cz, yaw: best.yaw };
  }

  // The one door_opening slot of a doorway, keyed by the WALL CENTRE + base band. Every writer and
  // reader (build-menu door ghost/commit, crafted door placement, the occupied probe) shares this
  // resolver, so single and double doorways stay exclusive through the one ledger.
  private doorSlot(cx: number, cz: number, base: number): ResolvedSlot {
    return { kind: 'door_opening', worldKey: `${keyOf(cx, cz)}@${Math.round(base * 2)}#door`, exclusiveWith: ['door_opening'] };
  }

  // The nearest wall DOORWAY opening (single or double) within range -> opening centre + yaw + which kind +
  // whether a door leaf is already in it (via the door_opening ledger). Crafted doors snap to this. `want`
  // filters the scan to one kind, so holding a double door finds the double doorway even when a single
  // one is closer.
  // With `ray` (the camera aim), the socket CLOSEST TO THE AIM LINE wins among those in player range --
  // so the doorway you are LOOKING AT gets the door, never a nearer one behind you (owner 2026-07-12:
  // door placement "fights you"). Without a ray it falls back to plain player proximity.
  nearestDoorwaySocket(px: number, pz: number, range: number, want?: 'single' | 'double', ray?: THREE.Ray): { x: number; y: number; z: number; yaw: number; double: boolean; occupied: boolean } | null {
    let best: PlacedWall | null = null, bestScore = ray ? 3.2 * 3.2 : range * range; // aim pick: within ~3.2m of the aim line
    const mid = new THREE.Vector3();
    for (const w of this.walls) {
      if (w.id !== 'doorway' && w.id !== 'double_doorway') continue;
      if (want && (w.id === 'double_doorway') !== (want === 'double')) continue;
      const dx = w.cx - px, dz = w.cz - pz, sq = dx * dx + dz * dz;
      if (sq > range * range) continue; // must still be in the player's reach
      const score = ray ? ray.distanceSqToPoint(mid.set(w.cx, w.base + BUILD.doorwayHeight / 2, w.cz)) : sq;
      if (score <= bestScore) { bestScore = score; best = w; }
    }
    if (!best) return null;
    const occupied = this.ledger.conflicts([this.doorSlot(best.cx, best.cz, best.base)]);
    return { x: best.cx, y: best.base, z: best.cz, yaw: best.yaw, double: best.id === 'double_doorway', occupied };
  }

  // Place a CRAFTED door into a doorway socket as a swinging leaf (reuses tickDoors/toggle/lock). A single door
  // is one leaf hinged at the left edge; a double door is two leaves hinged at each edge swinging apart. `sign`
  // flips the swing direction. The unit is a REAL placed piece: it reserves the socket's door_opening slot (no
  // stacking a second door in), parents to the host doorway (the leaves crumble with it instead of floating),
  // and carries HP at its material tier so raids chip it. Returns false when the socket is already taken.
  // `owner` = who placed the door (a locked door opens for its owner + their claim members + keypad-granted).
  // Defaults to LOCAL_PLAYER for a local placement; the network replay passes the true actor so a remote-placed
  // locked door is owned by its real placer on every client, which is what makes the claim-member gate work.
  placeCraftedDoor(x: number, y: number, z: number, yaw: number, kind: CraftedDoorId, sign: number, owner: string = LOCAL_PLAYER): boolean {
    const slot = this.doorSlot(x, z, y);
    if (this.ledger.conflicts([slot])) return false; // socket already holds a door (authority re-check)
    // Furniture in the would-be swing arc blocks hanging the leaf (the reverse of the deploy gate).
    if (this.doorSwingBlocked(x, y, z, yaw, kind === 'metal_double_door', sign)) return false;
    const dir = rotXZ(1, 0, yaw); // the wall's local +x in world (the leaf-extend axis)
    // One parent group at the socket centre carries both leaves, so a crumble takes the pair as one
    // unit while tickDoors still swings each leaf group independently.
    const unit = new THREE.Group();
    unit.position.set(x, y, z);
    this.group.add(unit);
    const leaves: PlacedDoor[] = [];
    const addLeaf = (hingeOffset: number, mirror: boolean, s: number): PlacedDoor => {
      const g = new THREE.Group();
      buildCraftedDoorLeaf(g, kind, mirror);
      const hx = x + dir.x * hingeOffset, hz = z + dir.z * hingeOffset;
      g.position.set(dir.x * hingeOffset, 0, dir.z * hingeOffset); // local: the unit sits at the socket centre
      g.rotation.y = yaw;
      unit.add(g);
      const entry: PlacedDoor = { mesh: g, hx, hy: y, hz, closedYaw: yaw, sign: s, open: false, t: 0, owner, mirror };
      this.doors.push(entry);
      leaves.push(entry);
      return entry;
    };
    if (kind === 'metal_double_door') {
      const left = addLeaf(-BUILD.doorwayWidth, false, sign);  // left leaf: hinge at left edge, extends to centre
      const right = addLeaf(BUILD.doorwayWidth, true, -sign);  // right leaf: hinge at right edge, mirrored, swings the other way
      left.pair = right; right.pair = left;                    // the two leaves act as ONE unit (open/lock/flip together)
    } else {
      addLeaf(-BUILD.doorwayWidth / 2, false, sign); // single: hinge at the opening's left edge (mirrors updateDoor)
    }
    // Register the unit as a placed parasite 'door' piece -- same seeding pattern as commit().
    const id = `p${this.nextId++}`;
    const host = this.doorHostAt(x, z);
    const tier: BuildTier = kind === 'wooden_door' ? 'wood' : kind === 'reinforced_door' ? 'reinforced' : 'metal';
    // Doors carry their OWN Rust-matched HP pool (BUILD.doorHp, owner 2026-07-12), not the wall tier's:
    // wooden 200, metal/double 250, reinforced 1000 -- so 1 C4 opens wood/metal doors, 2 open reinforced.
    const hp = BUILD.doorHp[kind] ?? BUILD.hp[tier] ?? 250;
    const piece: PlacedPiece = {
      id,
      defId: 'door',
      position: new THREE.Vector3(x, y, z),
      quaternion: new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), yaw),
      yaw,
      family: 'none',
      occupiedSlots: [slot],
      supportValue: 0,
      supports: host ? [host.id] : [],
      mesh: unit,
      hp, maxHp: hp, lastDamagedAt: -1e9,
      placedAt: nowSec(), placedBy: LOCAL_PLAYER,
      doorKind: kind, // the crafted item this door pickups back into (hammer remove of an UNLOCKED door)
    };
    unit.userData.placedId = id;
    unit.updateMatrixWorld(true);
    this.placed.set(id, piece);
    this.index.insert(piece);
    this.ledger.reserve(piece.occupiedSlots);
    this.stability.register(piece);
    this.pieceTier.set(id, tier); // raid damage reads the leaf material's eco multipliers (no refund: demolish skips doors)
    this.mirrorUndo.set(id, () => { for (const l of leaves) spliceItem(this.doors, l); });
    return true;
  }

  // GHOST preview for a crafted door while it's held: the ACTUAL leaf geometry, fitted to the doorway socket and
  // shown swung partway open in the `sign` direction (R flips it). Mirrors placeCraftedDoor exactly so what you see
  // is what you place. Hidden when not aiming a door at a doorway.
  showDoorGhost(x: number, y: number, z: number, yaw: number, kind: CraftedDoorId, sign: number): void {
    if (!this.doorGhost) { this.doorGhost = new THREE.Group(); this.group.add(this.doorGhost); }
    this.doorGhost.visible = true;
    const key = `${x.toFixed(2)},${z.toFixed(2)},${yaw.toFixed(2)},${kind},${sign}`;
    if (key === this.doorGhostKey) return; // unchanged -> reuse the built ghost (no per-frame geometry churn)
    this.doorGhostKey = key;
    this.doorGhost.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) m.geometry.dispose(); }); // free the old leaf geometry
    this.doorGhost.clear();
    const dir = rotXZ(1, 0, yaw);
    const ajar = THREE.MathUtils.degToRad(BUILD.doorOpenDeg) * 0.6; // swung partway -> the preview SHOWS the opening direction
    const addGhostLeaf = (off: number, mirror: boolean, s: number): void => {
      // BOUNDS leaf (owner 2026-07-12): the ghost is the leaf's box + edges, hinged and swung ajar so
      // the opening direction still reads -- not the crafted leaf geometry re-tinted.
      const w = BUILD.doorwayWidth * 1.02, h = BUILD.doorwayHeight * 1.02;
      const lg = new THREE.Group();
      const bb = new THREE.Box3(new THREE.Vector3(mirror ? -w : 0, 0, -0.03), new THREE.Vector3(mirror ? 0 : w, h, 0.03));
      lg.add(boundsShape(bb, false, GHOST_MAT, GHOST_EDGE_MAT));
      lg.position.set(x + dir.x * off, y, z + dir.z * off);
      lg.rotation.y = yaw + s * ajar;
      this.doorGhost!.add(lg);
    };
    if (kind === 'metal_double_door') { addGhostLeaf(-BUILD.doorwayWidth, false, sign); addGhostLeaf(BUILD.doorwayWidth, true, -sign); }
    else addGhostLeaf(-BUILD.doorwayWidth / 2, false, sign);
  }
  hideDoorGhost(): void { if (this.doorGhost) this.doorGhost.visible = false; }

  // The world centre of the door leaf the ray (ox,oz)+(dx,dz) is aimed at within ~3m, into `out`; false if none.
  // Same pick as toggleDoorLook but read-only -- used to float an "Open Door" hover label without toggling it.
  aimedDoorCenter(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, out: THREE.Vector3): boolean {
    const openRad = THREE.MathUtils.degToRad(BUILD.doorOpenDeg);
    let best = 0.9, found = false;
    for (const d of this.doors) {
      const yaw = d.closedYaw + d.sign * openRad * d.t;
      const dir = rotXZ(1, 0, yaw), ext = d.mirror ? -1 : 1;
      const cx = d.hx + dir.x * ext * (BUILD.doorwayWidth / 2), cy = d.hy + BUILD.doorwayHeight / 2, cz = d.hz + dir.z * ext * (BUILD.doorwayWidth / 2);
      const t = (cx - ox) * dx + (cy - oy) * dy + (cz - oz) * dz;
      if (t < 0 || t > 3.0) continue;
      const perp = Math.hypot(ox + dx * t - cx, oy + dy * t - cy, oz + dz * t - cz);
      if (perp < best) { best = perp; out.set(cx, cy, cz); found = true; }
    }
    return found;
  }

  // The door leaf the ray is aimed at (within ~3m): its mesh (for the selection outline), or null.
  private pickAimedDoor(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number): PlacedDoor | null {
    const openRad = THREE.MathUtils.degToRad(BUILD.doorOpenDeg);
    let best = 0.9, found: PlacedDoor | null = null;
    for (const d of this.doors) {
      const yaw = d.closedYaw + d.sign * openRad * d.t;
      const dir = rotXZ(1, 0, yaw), ext = d.mirror ? -1 : 1;
      const cx = d.hx + dir.x * ext * (BUILD.doorwayWidth / 2), cy = d.hy + BUILD.doorwayHeight / 2, cz = d.hz + dir.z * ext * (BUILD.doorwayWidth / 2);
      const t = (cx - ox) * dx + (cy - oy) * dy + (cz - oz) * dz;
      if (t < 0 || t > 3.0) continue;
      const perp = Math.hypot(ox + dx * t - cx, oy + dy * t - cy, oz + dz * t - cz);
      if (perp < best) { best = perp; found = d; }
    }
    return found;
  }
  aimedDoorMesh(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number): THREE.Object3D | null {
    return this.pickAimedDoor(ox, oy, oz, dx, dy, dz)?.mesh ?? null;
  }
  // Flip the swing direction of the door the ray is aimed at (R on a PLACED door). Returns true if one flipped.
  flipAimedDoor(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number): boolean {
    const d = this.pickAimedDoor(ox, oy, oz, dx, dy, dz);
    if (!d) return false;
    d.sign *= -1; // swing the other way (instantly retargets the eased swing + the live collider)
    if (d.pair) d.pair.sign *= -1; // double door: flip both leaves together
    return true;
  }

  // READ-ONLY: the world centre of the door you're RIGHT against (within 1.4m), into `out`; false if none. Mirrors
  // toggleDoorNear's pick so the unified interaction registry can resolve a near door (for the label) without toggling.
  nearDoorCenter(p: THREE.Vector3, out: THREE.Vector3): boolean {
    let best: PlacedDoor | null = null, bestD = 1.4 * 1.4;
    for (const d of this.doors) { const dx = d.hx - p.x, dz = d.hz - p.z, dd = dx * dx + dz * dz; if (dd < bestD) { bestD = dd; best = d; } }
    if (!best) return false;
    const yaw = best.closedYaw + best.sign * THREE.MathUtils.degToRad(BUILD.doorOpenDeg) * best.t;
    const dir = rotXZ(1, 0, yaw), ext = best.mirror ? -1 : 1;
    out.set(best.hx + dir.x * ext * (BUILD.doorwayWidth / 2), best.hy + BUILD.doorwayHeight / 2, best.hz + dir.z * ext * (BUILD.doorwayWidth / 2));
    return true;
  }

  // Swing the door you're RIGHT against open or closed (no aim). Tight radius so it doesn't hijack E across a room.
  toggleDoorNear(p: THREE.Vector3): boolean {
    let best: PlacedDoor | null = null;
    let bestD = 1.4 * 1.4;
    for (const d of this.doors) {
      const dx = d.hx - p.x, dz = d.hz - p.z;
      const dd = dx * dx + dz * dz;
      if (dd < bestD) { bestD = dd; best = d; }
    }
    this.lastDoorDenied = false;
    if (!best) return false;
    // Locked-door access (Rust semantics, matches the server's doorAccessOk gate):
    //   WOODEN lock -> the owner OR a member of a base claim covering the door.
    //   KEYPAD lock -> those, or anyone who has entered the code (a persistent grant).
    const lockKind = best.lock ?? best.pair?.lock;
    if (lockKind) {
      const code = best.code ?? best.pair?.code;
      const granted = (best.granted?.includes(LOCAL_PLAYER) ?? false) || (best.pair?.granted?.includes(LOCAL_PLAYER) ?? false);
      const claimOk = this.claimAccessCheck?.(best.hx, best.hz) ?? false;
      // Keypad with NO code set = unlocked (green light): anyone opens.
      const canOpen = best.owner === LOCAL_PLAYER || claimOk || (lockKind === 'keypad_lock' && (!code || granted));
      if (!canOpen) { this.lastDoorDenied = true; this.lastDeniedDoor = best; return false; }
    }
    best.open = !best.open;
    if (best.open) best.openedAt = nowSec(); // arm the auto-close timer (if flagged)
    if (best.pair) best.pair.open = best.open; // double door: both leaves swing together
    this.lastToggledOpen = best.open; this.lastToggledPos.set(best.hx, best.hy, best.hz);
    this.onDoorToggle(best.hx, best.hy, best.hz, best.open); // MP: converge the open/close on every client
    return true;
  }

  // Ease door leaves toward their open/closed pose, and advance any active crumbles. Called each
  // frame (the crumble field is zero-cost when idle).
  tickDoors(dt: number): void {
    const open = THREE.MathUtils.degToRad(BUILD.doorOpenDeg);
    for (const d of this.doors) {
      const target = d.open ? 1 : 0;
      d.t += (target - d.t) * Math.min(1, dt * 9);
      d.mesh.rotation.y = d.closedYaw + d.sign * open * d.t;
    }
    if (this.crumble.busy) this.crumble.tick(dt);
  }

  // Oriented-box colliders for every placed piece, appended to `out` as pooled Boxes (no allocation;
  // rebuilt every frame for collision + the bullet hit-test).
  collectColliders(out: Box[], pool: BoxPool): void {
    for (const f of this.foundations) {
      const half = f.type === 'square' ? S / 2 : TRI_R * 0.55;
      out.push(pool.take(f.x, (f.top + f.base) / 2, f.z, half, (f.top - f.base) / 2, half, f.yaw));
    }
    for (const w of this.walls) {
      if (w.id === 'doorway' || w.id === 'double_doorway') {
        // Two side posts so the central opening stays walk-through (double doorway = a wider opening).
        const openW = w.id === 'double_doorway' ? BUILD.doorwayWidth * 2 : BUILD.doorwayWidth;
        const t = rotXZ(1, 0, w.yaw);
        const off = (S + openW) / 4;
        const hxPost = (S - openW) / 4;
        for (const sgn of [-1, 1]) {
          out.push(pool.take(w.cx + t.x * off * sgn, w.cy, w.cz + t.z * off * sgn, hxPost, w.h / 2, BUILD.wallThick / 2, w.yaw));
        }
      } else if (w.id === 'window') {
        // A window is solid EXCEPT its opening: two side posts, a low sill block, and a lintel above
        // the hole — so the gap (windowSill .. windowSill+windowHeight) is passable and a crouch-jump
        // vaults through it. Heights are measured up from the wall base.
        const t = rotXZ(1, 0, w.yaw);
        const off = (S + BUILD.windowWidth) / 4;
        const hxPost = (S - BUILD.windowWidth) / 4;
        for (const sgn of [-1, 1]) {
          out.push(pool.take(w.cx + t.x * off * sgn, w.cy, w.cz + t.z * off * sgn, hxPost, w.h / 2, BUILD.wallThick / 2, w.yaw));
        }
        const openTop = w.base + BUILD.windowSill + BUILD.windowHeight;
        // Sill block: full width, base up to the (low) sill. Cleared by a crouch-jump.
        if (BUILD.windowSill > 1e-3) {
          out.push(pool.take(w.cx, w.base + BUILD.windowSill / 2, w.cz, S / 2, BUILD.windowSill / 2, BUILD.wallThick / 2, w.yaw));
        }
        // Lintel: full width, from the opening top to the wall top.
        const lintelH = w.base + w.h - openTop;
        if (lintelH > 1e-3) {
          out.push(pool.take(w.cx, (openTop + w.base + w.h) / 2, w.cz, S / 2, lintelH / 2, BUILD.wallThick / 2, w.yaw));
        }
      } else {
        out.push(pool.take(w.cx, w.cy, w.cz, S / 2, w.h / 2, BUILD.wallThick / 2, w.yaw));
      }
    }
    for (const p of this.pillars) {
      // One slim box at the flare width, so brushing a pillar feels like its visible bulk.
      out.push(pool.take(p.cx, p.cy, p.cz, PILLAR_FLARE, BUILD.wallHeight / 2, PILLAR_FLARE, 0));
    }
    for (const s of this.stairs) {
      // Ascending nested boxes: WIDTH inset a wall thickness on the sides, DEPTH runs the full footprint
      // (matches buildStairs) so the collider tracks the snug visual treads and the player climbs exactly
      // what they see -- including the top step that now reaches the landing.
      const z = rotXZ(0, 1, s.yaw);
      const widthSpan = S - 2 * BUILD.wallThick;
      const run = S / BUILD.stairSteps;
      const rise = BUILD.wallHeight / BUILD.stairSteps;
      for (let i = 0; i < BUILD.stairSteps; i++) {
        const off = -S / 2 + (i + 0.5) * run;
        const topH = (i + 1) * rise;
        out.push(pool.take(s.cx + z.x * off, s.cy + topH / 2, s.cz + z.z * off, widthSpan / 2, topH / 2, run / 2, s.yaw));
      }
    }
    // A door leaf is solid whether closed (filling the opening) or open (blocking its swung arc).
    // The collider tracks the leaf's live orientation, so an open door funnels movement instead of
    // leaving a free gap. This is what makes airlocks work: hold the inner door, fight in the gap.
    const doorOpen = THREE.MathUtils.degToRad(BUILD.doorOpenDeg);
    for (const d of this.doors) {
      const yaw = d.closedYaw + d.sign * doorOpen * d.t;
      const ext = d.mirror ? -1 : 1; // a mirrored leaf (double-door right side) extends the OTHER way from its hinge
      const dir = rotXZ(1, 0, yaw); // the leaf extends from its hinge along this axis
      const cx = d.hx + dir.x * ext * (BUILD.doorwayWidth / 2);
      const cz = d.hz + dir.z * ext * (BUILD.doorwayWidth / 2);
      out.push(pool.take(cx, d.hy + BUILD.doorwayHeight / 2, cz, BUILD.doorwayWidth / 2, BUILD.doorwayHeight / 2, BUILD.wallThick / 2, yaw));
    }
  }

  get foundationCount(): number {
    return this.foundations.length;
  }

  // Total placed pieces (foundations, walls, ceilings, ...). Useful for debug/verification.
  get placedCount(): number {
    return this.placed.size;
  }

  // Why the last ghost was valid/invalid (the stored PlacementResult.reason). Read by main.ts every
  // build frame to drive the on-screen placement hint, and by the build debug gizmos.
  get placementReason(): PlacementReason {
    return this.lastReason;
  }

  // Which rule body failed when placementReason === 'RULE_FAIL' (names the specific blocker).
  get placementRule(): string | null {
    return this.lastRule;
  }

  // Is the player standing under one of their ceilings (a roof over their head)? Drives the switch
  // to the "inside the base" interior view.
  isUnderRoof(x: number, z: number, feetY: number): boolean {
    for (const f of this.foundations) {
      if (!f.elevated) continue;
      if (f.top > feetY + 1.2 && f.top < feetY + 6 && pointInFootprint(f.type, x, z, f.x, f.z, f.yaw)) return true;
    }
    return false;
  }

  // Is the player inside an ENCLOSED room, not merely standing under a lone ceiling tile? A doorway
  // plus one ceiling with no surrounding walls must NOT count -- there is nothing to scout-protect,
  // so OLOS should stay off. Requires a roof overhead AND most of a head-height ray ring to hit a
  // wall within about a room's reach. Doorways and windows count as walls here (they are the
  // expected gaps in an enclosure); seeing out through them is handled by openings()/the LOS cones.
  isEnclosed(x: number, z: number, feetY: number): boolean {
    if (!this.isUnderRoof(x, z, feetY)) return false;
    const RAYS = 16;
    const reach = S * 3; // span a room's diagonal so far walls of a big room still count
    const hp = feetY + 1.4; // sample at head height, so a wall on this storey blocks but a floor does not
    let blocked = 0;
    for (let i = 0; i < RAYS; i++) {
      const a = (i / RAYS) * Math.PI * 2;
      if (this.wallAlongRay(x, z, Math.cos(a), Math.sin(a), reach, hp)) blocked++;
    }
    // Walled on essentially all sides. Doorways/windows are full segments here so they don't open a
    // gap; only a genuinely MISSING wall leaves rays escaping, which drops a 3-walled shell out.
    return blocked >= Math.ceil(RAYS * 0.8);
  }

  // --- EXACT rooms (build/roomGraph): the graph rebuilds lazily after commit/removePiece batches,
  // so per-frame queries (interior privacy) and deploy-time queries (size classes) stay cheap. ---
  private ensureRooms(): void {
    if (!this.roomsDirty) return;
    this.roomGraph.rebuild(this.placed.values());
    this.roomsDirty = false;
  }

  // The room at (x,z) for the storey at/under height y (feet or a floor-surface hit), or null.
  roomAt(x: number, z: number, y: number): Room | null {
    this.ensureRooms();
    return this.roomGraph.roomAt(x, z, y);
  }

  // Vertical clearance of that room (lid minus floor), Infinity under open sky / outside any room.
  // The deployable size classes gate on this: a locker needs a full-height room.
  clearanceAt(x: number, z: number, y: number): number {
    this.ensureRooms();
    return this.roomGraph.clearanceAt(x, z, y);
  }

  // Is the position inside a fully SEALED room? Drives the interior privacy view (occlusion.ts):
  // only a guaranteed enclosure may black out the surround, which isEnclosed could never promise.
  isSealedAt(x: number, z: number, y: number): boolean {
    this.ensureRooms();
    return this.roomGraph.isSealedAt(x, z, y);
  }

  // INCREMENTAL enclosure 0..1 (roof fraction x wall fraction of the room you stand in) -- the
  // graded interior dim: a 2x2 walled room with 1 of 4 ceilings dims ~25%, fully sealed dims fully.
  enclosureAt(x: number, z: number, y: number): number {
    this.ensureRooms();
    return this.roomGraph.enclosureAt(x, z, y);
  }

  // True if any wall spanning head height hp is crossed by the horizontal ray (ox,oz)+t*(dx,dz)
  // within reach. 2D ray-vs-segment against each wall's footprint line (length S along its yaw).
  private wallAlongRay(ox: number, oz: number, dx: number, dz: number, reach: number, hp: number): boolean {
    const perpX = -dz, perpZ = dx;
    for (const w of this.walls) {
      if (w.base > hp || w.base + w.h < hp) continue; // wall does not reach head height on this storey
      const u = rotXZ(1, 0, w.yaw);
      const ax = w.cx - u.x * (S / 2), az = w.cz - u.z * (S / 2);
      const ex = u.x * S, ez = u.z * S; // A -> B along the wall
      const denom = ex * perpX + ez * perpZ;
      if (Math.abs(denom) < 1e-9) continue; // ray parallel to the wall
      const v1x = ox - ax, v1z = oz - az;
      const t = (ex * v1z - ez * v1x) / denom; // distance along the (unit) ray to the wall line
      if (t < 0 || t > reach) continue;
      const s = (v1x * perpX + v1z * perpZ) / denom; // position along the wall segment
      if (s >= 0 && s <= 1) return true;
    }
    return false;
  }

  // True if a head-height wall stands between A (ax,ay,az) and B (bx,by,bz) -- a line-of-sight test through walls.
  // Used to stop a chest behind walls being opened from outside (anti-hack): a clear segment is required.
  // DEPLOY CLEARANCE (owner 2026-07-12: "placing stuff in buildings should snap around walls, not let
  // you clip"): slide a ground/floor item's footprint centre out of every wall it overlaps. The item is
  // approximated by its footprint's bounding circle; each wall is a rotated box (S/2 long, thick/2
  // deep). Returns the corrected XZ, or null when two passes can't clear every wall (jammed in a
  // corner too tight for the item -- placement should read invalid, not clip).
  slideClearOfWalls(x: number, z: number, y: number, itemR: number): { x: number; z: number } | null {
    let px = x, pz = z;
    for (let pass = 0; pass < 2; pass++) {
      let moved = false;
      for (const w of this.walls) {
        if (y > w.base + w.h + 0.2 || y < w.base - 2.2) continue; // different storey
        const dx = px - w.cx, dz = pz - w.cz;
        if (dx * dx + dz * dz > (S / 2 + itemR + 0.4) ** 2) continue;
        const c = Math.cos(w.yaw), s = Math.sin(w.yaw);
        const lx = c * dx - s * dz, lz = s * dx + c * dz; // wall-local frame
        const limX = S / 2 + itemR * 0.35, limZ = BUILD.wallThick / 2 + itemR;
        if (Math.abs(lx) >= limX || Math.abs(lz) >= limZ) continue;
        // Overlapping: push out along the wall normal (local z) to the near boundary.
        const outZ = (lz >= 0 ? 1 : -1) * limZ;
        const nx = s, nz = c; // local +z in world (inverse of the local transform above)
        px += (outZ - lz) * nx;
        pz += (outZ - lz) * nz;
        moved = true;
      }
      if (!moved) return { x: px, z: pz };
    }
    // Two passes still colliding = wedged between opposing walls: report unplaceable.
    for (const w of this.walls) {
      if (y > w.base + w.h + 0.2 || y < w.base - 2.2) continue;
      const dx = px - w.cx, dz = pz - w.cz;
      if (dx * dx + dz * dz > (S / 2 + itemR + 0.4) ** 2) continue;
      const c = Math.cos(w.yaw), s = Math.sin(w.yaw);
      const lx = c * dx - s * dz, lz = s * dx + c * dz;
      if (Math.abs(lx) < S / 2 + itemR * 0.35 && Math.abs(lz) < BUILD.wallThick / 2 + itemR) return null;
    }
    return { x: px, z: pz };
  }

  wallBlocks(ax: number, ay: number, az: number, bx: number, by: number, bz: number): boolean {
    const dx = bx - ax, dz = bz - az;
    const len = Math.hypot(dx, dz);
    if (len < 1e-6) return false;
    return this.wallAlongRay(ax, az, dx / len, dz / len, len, (ay + by) / 2); // sample at the segment's mid height
  }

  // Openings the player has line of sight through, for the interior LOS cones: every window (always
  // see-through) plus every doorway NOT shut by a closed door leaf. Returns world-XZ centres within
  // maxDist of (px,pz); a far opening still counts (its cone just renders narrow).
  openings(px: number, pz: number, maxDist: number): Array<{ x: number; z: number }> {
    const out: Array<{ x: number; z: number }> = [];
    const maxD2 = maxDist * maxDist;
    for (const w of this.walls) {
      if (w.id !== 'window' && w.id !== 'doorway' && w.id !== 'double_doorway') continue;
      const dx = w.cx - px, dz = w.cz - pz;
      if (dx * dx + dz * dz > maxD2) continue;
      if (w.id === 'doorway' || w.id === 'double_doorway') {
        // A closed leaf near this doorway blocks the view; an open (or absent) one leaves it clear.
        const shut = this.doors.some((d) => {
          const hx = d.hx - w.cx, hz = d.hz - w.cz;
          return hx * hx + hz * hz < 1.6 * 1.6 && d.t < 0.5;
        });
        if (shut) continue;
      }
      out.push({ x: w.cx, z: w.cz });
    }
    return out;
  }

  // Hide ceilings a clear storey above the player so the camera sees down into the room (belowY =
  // feet). Ceilings within jump-reach stay visible, so a roof you can crouch-jump onto from a
  // half-step is not culled out from under you. Pass null to restore every ceiling.
  setCutaway(belowY: number | null): void {
    for (const c of this.ceilingMeshes) {
      c.mesh.visible = belowY === null ? true : c.top <= belowY + 2.3;
    }
  }

  // Live ground-foundation footprints, for the grass field to cut holes under (elevated
  // ceilings sit above ground that is already cut, so they are skipped).
  getFoundations(): ReadonlyArray<{ type: 'square' | 'triangle'; x: number; z: number; yaw: number }> {
    return this.foundations; // cut grass under every footprint, including floor tiles / ceilings, so it never pokes through a floor
  }

  // Highest placed FOOTPRINT piece whose footprint covers (x,z), or undefined (the stairs base
  // scan). queryCircle-pruned: only pieces whose centre is within a footprint circumradius of the
  // point can cover it, so S is a safe pad (max circumradius is S/sqrt(2) for a square).
  private footprintTopAt(x: number, z: number): PlacedPiece | undefined {
    let best: PlacedPiece | undefined;
    for (const p of this.index.queryCircle(x, z, S)) {
      if (p.family === 'none' || p.top === undefined) continue;
      if (pointInFootprint(p.family as FootprintShape, x, z, p.position.x, p.position.z, p.yaw) && (!best || p.top > (best.top ?? -Infinity))) best = p;
    }
    return best;
  }

  // The floor to deploy stairs onto: the footprint covering (x,z) whose top is nearest the player's
  // feet (at or just below them), so the stairs land on the storey you are standing on instead of on
  // the roof above you. Falls back to the highest floor if none is near.
  private floorNear(x: number, z: number): PlacedPiece | undefined {
    let best: PlacedPiece | undefined;
    let bestD = Infinity;
    for (const p of this.index.queryCircle(x, z, S)) {
      if (p.family === 'none' || p.top === undefined) continue;
      if (!pointInFootprint(p.family as FootprintShape, x, z, p.position.x, p.position.z, p.yaw)) continue;
      if (p.top > this.playerY + 1.0) continue; // above the player's head: not this storey
      const d = Math.abs(p.top - this.playerY);
      if (d < bestD) { bestD = d; best = p; }
    }
    return best ?? this.footprintTopAt(x, z);
  }

  // --- foundation placement ---
  // The floor (walkable top) sits foundLift ABOVE the highest terrain corner under the footprint
  // (so it never z-fights the ground), and the block plants foundDepth straight down as legs.
  // R toggles the floor: free -> ground level vs one half-wall up; snapped -> even with the
  // neighbor vs a half-wall up/down (direction auto-picked from whether the local ground is below
  // or above the neighbor). Only clean half-wall offsets, never little incremental steps. A spot is
  // invalid if it overlaps, if the legs cannot reach the ground (too steep), or if rising ground
  // would bury the floor by more than a half-wall.
  // (The §B math is run verbatim here; the validator's foundationTerrainFit / footprintNoOverlap are
  // the same body, used as the canonical rule so the reason code is precise.)
  private updateFoundation(cursor: THREE.Vector3): void {
    const type = footprintType(this.piece);
    const HALF = BUILD.wallHeight / 2;
    const LIFT = BUILD.foundLift;
    const DEPTH = BUILD.foundDepth;
    const candidates = this.foundationSockets(cursor);
    // AIM INTENT over snap-greed (owner ask): a socket only claims the ghost while the cursor is
    // actually near its RESULTING tile. Point clearly at open ground and you free-place there,
    // instead of the nearest base edge yanking the foundation sideways.
    const c0 = candidates.length > 0 ? candidates[mod(this.socketIndex, candidates.length)] : null;
    const c = c0 && Math.hypot(c0.x - cursor.x, c0.z - cursor.z) <= S * 0.95 ? c0 : null;
    for (const k of candidates) { if (this.lastSockets.length >= 48) break; this.lastSockets.push({ x: k.x, y: k.top + 0.1, z: k.z, active: k === c }); }
    let fx: number, fz: number;
    if (c) { this.snapped = true; this.gYaw = c.yaw; fx = c.x; fz = c.z; }
    else { this.snapped = false; this.gYaw = THREE.MathUtils.degToRad(snapDeg(this.yawDeg)); fx = cursor.x; fz = cursor.z; }

    const { maxC } = footprintTerrain(type, fx, fz, this.gYaw);
    const freeFloor = maxC + LIFT; // the natural ground-level floor here
    let floor: number;
    if (c) {
      // AUTO-SNAP: pick whichever level best matches the terrain under the cursor -- flush with the
      // neighbour on even ground, or a half-wall up/down when the cursor sits on higher/lower terrain,
      // so a foundation run follows a slope by itself. R then nudges it a half-wall up on purpose.
      const even = c.top;
      const offset = freeFloor < c.top ? c.top - HALF : c.top + HALF;
      floor = Math.abs(freeFloor - even) <= Math.abs(freeFloor - offset) ? even : offset;
    } else {
      floor = freeFloor; // free placement sits on the ground
    }
    if (this.raisedFoundation) floor += HALF; // R: deliberately raise this foundation a half-wall

    this.ghostTop = floor;
    this.ghostBase = floor - DEPTH;
    this.gPos.set(fx, floor - DEPTH / 2, fz);
    // Validity = §B terrain fit (legs reach + not buried) AND no footprint overlap. Run the rule
    // bodies from the validator so a regression there shows up everywhere.
    const fits = foundationTerrainFit(type, fx, fz, this.gYaw, floor);
    const clear = footprintNoOverlap(type, fx, fz, this.gYaw, this.footprintPiecesNear(fx, fz));
    // The new foundation rests on terrain (grounded) or, when edge-extended, on its neighbor.
    this.pendingParents = c && c.hostId ? [c.hostId] : [];
    this.pendingSlots = this.cellSlots(type, fx, fz, floor);
    // Occupancy (no cell already filled at this band), then §B fits/overlap, then support. Grounded
    // foundations always seed full support; an edge-extended one inherits its neighbor minus a loss.
    if (this.ledger.conflicts(this.pendingSlots)) { this.ghostValid = false; this.lastReason = 'SLOT_OCCUPIED'; }
    else if (!fits || !clear) { this.ghostValid = false; this.lastReason = 'RULE_FAIL'; this.lastRule = !fits ? 'foundationTerrainFit' : 'footprintNoOverlap'; }
    else if (!this.supportOk()) { this.ghostValid = false; this.lastReason = 'NO_SUPPORT'; }
    else { this.ghostValid = true; this.lastReason = 'OK'; }
  }

  // --- wall placement: stand on a foundation top edge with no wall yet ---
  private updateWall(cursor: THREE.Vector3): void {
    const reach = S * BUILD.socketReach;
    const h = wallSlotHeight(this.piece);
    const cands: Array<{ x: number; z: number; nx: number; nz: number; yaw: number; base: number; key: string; dist: number; score: number; hostId?: string; shape: FootprintShape }> = [];
    // Candidate hosts come from the spatial index (footprints whose centre is within reach + a
    // footprint circumradius of the cursor), not a scan of every foundation on the island. The
    // placed piece IS the support parent, so the per-foundation pieceAt lookup is gone too.
    // With a live aim ray (3rd person especially), an edge also qualifies when the RAY passes close
    // to it -- the resolved ground point can land at your feet while the mouse sits on a far edge.
    for (const f of this.index.queryCircle(cursor.x, cursor.z, reach + S * 2)) {
      if (f.family === 'none' || f.top === undefined) continue;
      const ftop = f.top;
      for (const e of edgesFor(f.family as FootprintShape)) {
        const m = rotXZ(e.mx, e.mz, f.yaw);
        const n = rotXZ(e.nx, e.nz, f.yaw);
        const x = f.position.x + m.x;
        const z = f.position.z + m.z;
        // Walls stack per storey: the ghost sits on top of whatever is already on this edge at
        // this support's height (the foundation edge and the ceiling edge above it are separate).
        const key = wallKey(x, z, ftop);
        const base = ftop + (this.wallStacks.get(key) ?? 0);
        const dist = Math.hypot(x - cursor.x, z - cursor.z);
        // Score = how close the AIM is to this edge: perpendicular ray distance to the slot's
        // mid-height when a ray is live (what the mouse visually points at), else the point distance.
        const rayD = this.aimRayValid ? Math.sqrt(this.aimRay.distanceSqToPoint(WALL_AIM_PT.set(x, base + h / 2, z))) : Infinity;
        const score = Math.min(rayD, dist);
        if (dist > reach && !(this.aimRayValid && rayD < 1.6)) continue; // ray hover admits edges the point-gate would drop
        cands.push({ x, z, nx: n.x, nz: n.z, yaw: angle(n.x, n.z), base, key, dist, score, hostId: f.id, shape: f.family as FootprintShape });
      }
    }
    if (cands.length === 0) {
      this.snapped = false;
      this.ghostValid = false;
      this.lastReason = 'NO_COMPATIBLE_ANCHOR';
      this.gPos.set(cursor.x, BUILD.wallHeight / 2, cursor.z);
      this.gYaw = 0;
      this.pendingParents = [];
      this.pendingSlots = [];
      return;
    }
    // Stable order: aim score, then key -- equidistant candidates never swap frame-to-frame.
    cands.sort((a, b) => (a.score - b.score) || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
    this.snapped = true;
    // STICKY SELECTION with hysteresis: hold the previously aimed edge until the aim clearly
    // prefers another (best + ~1/3 tile), so the ghost stops jumping at 3- and 4-way junctions.
    // R/scroll still cycles: a socketIndex delta since last frame walks the ring from the held edge.
    const cycleDelta = this.socketIndex - this.wallCycleSeen;
    this.wallCycleSeen = this.socketIndex;
    const heldIdx = this.stickyWallKey === null ? -1 : cands.findIndex(k => k.key === this.stickyWallKey);
    let c: (typeof cands)[number];
    if (cycleDelta !== 0 && heldIdx >= 0) c = cands[mod(heldIdx + cycleDelta, cands.length)];
    else if (cycleDelta === 0 && heldIdx >= 0 && cands[heldIdx].score <= cands[0].score + S * 0.15) c = cands[heldIdx];
    else c = cands[mod(this.socketIndex === 0 ? 0 : this.socketIndex, cands.length)];
    this.stickyWallKey = c.key;
    for (const k of cands) { if (this.lastSockets.length >= 48) break; this.lastSockets.push({ x: k.x, y: k.base + 0.1, z: k.z, active: k === c }); }
    // Full height for wall/doorway/window (so doorways and windows are full walls with a punch-out,
    // sitting exactly like a wall); only half_wall is half -- `h` computed above for the aim scoring.
    const inset = BUILD.wallThick / 2;
    this.gPos.set(c.x - c.nx * inset, c.base + h / 2, c.z - c.nz * inset);
    this.gYaw = c.yaw + (this.wallFlip ? Math.PI : 0); // F spins the wall 180 so the chosen strong face points out
    this.pendingWallKey = c.key;
    // The wall's SOFT side normally faces the foundation interior (where the weak-side beams render): direct
    // fire from that side does BUILD.softSideMult damage once placed (Rust-style soft-side). F (wallFlip) SWAPS
    // it, so the player can turn the HARD side toward an expected raid direction -- the "rotate the wall" ask.
    this.pendingSoftDir = this.wallFlip ? { x: c.nx, z: c.nz } : { x: -c.nx, z: -c.nz };
    // Parent the wall to the wall DIRECTLY BELOW it in this stack (its top meets our base), so support
    // chains up the stack and pulling out a lower wall starves everything above it. Only the first wall
    // on an edge (nothing below) parents to the foundation/ceiling it stands on.
    const below = this.wallBelow(c.x, c.z, c.nx, c.nz, c.base);
    this.pendingParents = below ? [below] : (c.hostId ? [c.hostId] : []);
    // The wall reserves its edge slot at this support's height band: `key` carries the support top,
    // `base` disambiguates which storey of the stack. Square supports use square edge slots, triangle
    // supports triangle edge slots — kept separate (triangles are not half-squares).
    const kind = c.shape === 'square' ? 'edge_square' : 'edge_triangle';
    this.pendingSlots = [{ kind, worldKey: `${c.key}#edge@${Math.round(c.base * 2)}`, exclusiveWith: [kind] }];
    // Occupancy first (no two walls in the same edge band — stacking up lands in a fresh band), then
    // the support gate: a wall transmits up from its host edge (loss 0.02); reject a starved run.
    if (this.ledger.conflicts(this.pendingSlots)) { this.ghostValid = false; this.lastReason = 'SLOT_OCCUPIED'; }
    else if (this.supportOk()) { this.ghostValid = true; this.lastReason = 'OK'; }
    else { this.ghostValid = false; this.lastReason = 'NO_SUPPORT'; }
  }

  // --- ceiling: a thin floor slab that rests on a WALL/HALF-WALL top (real support) ---
  // A ceiling is load-bearing now: it must rest on a wall stack (or extend off an already-supported
  // ceiling), never directly on a bare foundation. It caps a support by sitting on the top of the
  // walls standing on it (a half-wall gives a halfway level); R steps it DOWN through those wall-top
  // levels so you can seal off a lower section. It can also extend edge-to-edge off an existing
  // CEILING (any family — a triangle can snap against a square) to grow a floor. Whichever support is
  // nearest the cursor wins. Never overlaps; rejected (NO_SUPPORT) when nothing real holds it up.
  private updateCeiling(cursor: THREE.Vector3): void {
    const type = footprintType(this.piece);
    const reach = S * BUILD.socketReach;
    let mode = '';
    let bx = 0, bz = 0, btop = 0, byaw = 0, bd = reach * reach;
    let parents: string[] = [];
    // Candidate hosts from the spatial index (footprints whose centre is within reach + a footprint
    // circumradius of the cursor) -- not every foundation on the island.
    const near = this.index.queryCircle(cursor.x, cursor.z, reach + S);
    // (1) Extend a floor: snap edge-to-edge off ANY existing CEILING (elevated floor) at its own
    // height, mixing families freely (a triangle ceiling lays flush against a square ceiling edge and
    // vice versa). NOT off a ground foundation — a ceiling never sits beside a foundation at ground
    // level. The adjacent ceiling carries the support across.
    for (const f of near) {
      if (!isCeiling(f.defId) || f.top === undefined) continue; // ceilings only, not ground foundations
      for (const e of edgesFor(f.family as FootprintShape)) {
        const m = rotXZ(e.mx, e.mz, f.yaw);
        const n = rotXZ(e.nx, e.nz, f.yaw);
        const sx = f.position.x + m.x, sz = f.position.z + m.z;
        const dd = (sx - cursor.x) ** 2 + (sz - cursor.z) ** 2 + ((f.top ?? 0) - cursor.y) ** 2; // #2: 3D so aiming high picks the upper floor
        if (dd >= bd) continue;
        const mt = mate(sx, sz, n.x, n.z, type); // guest's own family math; host family is independent
        bd = dd; bx = mt.x; bz = mt.z; byaw = mt.yaw; btop = f.top; mode = 'edge';
        parents = [f.id];
      }
    }
    // (1b) Mate off a WALL TOP EDGE, INSIDE or OUTSIDE (owner ask): any wall or half-wall top line
    // hosts a flush ceiling of EITHER family on both of its sides -- a triangle ceiling on a square
    // foundation's half wall, an awning off the outside of a full wall, a half-height shelf floor.
    // The wall itself is the support parent. Walls sit inset wallThick/2 from the true edge line, so
    // push back out along the wall normal before mating; the side the CURSOR is on wins.
    for (const w of near) {
      if (w.family !== 'none' || w.top === undefined || w.base === undefined || !isWallSlot(w.defId)) continue;
      const nx = Math.sin(w.yaw), nz = Math.cos(w.yaw); // wall outward normal (gYaw stored as angle(n))
      const ex = w.position.x + nx * (BUILD.wallThick / 2), ez = w.position.z + nz * (BUILD.wallThick / 2);
      for (const s of [1, -1]) {
        const mt = mate(ex, ez, s * nx, s * nz, type);
        const dd = (mt.x - cursor.x) ** 2 + (mt.z - cursor.z) ** 2 + ((w.top ?? 0) - cursor.y) ** 2; // #2: 3D score, so an UPPER wall top wins when you aim up
        if (dd >= bd) continue;
        bd = dd; bx = mt.x; bz = mt.z; byaw = mt.yaw; btop = w.top; mode = 'walltop';
        parents = [w.id];
      }
    }
    // (2) Cap a support by resting on a WALL stack on it. A foundation/ceiling with no walls on its
    // edges -- and no pillar on its corners -- offers no level, so it cannot host a ceiling directly:
    // that is the realism gate (no walls, no roof). The parents are the wall/pillar pieces whose
    // tops meet the chosen level, so the support graph reflects true wall-borne support.
    for (const f of near) {
      if (f.family !== type || f.top === undefined) continue; // capping keeps family aligned (the slab covers the cell)
      const levels = this.ceilingLevels(f);
      if (levels.length === 0) continue; // no wall stack or pillar under it: not a valid cap target
      // Level picked by CURSOR HEIGHT (the build cursor lands on real geometry now): aim at a
      // half-wall top and the half level is chosen, at a full wall top the full level.
      let lvl = levels[levels.length - 1];
      for (const l of levels) if (Math.abs(f.top + l - cursor.y) < Math.abs(f.top + lvl - cursor.y)) lvl = l;
      // #2: score with the CAP LEVEL's actual height, so aiming HIGH caps the UPPER storey's walls instead of
      // always the ground floor directly under the 2D cursor -- the "can't place ceilings above the 1st floor" fix.
      const dd = (f.position.x - cursor.x) ** 2 + (f.position.z - cursor.z) ** 2 + (f.top + lvl - cursor.y) ** 2;
      if (dd >= bd) continue;
      bd = dd; bx = f.position.x; bz = f.position.z; byaw = f.yaw; btop = f.top + lvl; mode = 'cap';
      parents = this.wallParentsAt(f, f.top + lvl);
    }
    if (!mode) {
      this.snapped = false;
      this.ghostValid = false;
      this.lastReason = 'NO_COMPATIBLE_ANCHOR';
      this.gPos.set(cursor.x, cursor.y + BUILD.wallHeight, cursor.z);
      this.gYaw = 0;
      this.pendingParents = [];
      this.pendingSlots = [];
      return;
    }
    this.snapped = true;
    this.ghostTop = btop;
    this.ghostBase = btop - BUILD.ceilingThick;
    this.gYaw = byaw;
    this.gPos.set(bx, btop - BUILD.ceilingThick / 2, bz);
    this.pendingParents = parents;
    const kind = type === 'square' ? 'top_square' : 'top_triangle';
    this.pendingSlots = [{ kind, worldKey: `${keyOf(bx, bz)}@${Math.round(btop * 2)}#top`, exclusiveWith: ['top_square', 'top_triangle'] }];
    // Gate order: occupancy (no ceiling already capping this cell at this band), then footprint
    // overlap, then the NO_SUPPORT gate — a ceiling with no wall/ceiling support (support<=0) is out.
    const clear = ceilingNoOverlap(type, bx, bz, byaw, btop, this.footprintPiecesNear(bx, bz));
    if (this.ledger.conflicts(this.pendingSlots)) { this.ghostValid = false; this.lastReason = 'SLOT_OCCUPIED'; }
    else if (!clear) { this.ghostValid = false; this.lastReason = 'RULE_FAIL'; this.lastRule = 'ceilingNoOverlap'; }
    else if (!this.supportOk()) { this.ghostValid = false; this.lastReason = 'NO_SUPPORT'; }
    else { this.ghostValid = true; this.lastReason = 'OK'; }
  }

  // Distinct ceiling snap heights above a support's top: the top of each WALL piece on its edges (a
  // half-wall yields a halfway level) plus the top of each PILLAR standing on its corners (a pillar
  // cap counts as a wall-top level). NO floor (0) and NO synthetic full-storey level when the
  // support is bare -- a ceiling must land on a real wall or pillar, so a bare edge offers nothing.
  private ceilingLevels(f: PlacedPiece): number[] {
    const tops = new Set<number>();
    const shape = f.family as FootprintShape;
    const ftop = f.top ?? 0;
    for (const e of edgesFor(shape)) {
      const m = rotXZ(e.mx, e.mz, f.yaw);
      for (const t of this.wallTops.get(wallKey(f.position.x + m.x, f.position.z + m.z, ftop)) ?? []) tops.add(t);
    }
    for (const c of (shape === 'square' ? SQUARE_CORNERS : TRI_CORNERS)) {
      const r = rotXZ(c.x, c.z, f.yaw);
      const px = f.position.x + r.x, pz = f.position.z + r.z;
      for (const p of this.index.queryCircle(px, pz, 0.6)) {
        if (p.defId !== 'pillar' || p.base === undefined || p.top === undefined) continue;
        if (Math.abs(p.base - ftop) > 0.3) continue; // stands on this storey of the footprint
        const dd = (p.position.x - px) ** 2 + (p.position.z - pz) ** 2;
        if (dd < 0.25) tops.add(p.top - ftop); // its cap is a valid ceiling level
      }
    }
    return [...tops].sort((a, b) => a - b);
  }

  // The placed WALL pieces on `f`'s perimeter edges -- and PILLARS on its corners -- whose top reaches
  // `level` (world Y), as a parent set for a ceiling capping them. A ceiling resting at a cap level
  // is held up by every wall/pillar on the ring that reaches that height, so demolishing the last of
  // them starves and crumbles the ceiling.
  private wallParentsAt(f: PlacedPiece, level: number): string[] {
    const ids = new Set<string>();
    const inset = BUILD.wallThick / 2;
    const shape = f.family as FootprintShape;
    for (const e of edgesFor(shape)) {
      const m = rotXZ(e.mx, e.mz, f.yaw);
      const n = rotXZ(e.nx, e.nz, f.yaw);
      const wx = f.position.x + m.x - n.x * inset;
      const wz = f.position.z + m.z - n.z * inset;
      for (const p of this.index.queryCircle(wx, wz, 0.6)) {
        if (p.family !== 'none' || p.top === undefined) continue;
        if (!isWallSlot(p.defId)) continue;
        if (Math.abs(p.top - level) > 0.35) continue; // this wall's top meets the ceiling band
        const dd = (p.position.x - wx) ** 2 + (p.position.z - wz) ** 2;
        if (dd < 0.25) ids.add(p.id); // a wall sitting on this edge
      }
    }
    for (const c of (shape === 'square' ? SQUARE_CORNERS : TRI_CORNERS)) {
      const r = rotXZ(c.x, c.z, f.yaw);
      const px = f.position.x + r.x, pz = f.position.z + r.z;
      for (const p of this.index.queryCircle(px, pz, 0.6)) {
        if (p.defId !== 'pillar' || p.top === undefined) continue;
        if (Math.abs(p.top - level) > 0.35) continue; // the pillar cap meets the ceiling band
        const dd = (p.position.x - px) ** 2 + (p.position.z - pz) ** 2;
        if (dd < 0.25) ids.add(p.id); // a pillar standing on this corner
      }
    }
    return [...ids];
  }

  // The placed WALL directly below a wall whose base sits at world Y `baseY` on the edge at midpoint
  // (ex,ez) with outward normal (nx,nz): the wall on that same edge column whose TOP meets the base.
  // Returns its id, or undefined when nothing is below (the wall is the first on the edge). Chains the
  // support graph up a wall stack, so removing a lower wall collapses everything above it.
  private wallBelow(ex: number, ez: number, nx: number, nz: number, baseY: number): string | undefined {
    const inset = BUILD.wallThick / 2;
    const wx = ex - nx * inset, wz = ez - nz * inset;
    for (const p of this.index.queryCircle(wx, wz, 0.6)) {
      if (p.family !== 'none' || p.top === undefined) continue;
      if (!isWallSlot(p.defId)) continue;
      if (Math.abs(p.top - baseY) > 0.2) continue; // its top is flush with our base
      const dd = (p.position.x - wx) ** 2 + (p.position.z - wz) ** 2;
      if (dd < 0.25) return p.id; // same edge column
    }
    return undefined;
  }

  // --- stairs: sit on a foundation, climb one storey toward their facing (R cycles the edges) ---
  // The yaw SNAPS to the host floor's edge normals (the same rotXZ math walls use), picking the edge
  // closest to the free-rotate yaw, and the stair reserves a stair_volume slot at its climb cell +
  // height band -- so a second staircase can no longer stack invisibly inside the first.
  private updateStairs(cursor: THREE.Vector3): void {
    const f = this.floorNear(cursor.x, cursor.z);
    if (!f || f.top === undefined) {
      this.snapped = false;
      this.ghostValid = false;
      this.lastReason = 'RULE_FAIL';
      this.lastRule = 'floorBelowPlayer';
      this.gPos.set(cursor.x, cursor.y, cursor.z);
      this.gYaw = THREE.MathUtils.degToRad(snapDeg(this.yawDeg));
      this.pendingParents = [];
      this.pendingSlots = [];
      return;
    }
    // Edge-normal yaw snap: candidates are the host footprint's outward edge normals in world space.
    // The one closest to the current free yaw wins; socketIndex (R / scroll while snapped) walks the
    // ring from there.
    const edges = edgesFor(f.family as FootprintShape);
    const freeYaw = THREE.MathUtils.degToRad(snapDeg(this.yawDeg));
    let closest = 0, bestErr = Infinity;
    for (let i = 0; i < edges.length; i++) {
      const n = rotXZ(edges[i].nx, edges[i].nz, f.yaw);
      const err = Math.abs(mod(angle(n.x, n.z) - freeYaw + Math.PI, Math.PI * 2) - Math.PI);
      if (err < bestErr) { bestErr = err; closest = i; }
    }
    this.snapped = true;
    const e = edges[mod(closest + this.socketIndex, edges.length)];
    const n = rotXZ(e.nx, e.nz, f.yaw);
    this.gYaw = angle(n.x, n.z);
    this.gPos.set(f.position.x, f.top + 0.02, f.position.z); // a hair proud of the floor so it does not z-fight it
    this.pendingParents = [f.id];
    // One staircase per climb cell: the slot key is the cell centre + the floor's height band
    // (keyOf + the wallKey band convention), exclusive against itself only.
    this.pendingSlots = [{ kind: 'stair_volume', worldKey: `${keyOf(f.position.x, f.position.z)}@${Math.round(f.top * 2)}#stair`, exclusiveWith: ['stair_volume'] }];
    if (this.ledger.conflicts(this.pendingSlots)) { this.ghostValid = false; this.lastReason = 'SLOT_OCCUPIED'; }
    else { this.ghostValid = true; this.lastReason = 'OK'; }
  }

  // --- pillar: a slim column standing on a footprint CORNER, carrying a ceiling on its cap ---
  private updatePillar(cursor: THREE.Vector3): void {
    const reach = S * BUILD.socketReach;
    const cands: Array<{ x: number; z: number; top: number; dist: number; hostId: string }> = [];
    // Corner sockets of every footprint near the cursor. Adjacent footprints SHARE corners; the
    // quantized slot key below collapses them to one socket, so one pillar serves both tiles.
    for (const f of this.index.queryCircle(cursor.x, cursor.z, reach + S)) {
      if (f.family === 'none' || f.top === undefined) continue;
      for (const c of (f.family === 'square' ? SQUARE_CORNERS : TRI_CORNERS)) {
        const r = rotXZ(c.x, c.z, f.yaw);
        const x = f.position.x + r.x, z = f.position.z + r.z;
        const dist = Math.hypot(x - cursor.x, z - cursor.z);
        if (dist > reach) continue;
        cands.push({ x, z, top: f.top, dist, hostId: f.id });
      }
    }
    if (cands.length === 0) {
      this.snapped = false;
      this.ghostValid = false;
      this.lastReason = 'NO_COMPATIBLE_ANCHOR';
      this.gPos.set(cursor.x, cursor.y + BUILD.wallHeight / 2, cursor.z);
      this.gYaw = 0;
      this.pendingParents = [];
      this.pendingSlots = [];
      return;
    }
    // Stable order (distance, then position) so equidistant corners never swap frame-to-frame.
    cands.sort((a, b) => (a.dist - b.dist) || (a.x - b.x) || (a.z - b.z));
    this.snapped = true; // R cycles the nearby corners
    const c = cands[mod(this.socketIndex, cands.length)];
    for (const k of cands) { if (this.lastSockets.length >= 48) break; this.lastSockets.push({ x: k.x, y: k.top + 0.1, z: k.z, active: k === c }); }
    this.gPos.set(c.x, c.top + BUILD.wallHeight / 2, c.z);
    this.gYaw = 0; // a square column: rotation is fixed
    this.pendingParents = [c.hostId];
    // One pillar per corner + storey band (walls' banding convention). The key quantizes through
    // keyOf, so the shared corner of adjacent tiles resolves to the same slot.
    this.pendingSlots = [{ kind: 'pillar_corner', worldKey: `${keyOf(c.x, c.z)}@${Math.round(c.top * 2)}#pillar`, exclusiveWith: ['pillar_corner'] }];
    if (this.ledger.conflicts(this.pendingSlots)) { this.ghostValid = false; this.lastReason = 'SLOT_OCCUPIED'; }
    else if (this.supportOk()) { this.ghostValid = true; this.lastReason = 'OK'; }
    else { this.ghostValid = false; this.lastReason = 'NO_SUPPORT'; }
  }

  // --- door: hinge a leaf into the nearest empty doorway (single OR double -- the double's leaf
  // hinges at ITS left edge, one doorwayWidth out, so the ghost sits at the wider opening's side) ---
  private updateDoor(cursor: THREE.Vector3): void {
    const reach = S * BUILD.socketReach;
    let best: PlacedWall | null = null;
    let bestD = reach * reach;
    for (const w of this.walls) {
      if (w.id !== 'doorway' && w.id !== 'double_doorway') continue;
      // #3: include HEIGHT in the pick so STACKED (multi-storey) doorways choose the one you're aiming AT, not
      // whichever is nearest in 2D -- that 2D-only match was the "door sticks to the wrong one" glitch.
      const dx = w.cx - cursor.x, dy = (w.base + BUILD.doorwayHeight / 2) - cursor.y, dz = w.cz - cursor.z;
      const dd = dx * dx + dy * dy + dz * dz;
      if (dd < bestD) { bestD = dd; best = w; }
    }
    if (!best) {
      this.snapped = false;
      this.ghostValid = false;
      this.lastReason = 'NO_COMPATIBLE_ANCHOR';
      this.gPos.set(cursor.x, cursor.y, cursor.z);
      this.gYaw = 0;
      this.pendingParents = [];
      this.pendingSlots = [];
      return;
    }
    this.snapped = true;
    const openW = BUILD.doorwayWidth * (best.id === 'double_doorway' ? 2 : 1);
    const t = rotXZ(1, 0, best.yaw);
    const hx = best.cx + t.x * (-openW / 2);
    const hz = best.cz + t.z * (-openW / 2);
    // doorSign (which way the door swings, in vs out) persists so R can flip it before placing.
    this.gPos.set(hx, best.base, hz);
    this.gYaw = best.yaw;
    // Empty-doorway test via the occupancy ledger. The slot is keyed by the WALL CENTRE (shared with
    // placeCraftedDoor / nearestDoorwaySocket), so a crafted leaf blocks the build-menu ghost too.
    // Then the swing-arc gate: furniture parked in the leaf's sweep rejects the door.
    const slot = this.doorSlot(best.cx, best.cz, best.base);
    if (this.ledger.conflicts([slot])) { this.ghostValid = false; this.lastReason = 'SLOT_OCCUPIED'; }
    else if (this.doorSwingBlocked(best.cx, best.base, best.cz, best.yaw, best.id === 'double_doorway', this.doorSign)) { this.ghostValid = false; this.lastReason = 'BOX_OVERLAP'; }
    else { this.ghostValid = true; this.lastReason = 'OK'; }
    this.pendingParents = this.pieceAtDoorway(best);
    this.pendingSlots = [slot];
  }

  private foundationSockets(cursor: THREE.Vector3): Array<{ x: number; z: number; yaw: number; top: number; dist: number; hostId?: string }> {
    const reach = S * BUILD.socketReach;
    const type: FootprintShape = this.piece === 'foundation_square' ? 'square' : 'triangle';
    const out: Array<{ x: number; z: number; yaw: number; top: number; dist: number; hostId?: string }> = [];
    // Candidate hosts from the spatial index; the piece itself is the support parent.
    for (const f of this.index.queryCircle(cursor.x, cursor.z, reach + S)) {
      if (f.family === 'none' || f.top === undefined) continue;
      if (isCeiling(f.defId)) continue; // foundations only snap to other GROUND foundations, never stack onto ceilings
      for (const e of edgesFor(f.family as FootprintShape)) {
        const m = rotXZ(e.mx, e.mz, f.yaw);
        const n = rotXZ(e.nx, e.nz, f.yaw);
        const sx = f.position.x + m.x;
        const sz = f.position.z + m.z;
        const dist = Math.hypot(sx - cursor.x, sz - cursor.z);
        if (dist > reach) continue;
        const mt = mate(sx, sz, n.x, n.z, type);
        if (!footprintNoOverlap(type, mt.x, mt.z, mt.yaw, this.footprintPiecesNear(mt.x, mt.z))) continue;
        // Score by the RESULTING tile centre, not the shared edge: candidates then rank by where the
        // piece would actually LAND relative to the aim, which is what the player is pointing at.
        const tileDist = Math.hypot(mt.x - cursor.x, mt.z - cursor.z);
        out.push({ x: mt.x, z: mt.z, yaw: mt.yaw, top: f.top, dist: tileDist, hostId: f.id });
      }
    }
    // Stable order (distance, then position) so equidistant edge candidates never swap frame-to-frame.
    out.sort((a, b) => (a.dist - b.dist) || (a.x - b.x) || (a.z - b.z));
    return out;
  }

  // --- helpers bridging the mirror arrays to the placed-piece graph ---

  // The NO_SUPPORT gate: project the support the pending ghost would receive from its resolved
  // parents (grounded foundations always pass; an edge-extended foundation / wall / ceiling loses a
  // little per hop). A value at or below zero means the run is too long to hold and the spot is
  // rejected, ghost red. Parasites (door/stairs/cupboard) always pass — they ride their host.
  private supportOk(): boolean {
    // Store the projection: the ghost tint blends toward amber as it approaches 0 (visible sag).
    this.lastProjected = this.stability.project(this.piece, this.pendingParents);
    return this.lastProjected > 0;
  }

  // The pending ghost's world box vs the placed deployable colliders: a wall may not be driven
  // through a furnace, a foundation not planted through a chest. Doorway/window/door pieces are
  // exempt -- mounted leaves, locks and window frames legitimately share their opening's volume.
  // The box is approximate per family (footprint extent x height band, mirroring collectColliders).
  private deployBlocked(): boolean {
    if (this.piece === 'doorway' || this.piece === 'double_doorway' || this.piece === 'window' || this.piece === 'door') return false;
    const boxes = this.collectDeployBoxes?.();
    if (!boxes || boxes.length === 0) return false;
    const cx = this.gPos.x, cz = this.gPos.z;
    let cy = this.gPos.y, hx: number, hy: number, hz: number;
    if (isFootprint(this.piece)) {
      hx = hz = footprintType(this.piece) === 'square' ? S / 2 : TRI_R * 0.55;
      hy = (this.ghostTop - this.ghostBase) / 2;
      cy = (this.ghostTop + this.ghostBase) / 2;
    } else if (isWallSlot(this.piece)) { // wall / half_wall (openings exempted above)
      hx = S / 2; hy = wallSlotHeight(this.piece) / 2; hz = BUILD.wallThick / 2;
    } else if (this.piece === 'stairs') {
      // The whole climb volume: treads sweep the (inset) cell up one storey.
      hx = hz = S / 2 - BUILD.wallThick; hy = BUILD.wallHeight / 2; cy = this.gPos.y + BUILD.wallHeight / 2;
    } else if (this.piece === 'pillar') {
      hx = hz = PILLAR_FLARE; hy = BUILD.wallHeight / 2;
    } else {
      return false;
    }
    // Deflate a hair so furniture parked flush against a demolished wall's line does not block the
    // rebuild (the repair loop must survive a packed base).
    hx = Math.max(0.02, hx - 0.02); hy = Math.max(0.02, hy - 0.02); hz = Math.max(0.02, hz - 0.02);
    const pr = hx + hz;
    for (const b of boxes) {
      const dx = b.cx - cx, dz = b.cz - cz;
      const rr = pr + b.hx + b.hz;
      if (dx * dx + dz * dz > rr * rr) continue; // circumradius prune before the SAT
      if (obbOverlap(cx, cy, cz, hx, hy, hz, this.gYaw, b.cx, b.cy, b.cz, b.hx, b.hy, b.hz, b.yaw)) return true;
    }
    return false;
  }

  // One door leaf's swept arc vs one oriented box: the leaf collider (the doorBoxes construction in
  // collectColliders) sampled at DOOR_ARC_STEPS+1 yaws from closed to fully open. Shared by both
  // swing gates below. `ext` is the leaf-extend sign from its hinge (-1 for a mirrored right leaf).
  private leafSweepHits(hingeX: number, hingeY: number, hingeZ: number, closedYaw: number, sign: number, ext: number,
    bcx: number, bcy: number, bcz: number, bhx: number, bhy: number, bhz: number, byaw: number): boolean {
    // Prune: the leaf never reaches past one doorway width from its hinge.
    const dx = bcx - hingeX, dz = bcz - hingeZ;
    const reach = BUILD.doorwayWidth + bhx + bhz;
    if (dx * dx + dz * dz > reach * reach) return false;
    const W = BUILD.doorwayWidth / 2, H = BUILD.doorwayHeight / 2, T = BUILD.wallThick / 2;
    const cy = hingeY + H;
    const openRad = THREE.MathUtils.degToRad(BUILD.doorOpenDeg);
    for (let i = 0; i <= DOOR_ARC_STEPS; i++) {
      const ly = closedYaw + sign * openRad * (i / DOOR_ARC_STEPS);
      const c = Math.cos(ly), s = Math.sin(ly); // leaf dir = rotXZ(1,0,ly) = (c, -s)
      if (obbOverlap(hingeX + c * ext * W, cy, hingeZ - s * ext * W, W, H, T, ly, bcx, bcy, bcz, bhx, bhy, bhz, byaw)) return true;
    }
    return false;
  }

  // Would a deployable OBB (base y, the deploy convention) sit inside any placed door's swing arc?
  // The deploy validity chain rejects such spots ('Blocks the door') so an opened door never grinds
  // into furniture parked in its sweep.
  doorArcBlockedAt(x: number, y: number, z: number, hx: number, hy: number, hz: number, yaw: number): boolean {
    const cy = y + hy;
    for (const d of this.doors) {
      if (this.leafSweepHits(d.hx, d.hy, d.hz, d.closedYaw, d.sign, d.mirror ? -1 : 1, x, cy, z, hx, hy, hz, yaw)) return true;
    }
    return false;
  }

  // Would a door snapped into the socket at (x,y,z,yaw) sweep through placed furniture? Both leaf
  // hinges of a double door are tested (they swing apart with opposite signs). Consulted by the
  // crafted-door ghost/placement and the build-menu door piece -- the reverse of doorArcBlockedAt.
  doorSwingBlocked(x: number, y: number, z: number, yaw: number, double: boolean, sign: number): boolean {
    const boxes = this.collectDeployBoxes?.();
    if (!boxes || boxes.length === 0) return false;
    const dir = rotXZ(1, 0, yaw);
    const off = double ? BUILD.doorwayWidth : BUILD.doorwayWidth / 2; // hinge offset from the socket centre
    for (const b of boxes) {
      if (this.leafSweepHits(x - dir.x * off, y, z - dir.z * off, yaw, sign, 1, b.cx, b.cy, b.cz, b.hx, b.hy, b.hz, b.yaw)) return true;
      if (double && this.leafSweepHits(x + dir.x * off, y, z + dir.z * off, yaw, -sign, -1, b.cx, b.cy, b.cz, b.hx, b.hy, b.hz, b.yaw)) return true;
    }
    return false;
  }

  // The placed FOOTPRINT pieces near (x,z), as the pre-pruned candidate list for the overlap rules.
  // Two footprints only overlap when their centres are within the sum of their circumradii (at most
  // S/sqrt(2) each), so S * 1.5 is a safe query radius; filtered in place to skip a second array.
  private footprintPiecesNear(x: number, z: number): PlacedPiece[] {
    const out = this.index.queryCircle(x, z, S * 1.5);
    let w = 0;
    for (let i = 0; i < out.length; i++) if (out[i].family !== 'none') out[w++] = out[i];
    out.length = w;
    return out;
  }

  // (The old pieceAt(x,z,top) bridge is gone: the candidate loops read placed pieces straight from
  // the spatial index now, so the host IS the candidate and no reverse lookup is needed.)

  // The doorway wall instance whose centre sits at (cx,cz) -- the host a door leaf hinges into.
  // Doorways are 'none' family walls; single AND double both host doors now.
  private doorHostAt(cx: number, cz: number): PlacedPiece | undefined {
    let best: PlacedPiece | undefined;
    let bestD = 0.01;
    for (const p of this.index.queryCircle(cx, cz, 0.6)) {
      if (p.defId !== 'doorway' && p.defId !== 'double_doorway') continue;
      const dd = (p.position.x - cx) ** 2 + (p.position.z - cz) ** 2;
      if (dd < bestD) { bestD = dd; best = p; }
    }
    return best;
  }

  // The doorway wall instance a door leaf hinges into, as a parent set.
  private pieceAtDoorway(w: PlacedWall): string[] {
    const p = this.doorHostAt(w.cx, w.cz);
    return p ? [p.id] : [];
  }

  // The footprint cell slot a foundation reserves at its height band (walls reserve their own edge
  // slots separately, so only the cell is claimed here).
  private cellSlots(type: FootprintShape, x: number, z: number, top: number): ResolvedSlot[] {
    const cellKind = type === 'square' ? 'cell_square' : 'cell_triangle';
    return [{ kind: cellKind, worldKey: `${keyOf(x, z)}@${Math.round(top * 2)}#cell`, exclusiveWith: ['cell_square', 'cell_triangle'] }];
  }
}

// --- geometry / mesh factories (REUSED unchanged) ---

// World-uniform texture density. The tier materials share ONE texture (repeat 1,1) across every
// placed piece, so density has to live in the geometry's UVs: scale them by world size / WORLD_TILE
// and the 512px tile reads at the same texels-per-metre on every face. Without this the whole tile
// was stretched 0..1 across each face (a 3m wall, a 0.2m edge), which is what smeared the planks.
const TILE = WORLD_TILE;

// STITCHED HAND-CUT FINISH, applied ONCE when a solid lands at its final transform (commit):
//  (1) corner displacement hashed from quantized WORLD position -- two pieces that meet at a corner
//      hash the SAME offset and move together, so joints stay watertight while edges wander like
//      hand-worked material. (The first cut jittered per-piece in LOCAL space, so every shared seam
//      tore open -- the "texture bleed" the owner screenshotted was geometry gaps.)
//  (2) WORLD-ANCHORED UVs -- every face re-projects its UVs from the displaced world position along
//      the face's dominant axes, so plank rows FLOW across adjacent ceiling tiles, block courses
//      continue around corners, and each piece samples a different patch of the shared texture:
//      stitched continuity AND per-piece variation from one projection.
const _sv = new THREE.Vector3();
const _sn = new THREE.Vector3();
const _snm = new THREE.Matrix3();
function stitchSolid(root: THREE.Object3D): void {
  root.updateMatrixWorld(true);
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    const geo = mesh.geometry as THREE.BufferGeometry;
    const pos = geo.attributes.position, nor = geo.attributes.normal, uv = geo.attributes.uv;
    if (!pos || !nor || !uv || geo.userData.stitched) return;
    geo.userData.stitched = true;
    _snm.getNormalMatrix(mesh.matrixWorld);
    // GEOMETRY IS EXACT (owner order 2026-07-12: "rectangles, no variation" -- the corner jitter measured
    // 6-9cm on piece tops and read as warped/damaged builds, worst on snow + grazing light). stitchSolid
    // now ONLY does the world-anchored UV re-projection, so plank rows/block courses still flow seamlessly
    // across adjacent pieces; vertices are never displaced. Twig GLBs always skipped this and were the
    // reference look ("see the twig? that's good").
    for (let i = 0; i < pos.count; i++) {
      _sv.fromBufferAttribute(pos, i).applyMatrix4(mesh.matrixWorld);
      _sn.fromBufferAttribute(nor, i).applyMatrix3(_snm).normalize();
      const ax = Math.abs(_sn.x), ay = Math.abs(_sn.y), az = Math.abs(_sn.z);
      if (ay >= ax && ay >= az) uv.setXY(i, _sv.x / TILE, _sv.z / TILE);      // tops/bottoms: XZ plane
      else if (ax >= az) uv.setXY(i, _sv.z / TILE, _sv.y / TILE);             // +-x faces: ZY plane
      else uv.setXY(i, _sv.x / TILE, _sv.y / TILE);                            // +-z faces: XY plane
    }
    uv.needsUpdate = true;
  });
}

// A box whose UVs are scaled per-face to world size, so each face shows the texture at the shared
// density instead of one stretched 0..1 tile. BoxGeometry lays out faces in groups of 4 verts in the
// order +x,-x,+y,-y,+z,-z; each face's two in-plane extents drive its U,V scale.
function texturedBox(w: number, h: number, d: number, mat: THREE.Material): THREE.Mesh {
  const geo = new THREE.BoxGeometry(w, h, d);
  const uv = geo.attributes.uv;
  // (uScale,vScale) per face, in the BoxGeometry face order. U/V map to the face's two world extents.
  const faces: Array<[number, number]> = [
    [d, h], [d, h], // +x, -x  (depth x height)
    [w, d], [w, d], // +y, -y  (width x depth)
    [w, h], [w, h], // +z, -z  (width x height)
  ];
  for (let f = 0; f < 6; f++) {
    const [su, sv] = faces[f];
    // WHOLE tiles per face (round, min 1): a fractional tile count (e.g. a 2m face at 1.33 tiles) made the
    // plank pattern end mid-tile, so an abutting piece started out of phase -> the visible seam/stitch. Snapping
    // to an integer tile count makes the pattern end cleanly at every piece edge, so grid-aligned pieces line up.
    const nu = Math.max(1, Math.round(su / TILE)), nv = Math.max(1, Math.round(sv / TILE));
    for (let i = f * 4; i < f * 4 + 4; i++) uv.setXY(i, uv.getX(i) * nu, uv.getY(i) * nv);
  }
  uv.needsUpdate = true;
  return new THREE.Mesh(geo, mat);
}

// A 3-sided prism (triangle foundation/ceiling) with its top/bottom-cap UVs scaled to world size.
// CylinderGeometry caps already carry radial UVs in [0,1]; scaling by diameter/TILE keeps the top
// face (the one you see most) at the shared density rather than a single stretched tile.
function texturedPrism(r: number, hgt: number, mat: THREE.Material): THREE.Mesh {
  const geo = new THREE.CylinderGeometry(r, r, hgt, 3);
  const uv = geo.attributes.uv;
  const s = (2 * r) / TILE;
  for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) * s, uv.getY(i) * s);
  uv.needsUpdate = true;
  return new THREE.Mesh(geo, mat);
}

// Per-tier buildable GLBs (owner test 2026-07-09, wood added 2026-07-12): when a placed (non-ghost) piece's
// tier has a GLB for this piece id, swap the procedural mesh for the owner's model, NON-uniformly stretched
// to the exact box the procedural piece occupied -- so it drops into the anchor/collision/stability system
// unchanged (the CATALOG, not the mesh, drives all gameplay; the mesh is purely visual). The ghost stays
// procedural: its translucent green/red validity tint would be lost on a GLB carrying its own materials. Any
// piece whose file is absent or a broken export simply keeps the procedural fallback (a failed load never
// calls the swap). New tiers just add another entry -- stone/metal/reinforced are empty until modelled.
const TIER_GLB: Partial<Record<BuildTier, Partial<Record<PieceId, string>>>> = {
  // TWIG re-export wave 2026-07-12 (uniform `<piece>_<tier>.glb` naming; the old twig_*.glb set is gone).
  twig: {
    foundation_square: 'foundation_square_twig.glb',
    foundation_triangle: 'foundation_triangle_twig.glb',
    wall: 'wall_twig.glb',
    half_wall: 'half_wall_twig.glb',
    ceiling_square: 'ceiling_square_twig.glb',
    ceiling_triangle: 'ceiling_triangle_twig.glb',
    doorway: 'doorway_twig.glb',
    double_doorway: 'double_doorway_twig.glb',
    window: 'window_twig.glb',
  },
  wood: {
    foundation_square: 'foundation_square_wood.glb',
    foundation_triangle: 'foundation_triangle_wood.glb',
    wall: 'wall_wood.glb',
    half_wall: 'half_wall_wood.glb',
    ceiling_square: 'ceiling_square_wood.glb',
    ceiling_triangle: 'ceiling_triangle_wood.glb',
    doorway: 'doorway_wood.glb',
    double_doorway: 'double_doorway_wood.glb',
    window: 'window_wood.glb',
  },
  // STONE wave 2026-07-12. No ceiling_square export yet -- that one stays procedural until it lands.
  stone: {
    foundation_square: 'foundation_square_stone.glb',
    foundation_triangle: 'foundation_triangle_stone.glb',
    wall: 'wall_stone.glb',
    half_wall: 'half_wall_stone.glb',
    ceiling_triangle: 'ceiling_triangle_stone.glb',
    doorway: 'doorway_stone.glb',
    double_doorway: 'double_doorway_stone.glb',
    window: 'window_stone.glb',
  },
};
for (const map of Object.values(TIER_GLB)) for (const f of Object.values(map)) preloadGLB('/buildables/' + f);

// Non-uniformly scale + centre a loaded GLB so its bounds exactly fill `target` (a local Box3 captured from
// the procedural piece it replaces -> same size, matched centres). ceiling_triangle is modelled standing
// VERTICAL (owner note), so it is laid flat first; every other piece is already Y-up.
function fitGLBToBox(scene: THREE.Object3D, target: THREE.Box3, id: PieceId): THREE.Object3D {
  if (id === 'ceiling_triangle') scene.rotation.x = -Math.PI / 2; // vertical model -> horizontal ceiling slab
  const wrap = new THREE.Group();
  wrap.add(scene);
  wrap.updateMatrixWorld(true);
  const src = new THREE.Box3().setFromObject(wrap), ss = src.getSize(new THREE.Vector3()), ts = target.getSize(new THREE.Vector3());
  wrap.scale.set(ts.x / (ss.x || 1), ts.y / (ss.y || 1), ts.z / (ss.z || 1)); // stretch to fit
  wrap.updateMatrixWorld(true);
  const scaled = new THREE.Box3().setFromObject(wrap);
  wrap.position.add(target.getCenter(new THREE.Vector3()).sub(scaled.getCenter(new THREE.Vector3()))); // align centres
  return wrap;
}

// Swap the procedural piece already built into `g` for its tier's GLB (if one exists for this piece id).
// The procedural piece stays as the instant placeholder while the model loads; a warm cache swaps
// synchronously. GLB meshes are marked `stitched` so stitchSolid skips them -- it jitters verts +
// reprojects UVs to the shared build texture, which would wreck the model's own baked texture/UVs.
// Invisible (opacity 0, no depth write -- draws nothing, costs nothing) but still `visible === true`, so
// THREE.Raycaster keeps testing it. Shared: it carries no per-instance state.
const PICK_PROXY_MAT = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false });

function maybeSwapTierGLB(g: THREE.Group, id: PieceId, tier: BuildTier): void {
  const file = TIER_GLB[tier]?.[id];
  if (!file) return;
  const url = '/buildables/' + file;
  const target = new THREE.Box3().setFromObject(g); // the procedural bounds = the fit target
  if (target.isEmpty()) return;
  const apply = (clone: THREE.Object3D): void => {
    g.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) m.geometry.dispose(); }); // one-off placeholder geo
    g.clear();
    const fitted = fitGLBToBox(clone, target, id);
    fitted.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh) {
        m.castShadow = true; m.receiveShadow = true;
        if (m.geometry) m.geometry.userData.stitched = true;
        // Owner GLBs run 5-6 figures of vertices -- fine for the GPU, ruinous for the CPU raycasts that
        // pick placed pieces every frame a build/placement tool is held (Builder.pickPlaced, the
        // builder.group floor/surface hit-tests in main.ts). Opt the heavy visual mesh out of ray
        // testing; the cheap box below (the SAME bounds the procedural placeholder occupied, so every
        // existing hit-test keeps its prior shape/behavior) stands in as the pick target.
        m.raycast = () => {};
      }
    });
    g.add(fitted);
    const size = target.getSize(new THREE.Vector3()), center = target.getCenter(new THREE.Vector3());
    const proxy = new THREE.Mesh(new THREE.BoxGeometry(Math.max(size.x, 0.01), Math.max(size.y, 0.01), Math.max(size.z, 0.01)), PICK_PROXY_MAT);
    proxy.position.copy(center);
    g.add(proxy);
  };
  const ready = cloneGLBIfReady(url);
  if (ready) apply(ready); else onGLBReady(url, apply);
}

// The procedural-only shape for a piece -- literally a fitted square/triangle/box, regardless of tier or
// GLB availability. This is the ONE place that switches on piece id to build geometry; makePiece calls it
// for the placeholder (then maybe swaps in the GLB) and the ghost (which never swaps), and
// makeProceduralSolid below calls it directly for transient visual states that must stay cheap no matter
// how heavy the tier's GLB is (aim-demolish highlight, the crumble/fall-out animation).
function buildProceduralSolid(id: PieceId, tier: BuildTier, mat: THREE.Material, foundationMat: THREE.Material, beamMat: THREE.Material): THREE.Group {
  const g = new THREE.Group();
  switch (id) {
    case 'foundation_square':
      buildFoundation(g, 'square', tier, foundationMat);
      break;
    case 'foundation_triangle':
      buildFoundation(g, 'triangle', tier, foundationMat);
      break;
    case 'ceiling_square':
      g.add(texturedBox(S, BUILD.ceilingThick, S, mat));
      break;
    case 'ceiling_triangle':
      g.add(texturedPrism(TRI_R, BUILD.ceilingThick, mat));
      break;
    case 'wall':
    case 'half_wall': {
      const h = wallSlotHeight(id);
      g.add(texturedBox(S, h, BUILD.wallThick, mat));
      const beam = new THREE.Mesh(new THREE.BoxGeometry(S * 0.92, 0.16, 0.1), beamMat);
      beam.position.set(0, -h * 0.18, -(BUILD.wallThick / 2) - 0.05);
      g.add(beam);
      break;
    }
    case 'doorway':
      buildDoorway(g, mat);
      break;
    case 'double_doorway':
      buildDoubleDoorway(g, mat);
      break;
    case 'window':
      buildWindow(g, mat);
      break;
    case 'door':
      buildDoorLeaf(g, mat);
      break;
    case 'stairs':
      buildStairs(g, mat);
      break;
    case 'pillar':
      buildPillar(g, mat);
      break;
  }
  return g;
}

// Exported so the F4 -> OBJECTS calibration tool (ui/objectLab.ts) can build a real foundation/wall/
// half-wall/ceiling test rig from the SAME geometry gameplay places, instead of a hand-approximated copy.
// `instanced` (the Builder's placed-piece path): DON'T clone a per-piece GLB -- mark the group with the
// file + fit target so the Builder adopts it into BuildGLBInstances after commit (one InstancedMesh per
// GLB submesh for ALL pieces of a kind). Non-builder callers (objectLab rig, default) keep the solo swap.
export function makePiece(id: PieceId, ghost: boolean, tier: BuildTier, skinId?: SkinId | null, instanced = false): THREE.Group {
  const mat = ghost ? GHOST_MAT : getTierMaterial(tier, skinId);
  const foundationMat = ghost ? GHOST_MAT : getFoundationMaterial(tier, skinId); // minutely darker/rougher than walls+ceilings
  const beamMat = ghost ? GHOST_MAT : WEAK_MAT;
  const g = buildProceduralSolid(id, tier, mat, foundationMat, beamMat);
  if (!ghost) {
    const file = TIER_GLB[tier]?.[id];
    if (file && instanced) {
      // Measure the fit target NOW (local, untransformed procedural bounds -- same contract as the
      // solo swap); the Builder passes it to the instancer once the piece has its placedId.
      const target = new THREE.Box3().setFromObject(g);
      if (!target.isEmpty()) g.userData.glbWant = { file, target, verticalFix: id === 'ceiling_triangle' };
    } else if (file) maybeSwapTierGLB(g, id, tier); // solo clone path (F4 rig etc.)
  }
  // Placed pieces cast and receive shadows; the translucent ghost does neither.
  if (!ghost) g.traverse((o) => { o.castShadow = true; o.receiveShadow = true; });
  return g;
}

// A cheap, GLB-free stand-in at the piece's real tier material (not the ghost tint) -- for transient
// visual states where the true model would be wasted or actively harmful to raycast/animate: the hammer's
// aim-demolish highlight and the crumble fall-out both use this instead of the real placed mesh. Never
// triggers a GLB load, so it stays "literally a fitted square/triangle/box" no matter how heavy the wired
// tier GLB is.
export function makeProceduralSolid(id: PieceId, tier: BuildTier, skinId?: SkinId | null): THREE.Group {
  return buildProceduralSolid(id, tier, getTierMaterial(tier, skinId), getFoundationMaterial(tier, skinId), WEAK_MAT);
}

// A foundation block, centred so its top face is at +foundDepth/2 (placed so that lands on the
// floor). Twig is a thin top slab on corner legs (Rust's 4-pillar twig foundation); wood/stone are
// A low raised lip hugging the foundation's OUTER top edge -- a subtle "footing" so a foundation reads apart from a
// flat wall/ceiling from above. It sits at the very perimeter (hidden under any wall placed on the edge; visible on
// open edges + as a panel seam where foundations tile), so it never ridges across the middle of a floor.
function addFoundationEdge(g: THREE.Group, mat: THREE.Material, topY: number): void {
  const rimH = 0.05, rimW = 0.1, e = S / 2 - rimW / 2, cy = topY + rimH / 2;
  for (const s of [-1, 1]) {
    const bz = texturedBox(rimW, rimH, S, mat); bz.position.set(s * e, cy, 0); g.add(bz);
    const bx = texturedBox(S, rimH, rimW, mat); bx.position.set(0, cy, s * e); g.add(bx);
  }
}

// a solid block whose buried depth doubles as the legs/skirt that plant into the slope.
function buildFoundation(g: THREE.Group, type: 'square' | 'triangle', tier: BuildTier, mat: THREE.Material): void {
  const D = BUILD.foundDepth;
  const topY = D / 2; // local Y of the walkable top face
  if (tier === 'twig') {
    const slabT = 0.18, legW = 0.24, legH = D - slabT;
    const legCY = topY - slabT - legH / 2;
    if (type === 'square') {
      const slab = texturedBox(S, slabT, S, mat);
      slab.position.y = topY - slabT / 2;
      g.add(slab);
      const inset = S / 2 - legW / 2 - 0.04;
      for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
        const leg = texturedBox(legW, legH, legW, mat);
        leg.position.set(sx * inset, legCY, sz * inset);
        g.add(leg);
      }
      addFoundationEdge(g, mat, topY);
    } else {
      const slab = texturedPrism(TRI_R, slabT, mat);
      slab.position.y = topY - slabT / 2;
      g.add(slab);
      for (const corner of TRI_CORNERS) {
        const leg = texturedBox(legW, legH, legW, mat);
        leg.position.set(corner.x * 0.82, legCY, corner.z * 0.82);
        g.add(leg);
      }
    }
  } else if (type === 'square') {
    g.add(texturedBox(S, D, S, mat));
    addFoundationEdge(g, mat, topY);
  } else {
    g.add(texturedPrism(TRI_R, D, mat));
  }
}

// A full wall as ONE extruded piece with a rectangular cutout, so the texture flows across it
// continuously instead of restarting on separate posts/panels. hole bounds are measured up from the
// wall base. ExtrudeGeometry's front/back UVs are the raw shape coords (world units centred on the
// wall); we rebase them to the wall corner and scale by WORLD_TILE so the planks read at the SAME
// density as a solid wall — the old code remapped to 0..1, stretching one tile across the whole 3m
// face (the smear). The thin extruded sides get UVs from ExtrudeGeometry too; rescaling all of them
// by the same factor keeps them consistent.
function holeWallMesh(holeBottom: number, holeTop: number, holeW: number, mat: THREE.Material): THREE.Mesh {
  const h = BUILD.wallHeight, th = BUILD.wallThick;
  const shape = new THREE.Shape();
  shape.moveTo(-S / 2, -h / 2);
  shape.lineTo(S / 2, -h / 2);
  shape.lineTo(S / 2, h / 2);
  shape.lineTo(-S / 2, h / 2);
  shape.lineTo(-S / 2, -h / 2);
  const hole = new THREE.Path();
  const y0 = -h / 2 + holeBottom, y1 = -h / 2 + holeTop;
  hole.moveTo(-holeW / 2, y0);
  hole.lineTo(holeW / 2, y0);
  hole.lineTo(holeW / 2, y1);
  hole.lineTo(-holeW / 2, y1);
  hole.lineTo(-holeW / 2, y0);
  shape.holes.push(hole);
  const geo = new THREE.ExtrudeGeometry(shape, { depth: th, bevelEnabled: false, steps: 1 });
  geo.translate(0, 0, -th / 2);
  const uv = geo.attributes.uv;
  // Shape-space UVs are world-unit coords centred at the wall middle; rebase to a corner and divide
  // by WORLD_TILE for world-uniform density (one tile per TILE metres) instead of one tile per face.
  for (let i = 0; i < uv.count; i++) uv.setXY(i, (uv.getX(i) + S / 2) / TILE, (uv.getY(i) + h / 2) / TILE);
  uv.needsUpdate = true;
  geo.computeVertexNormals();
  return new THREE.Mesh(geo, mat);
}

function buildDoorway(g: THREE.Group, mat: THREE.Material): void {
  g.add(holeWallMesh(0, BUILD.doorwayHeight, BUILD.doorwayWidth, mat));
}

// A wider doorway sized for a double door (two leaves). Hole = 2x the single doorway width.
function buildDoubleDoorway(g: THREE.Group, mat: THREE.Material): void {
  g.add(holeWallMesh(0, BUILD.doorwayHeight, BUILD.doorwayWidth * 2, mat));
}

function buildWindow(g: THREE.Group, mat: THREE.Material): void {
  g.add(holeWallMesh(BUILD.windowSill, BUILD.windowSill + BUILD.windowHeight, BUILD.windowWidth, mat));
}

// A door leaf hinged at the group origin: extends +x to its width, rises from y=0 (base).
function buildDoorLeaf(g: THREE.Group, mat: THREE.Material): void {
  // Fill the opening (only a hair of clearance so it can swing without z-fighting the frame). THIN like
  // the crafted leaves: a door should read sunken inside the doorway, not flush with the wall faces.
  const w = BUILD.doorwayWidth * 0.99, h = BUILD.doorwayHeight * 0.99, th = 0.05;
  const leaf = texturedBox(w, h, th, mat);
  leaf.position.set(w / 2, h / 2, 0);
  g.add(leaf);
  const handle = texturedBox(0.07, 0.2, 0.14, mat);
  handle.position.set(w * 0.85, h * 0.5, th);
  g.add(handle);
}

// A CRAFTED door leaf (one doorway-width wide) hinged at the group origin. `mirror` flips it to extend -x for
// the second leaf of a double door. The leaf VISUAL lives in craftables.ts (birch / patchwork / armor per
// kind) so the placed door matches the held item, and it is THIN (~0.05) on purpose: a leaf should read
// sunken inside the 0.2m doorway, not flush with the wall faces.
function buildCraftedDoorLeaf(g: THREE.Group, kind: CraftedDoorId, mirror: boolean): void {
  // Slight OVERLAP (1.02 w/h) so the leaf fully fills the opening -- NO gaps, can't see through around it.
  const w = BUILD.doorwayWidth * 1.02, h = BUILD.doorwayHeight * 1.02;
  const leaf = buildDoorLeafModel(kind, w, h);
  leaf.position.x = (mirror ? -1 : 1) * (w / 2);
  if (mirror) leaf.scale.x = -1; // negative determinant: the renderer flips frontFace, so faces stay correct
  g.add(leaf);
}

// A slim tiered-material column, centred at mid-height like a wall: the shaft spans the full wall
// height, with a small flare block at the base and cap so it reads as a load-bearing post from any
// angle (and the cap visually meets the ceiling slab it carries).
function buildPillar(g: THREE.Group, mat: THREE.Material): void {
  const h = BUILD.wallHeight;
  const w = PILLAR_HALF * 2;
  const flareW = PILLAR_FLARE * 2;
  const flareH = 0.14;
  g.add(texturedBox(w, h, w, mat));
  for (const s of [-1, 1]) {
    const flare = texturedBox(flareW, flareH, flareW, mat);
    flare.position.y = s * (h / 2 - flareH / 2);
    g.add(flare);
  }
}

// A staircase that climbs one wall-height across the footprint depth, base at y=0.
function buildStairs(g: THREE.Group, mat: THREE.Material): void {
  const steps = BUILD.stairSteps;
  // WIDTH is inset by a wall thickness on the two SIDES so the treads sit snug inside the side walls
  // without z-fighting them (logarithmicDepthBuffer can't resolve exactly-overlapping surfaces).
  // DEPTH, though, runs the FULL footprint: the bottom step must meet the near foundation edge and the
  // TOP step must reach the far edge where the upper landing / ceiling slab begins. Reusing the width
  // inset on the depth axis (the old bug) pulled the whole staircase 0.2m off BOTH ends, leaving a
  // full-height gap at the top (and bottom). Decoupling the two closes it. The collider matches below.
  const widthSpan = S - 2 * BUILD.wallThick;
  const run = S / steps, rise = BUILD.wallHeight / steps;
  for (let i = 0; i < steps; i++) {
    const topH = (i + 1) * rise;
    const step = texturedBox(widthSpan, topH, run, mat);
    step.position.set(0, topH / 2, -S / 2 + (i + 0.5) * run);
    g.add(step);
  }
}

// Remove a specific element from a list in place (identity match). Used by the mirror-undo closures.
function spliceItem<T>(list: T[], item: T): void {
  const i = list.indexOf(item);
  if (i >= 0) list.splice(i, 1);
}

// Do two footprint pieces share a FLUSH edge (an edge midpoint of one coincides with an edge
// midpoint of the other)? mate() lands adjacent pieces so their midpoints are exactly shared, so a
// 0.15m tolerance is generous. Drives the retroactive ceiling edge-extend relink.
function footprintsShareEdge(a: PlacedPiece, b: PlacedPiece): boolean {
  for (const ea of edgesFor(a.family as FootprintShape)) {
    const ma = rotXZ(ea.mx, ea.mz, a.yaw);
    const ax = a.position.x + ma.x, az = a.position.z + ma.z;
    for (const eb of edgesFor(b.family as FootprintShape)) {
      const mb = rotXZ(eb.mx, eb.mz, b.yaw);
      const dd = (b.position.x + mb.x - ax) ** 2 + (b.position.z + mb.z - az) ** 2;
      if (dd < 0.0225) return true;
    }
  }
  return false;
}

// Free a placed solid's geometry when it is removed WITHOUT a crumble (materials are shared tier
// materials and stay; only the per-instance geometry is owned here, mirroring disposeGhost).
function disposeSolid(obj: THREE.Object3D): void {
  obj.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (mesh.geometry) mesh.geometry.dispose();
  });
}
